import { RootState } from 'app/store';
import { EControlType } from 'components/ControlWrapper';
import {
    AppLinkTo,
    AppLinkToExternal,
    AppNavigateToExternal,
    buildBenefitStatusOptions,
    buildDeactivateOptions,
    buildGroupConfigurationStatusOptions,
    buildProductOptions,
    calcFieldName,
    compareUnderWritingReviewToWithoutDispatch,
    ConvertToDropdownOptions,
    createAttributeSet,
    createNonStandard,
    createNonStandardTemplate,
    createOpportunityOptionAndBenefits,
    emitCustomEvent,
    ENavigationReason,
    EViewType,
    friendlyValue,
    matchingNonStandard,
    URIVersionToBlobVersion
} from 'global/Application';
import { EmitEventProcess } from 'global/EmitEvent/EmitEventManager';
import {
    ICompareGroupConfigurationData,
    loadLeftSnap,
    loadMergeSnap,
    loadRightSnap, transformLeftSnapForCompare,
    updatePharmacyStatus
} from 'global/Reducer/compSlice';
import {
    addUpdateBenefit,
    addUpdateGroupConfiguration,
    addUpdateNonStandards,
    addUpdateNonStandardTemplates,
    IGCData,
    IGCDataLight,
    INonStandardPayload,
    INonStandardTemplatePayload,
    purgeOnData,
    replaceComments,
    setAssociationTemplateSnapshot,
    setSaveTokenOnData,
    updateFeesAndPremiums,
    updateIteration,
    updateModifiedOn,
    updateSnapshotData
} from 'global/Reducer/dataSlice';
import { AutoCalculateTransformer } from 'global/Transform/AutoCalculateTransformer';
import { CloneTransformer } from 'global/Transform/CloneTransformer';
import { EGroupCollectIntegrationStatus, GroupCollectEmitEventTransformer } from 'global/Transform/GroupCollectEmitEventTransformer';
import { IJsonTransformer } from 'global/Transform/IJsonTransformer';
import { EJsonTransformerRuntime, JsonTransformer } from 'global/Transform/JsonTransformer';
import { PharmacyResetTransformer } from 'global/Transform/PharmacyResetTransformer';
import { RemoveDeactivatedDataTransformer } from 'global/Transform/RemoveDeactivatedDataTransformer';
import { RevisionResetTransformer } from 'global/Transform/RevisionResetTransformer';
import { UpgradeReleaseTransformer } from 'global/Transform/UpgradeReleaseTransformer';
import { createEntityUpdatePayload, findEntityByCategoryId, findNonStandardInEntity, getEntityCollection, IEntityContext } from 'global/Utility/EntityHelpers';
import { IFieldVisibility } from 'global/Visibility/IFieldVisibility.type';
import { ParentVisibilityGraphProcessor } from 'global/Visibility/ParentVisibilityGraph';
import {
    bcbs_groupconfigurationsnapshot as IXrmOptions_bcbs_groupconfigurationsnapshot,
    bcbs_opportunityoption as IXrmOptions_bcbs_opportunityoption,
} from 'model/AttributeOptionSets.type';
import {
    $att_$fees_and_premiums_$bcbs_bccadminid_id,
    $att_$fees_and_premiums_$bcbs_billgroupcode_id,
    $att_$fees_and_premiums_$bcbs_fee_feetype_id,
    $att_$non_standard_$bcbs_benefitdefinition_id,
    $att_$non_standard_$bcbs_doesthisbenefitpayaccoridngtotherelatedbe_id,
    $att_$non_standard_$bcbs_effectivedate_id,
    $att_$non_standard_template_$bcbs_nst_benefitdefinition_id,
    $att_$non_standard_template_$bcbs_nst_doesthisbenefitpayaccoridngtotherelatedbe_id,
    $att_$non_standard_template_$bcbs_nst_effectivedate_id
} from "model/ConfigAttributeConstants";
import {
    $cat_$ancillary_id,
    $cat_$cobra_id,
    $cat_$dental_id,
    $cat_$florida_prime_id,
    $cat_$fsa_id,
    $cat_$hra_id,
    $cat_$hsa_id,
    $cat_$lsa_id,
    $cat_$medical_id,
    $cat_$pharmacy_id,
    $cat_$telehealth_id,
    $cat_$vision_id
} from "model/ConfigCategoryConstants";
import { $oset_$fee_fee_type_$per_employee_per_month_pepm_id } from "model/ConfigOptionSetConstants";
import {
    $scat_$ancillary_id, $scat_$ancillary_name,
    $scat_$cobra_id, $scat_$cobra_name,
    $scat_$dental_id, $scat_$dental_name,
    $scat_$fsa_id, $scat_$fsa_name,
    $scat_$hra_id, $scat_$hra_name,
    $scat_$hsa_id, $scat_$hsa_name,
    $scat_$lsa_id, $scat_$lsa_name,
    $scat_$medical_id, $scat_$medical_name,
    $scat_$pharmacy_id, $scat_$pharmacy_name,
    $scat_$telehealth_id, $scat_$telehealth_name,
    $scat_$vision_id, $scat_$vision_name
} from "model/ConfigSupercategoryConstants";
import {
    bcbs_configurationrelationship as IConfigurationRelationship,
    bcbs_config_attribute as IXrmTypes_bcbs_config_attribute,
    bcbs_config_category as IXrmTypes_bcbs_config_category,
    bcbs_config_optionsetitem as IXrmTypes_bcbs_config_optionsetitem,
    bcbs_config_release as IXrmTypes_bcbs_config_release,
    bcbs_config_row as IXrmTypes_bcbs_config_row,
    bcbs_config_section as IXrmTypes_bcbs_config_section,
    bcbs_config_tab as IXrmTypes_bcbs_config_tab,
    bcbs_config_tierlevel as IXrmTypes_bcbs_config_tierlevel,
    bcbs_emchecklist as IXrmTypes_bcbs_emchecklist,
    bcbs_enrollmentdetails as IXrmTypes_bcbs_enrollmentdetails,
    bcbs_groupconfigurationsnapshot as IXrmTypes_bcbs_groupconfigurationsnapshot,
    bcbs_opportunitybenefit as IXrmTypes_bcbs_opportunitybenefit,
    bcbs_opportunityoption as IXrmTypes_bcbs_opportunityoption,
    bcbs_quoterequest as IXrmTypes_bcbs_quoterequest,
    opportunity as IXrmTypes_opportunity,
    systemuser as IXrmTypes_systemuser
} from 'model/CRM';
import {
    EOriginTypeCode, ERelationshipTypeCode, ETargetActionCode, Ibcbs_configurationrelationship_extended,
    Ibcbs_config_attribute_extended as IXrmTypesExtended_Ibcbs_config_attribute_extended,
    Ibcbs_config_optionsetitem_extended as IXrmTypesExtended_Ibcbs_config_optionsetitem_extended,
    ICopayCoin as IXrmTypesExtended_ICopayCoin,
    IOptionSetDefinition as IXrmTypesExtended_IOptionSetDefinition,
    ISystemUserExtended as IXrmTypesExtended_ISystemUserExtended,
    IUpdatable_bcbs_groupconfigurationsnapshot as IXrmTypesExtended_IUpdatable_bcbs_groupconfigurationsnapshot,
    TCategoryName as IXrmTypesExtended_TCategoryName
} from 'model/CRM.type';
import { buildAttributeSetFormDefinitions, buildCategoryFormDefinitions, buildFormDefinitions, buildNonStandardFormDefinitions } from 'model/FlexibleFormModel';
import {
    bcbs_fundingtypes as IXrmGlobalOptions_bcbs_fundingtypes,
    bcbs_lob as IXrmGlobalOptions_bcbs_lob,
    TOptionSetNames as IXrmGlobalOptions_TOptionSetNames,
} from 'model/GlobalOptionSetStatic';
import { ICheckList } from 'model/ICheckList';
import { IGroupConfigurationUpdate } from 'model/IGroupConfigurationUpdate';
import { $matt_$fees_and_premiums_$bcbs_attributeset_fee_id } from "model/MasterAttributeConstants";
import { blankSnapshot } from 'model/snapshotTemplate';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { EUnderwritingConfirmationDialogState } from 'views/BlueRelationship/Compare/CompareContext';
import { INonstandardSearchResult } from 'views/BlueRelationship/NonStandardTemplateEditor';
import { ITierLevelChange } from 'views/TierWizard';
import {
    bcbs_statecode_byStatus,
    EGroupConfigurationStatus as FAM_EGroupConfigurationStatus,
    IAccountsReceivable as FAM_IAccountsReceivable,
    IBenefit as FAM_IBenefit,
    IBooleanValue as FAM_IBooleanValue,
    IEntityReferenceValue as FAM_IEntityReferenceValue,
    IFeesAndPremiums as FAM_IFeesAndPremiums,
    IFlexibleField as FAM_IFlexibleField,
    IFlexibleFieldValue as FAM_IFlexibleFieldValue,
    IGroupConfigurationConfig as FAM_IGroupConfigurationConfig,
    IGroupConfigurationData as FAM_IGroupConfigurationData,
    INonStandard as FAM_INonStandard,
    INonStandardTemplate as FAM_INonStandardTemplate,
    IOpportunityProduct as FAM_IOpportunityProduct,
    IOpportunityProductOptionBenefit as FAM_IOpportunityProductOptionBenefit,
    IRef as FAM_IRef,
    IStopLoss as FAM_IStopLoss,
    ITemplateRelationship as FAM_ITemplateRelationship,
    ITemplateSectionRelationship as FAM_ITemplateSectionRelationship,
    ITierLevel as FAM_ITierLevel,
    TGuid as FAM_TGuid,
    TValue as FAM_TValue,
    IComment,
    IOptionSetValue,
    IPharmacyORE
} from '../../model/FlexibleAttributeModel';
import { AppDataSource, ISaveResult, ISnapshotInfo } from '../DataSources/AppDataSource';
import { loadSnapshotsData } from "../Reducer/compSlice";
import {
    changeSnapshotStatus, defaultStatusSlice, ELoadStatus,
    endSnapshotLoad,
    ESnapshotStatus, IGroupConfigurationStatus,
    purgeOnStatus,
    setConcurrencyConfirmationModal,
    setProcessing,
    setStatusRecycle,
    setViewType,
    startSnapshotLoad,
    updateAccount, updateCreatingStatus, updateDataSavingStatus,
    updateDebug,
    updateLastSaved, updateSnapshotBlobVersion, updateSnapshots
} from '../Reducer/statusSlice';
import { ArrayToLookup, ArrayToLookupByKey, ArrayToLookupByPredicate, ArrayToLookupByPredicateWithMap, copyPropertiesOnly, Delay, formatDate, hasValue, objectFlip, parseDate, timestamp, trimGUIDLower } from '../Utility/TSFunctions';
import { appInsights } from './AppInsight';
import { Constants } from './Constants';
import { ValidationError } from './ValidationError';

type TDispatch = ReturnType<typeof useDispatch>

interface ILoadSnapshotInstructions {
    leftSnapshot?: IXrmTypes_bcbs_groupconfigurationsnapshot;
    leftSnapshotId?: FAM_TGuid;

    rightSnapshot?: IXrmTypes_bcbs_groupconfigurationsnapshot;
    rightSnapshotId?: FAM_TGuid;

    compareTransformReady?: boolean;

    primarySnapshot?: IXrmTypes_bcbs_groupconfigurationsnapshot;
    primarySnapshotId?: FAM_TGuid;
    primarySnapshotVersionId?: string;

    purgeStore?: Boolean;

    // TODO until we parallel load
    context?: "primary" | "left" | "right" | "?";

    merge?: boolean;
    liveLoad?: boolean;
}

export interface ICloneResult {
    snapshot: IXrmTypes_bcbs_groupconfigurationsnapshot;
    change: Partial<IXrmTypesExtended_IUpdatable_bcbs_groupconfigurationsnapshot>;
    release: IXrmTypes_bcbs_config_release;
}

export enum ESaveType {
    EDefault, ECreate, EManual, EAuto, ESubmit, EUpgradeRelease, EConcurrencyConflict,
}

export interface ISaveInstruction {
    saveType?: ESaveType
    ignoreJsonSave?: boolean;
    isMergeSave?: boolean;
    isUnderwritingSave?: boolean;

    onPostSaveNavigateExternalToSaveResult?: boolean;
    onPreSaveUpdateJson?: IGroupConfigurationUpdate

    onSaveClone?: boolean;
    onSaveSetStatusCode?: number;
    onSaveGenerateGoldStandardAndSQL?: boolean;
    onSaveChangeUWConfirmedParentId?: string;
    onSaveBypassUnderwritingTaskGeneration?: boolean;
    onSaveSetSnapshotName?: string;
    onSaveStatusScreen?: boolean;
    onSaveCreateEnrollmentDetail?: IXrmTypes_bcbs_enrollmentdetails
    onSaveUpdateRelease?: boolean;
    onSaveForceStatusCodeToInProgress?: boolean;

    onPostSaveIgnoreReload?: boolean;
    onPostSaveSetViewOnly?: boolean;

    onCloneSetOriginatingSnapshotId?: string;

    /** Configuration relationship records to save to bcbs_configurationrelationship after a successful save. */
    onSaveConfigurationRelationships?: IConfigurationRelationshipPayload[];

    ignoreViewType?: boolean;
}

const CloneSnapshotAsync = async (
    dataSource: AppDataSource,
    xSnapshot: IXrmTypes_bcbs_groupconfigurationsnapshot,
    state: RootState,
    instructions?: ISaveInstruction
) => {

    appInsights?.trackEvent({
        name: `dataActions.CloneSnapshotAsync`,
        properties: {
            bcbs_groupconfigurationsnapshotid: xSnapshot.bcbs_groupconfigurationsnapshotid,
            instructions: instructions
        }
    });

    let nSnapshot: Partial<IXrmTypesExtended_IUpdatable_bcbs_groupconfigurationsnapshot> = {}

    // bcbs_groupconfigurationsnapshot[required]:
    // ----------------------------------------------------------------------
    nSnapshot.bcbs_name = xSnapshot.bcbs_name;
    nSnapshot.statuscode = xSnapshot.statuscode;
    nSnapshot.statecode = xSnapshot.statecode;

    // bcbs_name
    // ----------------------------------------------------------------------
    // newSnapshot.bcbs_name = xSnapshot.bcbs_name;

    // bcbs_groupid (account)
    // ----------------------------------------------------------------------
    if (xSnapshot._bcbs_groupid_value)
        nSnapshot["bcbs_groupid@odata.bind"] = `/accounts(${xSnapshot._bcbs_groupid_value})`;

    // bcbs_Opportunityid (opportunity)
    // ----------------------------------------------------------------------
    // The Opportunity field should be NULL when a GC is created from another GC.
    // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/283003
    // if (snapshot._bcbs_opportunityid_value)
    //     newSnapshot["bcbs_Opportunityid@odata.bind"] = `/opportunities(${snapshot._bcbs_opportunityid_value})`;

    if (instructions?.saveType === ESaveType.EUpgradeRelease) {
        if (xSnapshot._bcbs_opportunityid_value)
            nSnapshot["bcbs_Opportunityid@odata.bind"] = `/opportunities(${xSnapshot._bcbs_opportunityid_value})`;
    }

    // bcbs_OriginatingSnapshotid
    // ----------------------------------------------------------------------
    if (instructions?.saveType === ESaveType.EUpgradeRelease) {
        if (xSnapshot._bcbs_originatingsnapshotid_value)
            nSnapshot["bcbs_OriginatingSnapshotid@odata.bind"] = `/bcbs_groupconfigurationsnapshots(${xSnapshot._bcbs_originatingsnapshotid_value})`;
    }

    if (xSnapshot._bcbs_originatingsnapshotid_value)
        nSnapshot["bcbs_OriginatingSnapshotid@odata.bind"] = `/bcbs_groupconfigurationsnapshots(${xSnapshot._bcbs_originatingsnapshotid_value})`;

    if (instructions?.onCloneSetOriginatingSnapshotId)
        nSnapshot["bcbs_OriginatingSnapshotid@odata.bind"] = `/bcbs_groupconfigurationsnapshots(${instructions?.onCloneSetOriginatingSnapshotId})`;

    // bcbs_OriginatingSnapshot2id
    // ----------------------------------------------------------------------
    if (instructions?.saveType === ESaveType.EUpgradeRelease) {
        if (xSnapshot._bcbs_originatingsnapshot2id_value)
            nSnapshot["bcbs_OriginatingSnapshot2id@odata.bind"] = `/bcbs_groupconfigurationsnapshots(${xSnapshot._bcbs_originatingsnapshot2id_value})`;
    }
    // Bug https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/278707
    // The only time Originating Snapshot 2 value is set is when the record is the result of the Merge process, not the Create process.
    if (instructions?.isMergeSave && xSnapshot._bcbs_originatingsnapshot2id_value)
        nSnapshot["bcbs_OriginatingSnapshot2id@odata.bind"] = `/bcbs_groupconfigurationsnapshots(${xSnapshot._bcbs_originatingsnapshot2id_value})`;

    // bcbs_GroupCollectFinancialConfigurationid@odata.bind
    // ----------------------------------------------------------------------
    if (instructions?.saveType === ESaveType.EUpgradeRelease) {
        if (xSnapshot._bcbs_groupcollectfinancialconfigurationid_value) {
            nSnapshot["bcbs_GroupCollectFinancialConfigurationid@odata.bind"] = `/bcbs_financialconfigurations(${xSnapshot._bcbs_groupcollectfinancialconfigurationid_value})`;
        }
    }
    // confirmed with MUal, Group Collect Integration should not be carried over on any record, merge, create new, save concurrency, ...
    // if (snapshot._bcbs_groupcollectfinancialconfigurationid_value)
    //     newSnapshot["bcbs_GroupCollectFinancialConfigurationid@odata.bind"] = `/bcbs_financialconfigurations(${snapshot._bcbs_groupcollectfinancialconfigurationid_value})`;

    // bcbs_underwritingreviewcompletedate
    // ----------------------------------------------------------------------
    if (instructions?.saveType === ESaveType.EUpgradeRelease) {
        if (xSnapshot.bcbs_underwritingreviewcompletedate)
            nSnapshot.bcbs_underwritingreviewcompletedate = xSnapshot.bcbs_underwritingreviewcompletedate
    }
    // // had a conversation with MUal, this field should never carry over:
    // // It should be handled by the flow 100% of the time
    // if (snapshot.bcbs_underwritingreviewcompletedate)
    //     data.bcbs_underwritingreviewcompletedate = snapshot.bcbs_underwritingreviewcompletedate;

    // bcbs_ismnil
    // ----------------------------------------------------------------------
    if (instructions?.saveType === ESaveType.EUpgradeRelease) {
        if (xSnapshot.bcbs_ismnil)
            nSnapshot.bcbs_ismnil = xSnapshot.bcbs_ismnil;
    }

    // bcbs_isreconciliation
    // ----------------------------------------------------------------------
    if (instructions?.saveType === ESaveType.EUpgradeRelease) {
        if (xSnapshot.bcbs_isreconciliation)
            nSnapshot.bcbs_isreconciliation = xSnapshot.bcbs_isreconciliation;
    }

    // bcbs_isparentoverriddenbyuw
    // ----------------------------------------------------------------------
    if (instructions?.saveType === ESaveType.EUpgradeRelease) {
        if (xSnapshot.bcbs_isparentoverriddenbyuw)
            nSnapshot.bcbs_isparentoverriddenbyuw = xSnapshot.bcbs_isparentoverriddenbyuw;
    }

    // bcbs_UWConfirmedParentid
    // ----------------------------------------------------------------------
    if (instructions?.saveType === ESaveType.EUpgradeRelease) {
        if (xSnapshot._bcbs_uwconfirmedparentid_value)
            nSnapshot["bcbs_UWConfirmedParentid@odata.bind"] = `/bcbs_groupconfigurationsnapshots(${xSnapshot._bcbs_uwconfirmedparentid_value})`;
    }

    // bcbs_shortname
    // ----------------------------------------------------------------------
    if (instructions?.saveType === ESaveType.EUpgradeRelease) {
        if (xSnapshot.bcbs_shortname)
            nSnapshot.bcbs_shortname = xSnapshot.bcbs_shortname;
    }

    // bcbs_effectivedate
    // ----------------------------------------------------------------------
    if (xSnapshot.bcbs_effectivedate)
        nSnapshot.bcbs_effectivedate = xSnapshot.bcbs_effectivedate;

    // bcbs_iseffectivedategoldstandard
    // ----------------------------------------------------------------------
    // // had a conversation with MUal, this field should never carry over:
    // // It should be handled by the flow 100% of the time
    // if (snapshot.bcbs_iseffectivedategoldstandard)
    //     data.bcbs_iseffectivedategoldstandard = snapshot.bcbs_iseffectivedategoldstandard;

    if (instructions?.saveType === ESaveType.EUpgradeRelease)
        if (xSnapshot.bcbs_iseffectivedategoldstandard)
            nSnapshot.bcbs_iseffectivedategoldstandard = xSnapshot.bcbs_iseffectivedategoldstandard;

    // bcbs_fundingtypecode
    // ----------------------------------------------------------------------
    if (xSnapshot.bcbs_fundingtypecode)
        nSnapshot.bcbs_fundingtypecode = xSnapshot.bcbs_fundingtypecode;

    // bcbs_fundingtypecode
    // ----------------------------------------------------------------------
    if (xSnapshot.bcbs_dualfunded)
        nSnapshot.bcbs_dualfunded = xSnapshot.bcbs_dualfunded;

    // bcbs_changetypecode
    // ----------------------------------------------------------------------
    if (xSnapshot.bcbs_changetypecode)
        nSnapshot.bcbs_changetypecode = xSnapshot.bcbs_changetypecode;

    // for merge we will not be changing changetype from json
    // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/278711
    if (!instructions?.isMergeSave)
        if (state.data?.json?.changeType?.value)
            nSnapshot.bcbs_changetypecode = state.data.json.changeType.value;

    // bcbs_appurl
    // ----------------------------------------------------------------------
    // // app url should not be copied as is from the old snapshot, in case the secondary save fails
    // // it will cause the snapshot to point to source snapshot
    // if (snapshot.bcbs_appurl)
    //     newSnapshot.bcbs_appurl = snapshot.bcbs_appurl;

    // bcbs_benefitcategorylist
    // ----------------------------------------------------------------------
    if (xSnapshot.bcbs_benefitcategorylist)
        nSnapshot.bcbs_benefitcategorylist = xSnapshot.bcbs_benefitcategorylist;

    // bcbs_Releaseid
    // ----------------------------------------------------------------------
    const published = await dataSource.bcbs_config_releases_byPublishedRelease_getAsync();
    nSnapshot["bcbs_Releaseid@odata.bind"] = `/bcbs_config_releases(${published.bcbs_config_releaseid})`;

    // if (xSnapshot._bcbs_releaseid_value)
    //     nSnapshot["bcbs_Releaseid@odata.bind"] = `/bcbs_config_releases(${xSnapshot._bcbs_releaseid_value})`;
    // // if (instructions?.saveType === ESaveType.EUpgradeRelease) {
    //     nSnapshot["bcbs_Releaseid@odata.bind"] = `/bcbs_config_releases(${published.bcbs_config_releaseid})`;
    // }

    // ownerid
    // ----------------------------------------------------------------------
    if (xSnapshot._owningteam_value)
        nSnapshot["ownerid@odata.bind"] = `/teams(${xSnapshot._owningteam_value})`;

    // bcbs_operativedate
    // ----------------------------------------------------------------------
    nSnapshot.bcbs_operativedate = formatDate(new Date(), 'local:yyyy-MM-ddT00:00:00Z');

    // statuscode/statecode
    // -----------------------------------
    if (instructions?.onSaveSetStatusCode) {
        nSnapshot.statuscode = instructions.onSaveSetStatusCode;
        nSnapshot.statecode = bcbs_statecode_byStatus[instructions.onSaveSetStatusCode];
    }

    if (nSnapshot.statuscode === IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['Gold Standard(808900006)'].value) {
        throw new Error(`Cannot clone a Gold Standard snapshot.\nPlease contact Group Library Support with steps to reproduce this issue.`);
    }

    // Enrollment Detail
    // -----------------------------------
    const changeEnrollmentDetail: Partial<IXrmTypes_bcbs_enrollmentdetails> & { [key: string]: any } = {
        "bcbs_name": `Group Configuration - ${timestamp()}`,
        "statuscode": 1,
        "statecode": 0,
    }

    // bcbs_enrollmentdetails.ownerid
    // -----------------------------------
    if (xSnapshot._owningteam_value)
        changeEnrollmentDetail["ownerid@odata.bind"] = `/teams(${xSnapshot._owningteam_value})`;

    const enrollmentDetailResult = await dataSource.createAndReturnEnrollmentDetail(changeEnrollmentDetail);

    if (enrollmentDetailResult.bcbs_enrollmentdetailsid)
        nSnapshot['bcbs_EnrollmentDetailsid@odata.bind'] = `/bcbs_enrollmentdetailses(${enrollmentDetailResult.bcbs_enrollmentdetailsid})`;

    // bcbs_releaseupgradedfromid
    // -----------------------------------
    if (instructions?.saveType === ESaveType.EUpgradeRelease)
        nSnapshot['bcbs_ReleaseUpgradedFromid@odata.bind'] = `/bcbs_groupconfigurationsnapshots(${xSnapshot.bcbs_groupconfigurationsnapshotid})`;

    const clonedSnapshot = await dataSource.createGroupConfigurationSnapshot(nSnapshot);

    const cloneResult: ICloneResult = {
        snapshot: clonedSnapshot,
        change: nSnapshot,
        release: published,
    };

    console.log('[appl] dataActions.CloneSnapshotAsync cloneResult', cloneResult);

    return cloneResult;
}

/**
 * Loads the configuration asynchronously.
 *
 * @param dataSource - The data source for retrieving the configuration.
 * @param bcbs_config_release - The release of the BCBS config, if not provided, the latest release will be used.
 * @param instructions - The instructions for loading the snapshot.
 * @returns A promise that resolves to the loaded configuration and bcbs_config_release record used.
 */
export const LoadConfigAsync = async (
    dataSource: AppDataSource,
    bcbs_config_release?: IXrmTypes_bcbs_config_release,
    instructions?: ILoadSnapshotInstructions
) => {

    appInsights?.startTrackEvent(`dataActions.LoadConfigAsync`);
    const t0 = performance.now();

    type Tbcbs_config_optionsetitemSorter = (t1: IXrmTypesExtended_Ibcbs_config_optionsetitem_extended, t2: IXrmTypesExtended_Ibcbs_config_optionsetitem_extended) => number;

    const bakeAsync = async (config: FAM_IGroupConfigurationConfig): Promise<void> => {

        let optionSetItemSorter: Tbcbs_config_optionsetitemSorter = (t1, t2) => (t1.bcbs_value ?? 0) - (t2.bcbs_value ?? 0);

        // if live loading or the configuration was created after the view sort order update time, use the dynamic bcbs_viewsortorder
        if (instructions?.liveLoad || config.createdOnTime > Constants.data.viewSortOrderUpdateTime) {
            optionSetItemSorter = (t1, t2) => (t1.bcbs_viewsortorder ?? 0) - (t2.bcbs_viewsortorder ?? 0);
        }

        const setAttributeVisibilityScope = (
            entity: IXrmTypesExtended_Ibcbs_config_attribute_extended | IXrmTypesExtended_Ibcbs_config_optionsetitem_extended
        ) => {

            // Effective Date/Expiration Date
            // null -> 0
            const effectiveDate = new Date(entity.bcbs_effectivedate!).getTime();
            const expirationDate = new Date(entity.bcbs_expirationdate!).getTime();
            const effective_effectiveDate = new Date(entity.bcbs_effectivedate_effectivedate!).getTime();
            const effective_expirationDate = new Date(entity.bcbs_expirationdate_effectivedate!).getTime();

            const visibility: IFieldVisibility = {
                effectiveDate: effectiveDate,
                expirationDate: expirationDate,
                effective_effectiveDate: effective_effectiveDate,
                effective_expirationDate: effective_expirationDate,
                fundingType: null,
                erisa: null,
                grandFathered: null,
                lineOfBusiness: null,
            };

            // Funding Types Code Summary:
            entity.bcbs_fundingtypecode?.split(',').forEach(eachCode => {
                visibility.fundingType = visibility.fundingType ?? {};
                visibility.fundingType[eachCode] = true;
            });

            // ERISA code Summary:
            entity.bcbs_erisacode?.split(',').forEach(eachCode => {
                visibility.erisa = visibility.erisa ?? {};
                visibility.erisa[eachCode] = true;
            });

            // Grand Fathered Code
            entity.bcbs_grandfatheredcode?.split(',').forEach(eachCode => {
                visibility.grandFathered = visibility.grandFathered ?? {};
                visibility.grandFathered[eachCode] = true;
            });

            // Line of Business Code
            entity.bcbs_lineofbusinesscode?.split(',').forEach(eachCode => {
                visibility.lineOfBusiness = visibility.lineOfBusiness ?? {};
                visibility.lineOfBusiness[eachCode] = true;
            });


            //@ts-ignore
            entity.visibility = visibility;
        }

        // bcbs_config_categoryid -> supercategoryid and name
        const mapCategoryToSuperCategory: Record<string, { bcbs_config_supercategoryid: string; name: string }> = {
            [$cat_$lsa_id]: {
                bcbs_config_supercategoryid: $scat_$lsa_id,
                name: $scat_$lsa_name,
            },
            [$cat_$telehealth_id]: {
                bcbs_config_supercategoryid: $scat_$telehealth_id,
                name: $scat_$telehealth_name,
            },
            [$cat_$ancillary_id]: {
                bcbs_config_supercategoryid: $scat_$ancillary_id,
                name: $scat_$ancillary_name,
            },
            [$cat_$cobra_id]: {
                bcbs_config_supercategoryid: $scat_$cobra_id,
                name: $scat_$cobra_name,
            },
            [$cat_$fsa_id]: {
                bcbs_config_supercategoryid: $scat_$fsa_id,
                name: $scat_$fsa_name,
            },
            [$cat_$hra_id]: {
                bcbs_config_supercategoryid: $scat_$hra_id,
                name: $scat_$hra_name,
            },
            [$cat_$hsa_id]: {
                bcbs_config_supercategoryid: $scat_$hsa_id,
                name: $scat_$hsa_name,
            },
            [$cat_$medical_id]: {
                bcbs_config_supercategoryid: $scat_$medical_id,
                name: $scat_$medical_name,
            },
            [$cat_$pharmacy_id]: {
                bcbs_config_supercategoryid: $scat_$pharmacy_id,
                name: $scat_$pharmacy_name,
            },
            [$cat_$vision_id]: {
                bcbs_config_supercategoryid: $scat_$vision_id,
                name: $scat_$vision_name,
            },
            [$cat_$dental_id]: {
                bcbs_config_supercategoryid: $scat_$dental_id,
                name: $scat_$dental_name,
            },
        };

        config.cache = {} as any;

        // Test code:
        // config.attributes.find(t => t.bcbs_config_attributeid === '8770b9ab-9b0f-ee11-8f6d-000d3a305861')!.bcbs_effectivedate = undefined;

        // Sorting configuration elements by sort order
        // -----------------------------------------------------------------------------------------------
        config.segments = config.segments.sort((t1, t2) => t1.bcbs_sortorder! - t2.bcbs_sortorder!);
        config.tabs = config.tabs.sort((t1, t2) => t1.bcbs_sortorder! - t2.bcbs_sortorder!);
        config.sections = config.sections.sort((t1, t2) => t1.bcbs_sortorder! - t2.bcbs_sortorder!);
        config.attributes = config.attributes.sort((t1, t2) => t1.bcbs_sortorder! - t2.bcbs_sortorder!);
        config.rows = config.rows.sort((t1, t2) => t1.bcbs_sortorder! - t2.bcbs_sortorder!);
        config.tierlevels = config.tierlevels.sort((a, b) => { return (a?.bcbs_sortorder ?? 0) - (b?.bcbs_sortorder ?? 0) })
        config.optionsetitems = config.optionsetitems.sort(optionSetItemSorter);
        config.copaycoinoptions.forEach(eachOpt => {
            eachOpt.bcbs_CopayCoinOption_Config_Category = eachOpt.bcbs_CopayCoinOption_Config_Category.sort((t1, t2) => {
                return t1.bcbs_name.localeCompare(t2.bcbs_name);
            });
        });

        // Caching
        // -------------------------------------------------------------------------------------------
        config.cache.map_bcbs_config_tab = config.tabs.reduce((prev, current) => {
            prev[current.bcbs_config_tabid!] = current;
            return prev;
        }, {} as Record<FAM_TGuid, IXrmTypes_bcbs_config_tab>);

        config.cache.map_bcbs_config_section = config.sections.reduce((prev, current) => {
            prev[current.bcbs_config_sectionid!] = current;
            return prev;
        }, {} as Record<FAM_TGuid, IXrmTypes_bcbs_config_section>);

        config.cache.map_bcbs_config_row = config.rows.reduce((prev, current) => {
            prev[current.bcbs_config_rowid!] = current;
            return prev;
        }, {} as Record<FAM_TGuid, IXrmTypes_bcbs_config_row>);

        // bcbs_config_attribute
        // -------------------------------------------------------------------------------------------------
        config.cache.map_bcbs_config_attribute = {};
        config.cache.map_bcbs_config_attribute_by_masterAttributeId = {} as Record<FAM_TGuid, IXrmTypes_bcbs_config_attribute[]>;

        config.attributes.forEach((eachAttribute) => {
            config.cache.map_bcbs_config_attribute[eachAttribute.bcbs_config_attributeid!] = eachAttribute;

            const masterAttributeId = eachAttribute._bcbs_config_masterattributeid_value!;

            if (!config.cache.map_bcbs_config_attribute_by_masterAttributeId[masterAttributeId]) {
                config.cache.map_bcbs_config_attribute_by_masterAttributeId[masterAttributeId] = [];
            }
            config.cache.map_bcbs_config_attribute_by_masterAttributeId[masterAttributeId].push(eachAttribute);

            // Set the visibility scope for the attribute with friendly values
            setAttributeVisibilityScope(eachAttribute);

            const eachAttributeExtended = eachAttribute as IXrmTypesExtended_Ibcbs_config_attribute_extended

            // Non Standard Sections
            if (!eachAttributeExtended.NonStandardCustomSections) {
                eachAttributeExtended.NonStandardCustomSections = {};

                config.attributeNonStandardSections.forEach(eachNonStandardSection => {
                    if (eachNonStandardSection.bcbs_config_attributeid === eachAttributeExtended.bcbs_config_attributeid) {
                        const section = config?.sections.find(q => q.bcbs_config_sectionid === eachNonStandardSection.bcbs_config_sectionid);
                        if (section && eachAttributeExtended.NonStandardCustomSections)
                            eachAttributeExtended.NonStandardCustomSections[eachNonStandardSection.bcbs_config_sectionid!] = section;
                    }
                });
            }

            const section = config.cache.map_bcbs_config_section[eachAttributeExtended._bcbs_config_sectionid_value!];
            const tab = config.cache.map_bcbs_config_tab[section?._bcbs_config_tabid_value!];

            eachAttributeExtended.bcbs_iscancellation = tab.bcbs_iscancellation ?? false;

        });

        // bcbs_config_masterattribute
        // -------------------------------------------------------------------------------------------------
        config.cache.map_bcbs_config_masterattribute = {}
        config.cache.map_bcbs_config_masterattribute_cancellations = {};
        config.masterattributes.forEach((eachMasterAttribute) => {
            config.cache.map_bcbs_config_masterattribute[eachMasterAttribute.bcbs_config_masterattributeid!] = eachMasterAttribute;

            // Cancellation Effective Date fields
            if (eachMasterAttribute.bcbs_name === "Cancellation Effective Date") {
                config.cache.map_bcbs_config_masterattribute_cancellations[eachMasterAttribute.bcbs_config_masterattributeid] = eachMasterAttribute;
            }

        });

        // bcbs_config_category
        // -------------------------------------------------------------------------------------------------
        config.cache.map_bcbs_config_category = {};
        config.categories.forEach((bcbs_config_category) => {
            config.cache.map_bcbs_config_category[bcbs_config_category.bcbs_config_categoryid!] = bcbs_config_category;

            // Update - Prime to Florida Prime
            if (bcbs_config_category.bcbs_config_categoryid === $cat_$florida_prime_id) {
                bcbs_config_category.bcbs_name = "Florida Prime";
            }

            // Update - Super Category
            // ---------------------------------------------------------------------------------------------------------
            // should we repair config categories for super categories?
            // super category was introduced after the fact, so we need to link them up for cases where it might be missing
            // we cant just staticly load bcbs_config_category, since those have also been updated with releases.
            // does bcbs_config_category exist?
            // categories such as LSA were added after go live
            if (!bcbs_config_category._bcbs_supercategoryid_value) {
                const map = mapCategoryToSuperCategory[bcbs_config_category.bcbs_config_categoryid];
                if (map)
                    bcbs_config_category._bcbs_supercategoryid_value = map.bcbs_config_supercategoryid;
            }
        });

        config.cache.map_bcbs_config_category_named = config.categories.reduce((prev, current) => {
            const name = current.bcbs_name as IXrmTypesExtended_TCategoryName;
            prev[name] = current;
            return prev;
        }, {} as Record<IXrmTypesExtended_TCategoryName, IXrmTypes_bcbs_config_category>);

        config.cache.map_bcbs_copaycoinoption_type = config.copaycoinoptions.reduce((prev, curr) => {
            prev[curr.bcbs_config_categoryid] = curr.bcbs_CopayCoinOption_Config_Category
            return prev;
        }, {} as Record<FAM_TGuid, IXrmTypesExtended_ICopayCoin[]>);

        config.cache.map_bcbs_config_attribute_sets = config.masterattributes.filter(t => t.bcbs_attributetypecode === EControlType.attributeSet)
            .reduce((prev, curr) => {
                const u = config.attributes.filter(w => w._bcbs_masterattributesetid_value === curr.bcbs_config_masterattributeid)
                prev[curr.bcbs_config_masterattributeid!] = u;
                return prev;
            }, {} as Record<FAM_TGuid, IXrmTypes_bcbs_config_attribute[]>)

        // bcbs_config_optionsetitems
        // -------------------------------------------------------------------------------------------------
        config.cache.map_bcbs_config_optionsetitems = {} as Record<FAM_TGuid, IXrmTypes_bcbs_config_optionsetitem>;
        config.cache.map_bcbs_config_optionsetitems_bySetId = {} as Record<FAM_TGuid, IXrmTypes_bcbs_config_optionsetitem[]>;
        config.optionsetitems.forEach((eachOptionSetItem) => {

            // if (eachOptionSetItem.bcbs_config_optionsetitemid === '422d2605-bc5d-ec11-8f8f-000d3a5a062b') {
            //     eachOptionSetItem.bcbs_effectivedate = "2024-01-01"
            //     eachOptionSetItem.bcbs_expirationdate_effectivedate = "2024-01-01";
            // }


            // if (eachOptionSetItem.bcbs_config_optionsetitemid === '8ae995e9-b911-f011-9989-000d3a8f158d') {
            //     eachOptionSetItem.bcbs_effectivedate = "2024-01-01"
            //     eachOptionSetItem.bcbs_expirationdate_effectivedate = "2024-01-01";
            // }


            // // TODO setting default values, so it wont blank out until Mike/Lisa update these correctly
            // {
            //     if (!eachOptionSetItem.bcbs_erisacode)
            //         eachOptionSetItem.bcbs_erisacode = "808900000,808900001";

            //     if (!eachOptionSetItem.bcbs_grandfatheredcode)
            //         eachOptionSetItem.bcbs_grandfatheredcode = "808900000,808900001";

            //     if (!eachOptionSetItem.bcbs_lineofbusinesscode)
            //         eachOptionSetItem.bcbs_lineofbusinesscode = "808900001,808900002,808900003,808900004,808900005,808900006";

            //     if (!eachOptionSetItem.bcbs_fundingtypecode)
            //         eachOptionSetItem.bcbs_fundingtypecode = "808900000,808900001,808900002,808900003,808900004";
            // }

            // Set the visibility scope for the attribute with friendly values
            setAttributeVisibilityScope(eachOptionSetItem);

            config.cache.map_bcbs_config_optionsetitems[eachOptionSetItem.bcbs_config_optionsetitemid!] = eachOptionSetItem;

            if (!config.cache.map_bcbs_config_optionsetitems_bySetId[eachOptionSetItem._bcbs_optionsetid_value!]) {
                config.cache.map_bcbs_config_optionsetitems_bySetId[eachOptionSetItem._bcbs_optionsetid_value!] = [];
            }

            config.cache.map_bcbs_config_optionsetitems_bySetId[eachOptionSetItem._bcbs_optionsetid_value!].push(eachOptionSetItem);
        });

        // map_bcbs_bccadmin
        // -------------------------------------------
        config.cache.map_bcbs_bccadmin = {};
        config.bcbs_bccadmin.forEach(current => {
            config.cache.map_bcbs_bccadmin[current.bcbs_bccadminid!] = current;
        });

        config.cache.map_bcbs_config_tierlevels_by_tierSetId = config.tierlevels.reduce((prev, curr) => {
            // grab previous tierlevel by tier id and if it doesnt exist create a new one
            const t = prev[curr._bcbs_tiersetid_value!] ?? [];
            t.push(curr);
            prev[curr._bcbs_tiersetid_value!] = t;
            return prev;
        }, {} as Record<FAM_TGuid, IXrmTypes_bcbs_config_tierlevel[]>);

        // Optionset/DropDown mapping
        config.cache.options_bcbs_lob = ConvertToDropdownOptions(config.appOptionSets?.bcbs_lob ?? []);
        config.cache.options_bcbs_fundingtypes = ConvertToDropdownOptions(config.appOptionSets?.bcbs_fundingtypes ?? []);
        config.cache.options_bcbs_erisacode = ConvertToDropdownOptions(config.appOptionSets?.bcbs_erisacode ?? []);
        config.cache.options_bcbs_grandfatheredcode = ConvertToDropdownOptions(config.appOptionSets?.bcbs_grandfatheredcode ?? []);
        config.cache.options_bcbs_changetypecode = ConvertToDropdownOptions(config.appOptionSets?.bcbs_groupconfigurationchangetypecode ?? []);
        config.cache.options_bcbs_idcardorderingoptioncode = ConvertToDropdownOptions(config.appOptionSets?.bcbs_idcardorderingoptioncode ?? []);

        config.cache.bcbs_config_category_benefits = config.categories.filter(q => q._bcbs_config_segmentid_value === '69d33403-a832-ec11-b6e5-0022480ab88f');

        config.cache.options_status = buildDeactivateOptions();
        config.cache.options_benefitStatusReasons = buildBenefitStatusOptions();
        config.cache.options_groupConfigurationStatusReasons = buildGroupConfigurationStatusOptions();
        config.cache.options_products = buildProductOptions();

        config.cache.categoryFormDefinitions = buildCategoryFormDefinitions(config);
        config.cache.attributeSetFormDefinitions = buildAttributeSetFormDefinitions(config);

        const formDefinitions = buildFormDefinitions(config);
        config.cache.newAttributeSetFormDefinitions = formDefinitions.attributeSetFormDefinitions;
        config.cache.newNonStandardFormDefinitions = formDefinitions.nonStandardFormDefinitions;

        // REPLACE
        // config.cache.categoryFormDefinitions = categoryFormDefinitions;
        config.cache.nonStandardFormDefinitions = buildNonStandardFormDefinitions(config);

        // bcbs_config_supercategory
        config.cache.map_bcbs_config_supercategory = ArrayToLookupByKey(
            config.bcbs_config_supercategories,
            'bcbs_config_supercategoryid');

        // linking super categories to categories
        config.cache.map_bcbs_config_categories_by_bcbs_config_supercategoryid = {};
        config.cache.map_bcbs_config_supercategory_by_bcbs_config_categoryid = {};
        config.categories.forEach(bcbs_config_category => {

            const bcbs_supercategoryid = bcbs_config_category._bcbs_supercategoryid_value;

            // only benefits have a super category
            if (!bcbs_supercategoryid)
                return;

            const bcbs_config_supercategory = config.cache.map_bcbs_config_supercategory[bcbs_supercategoryid];

            config.cache.map_bcbs_config_supercategory_by_bcbs_config_categoryid[bcbs_config_category.bcbs_config_categoryid!] = bcbs_config_supercategory

            // create a map of categories by super category (bcbs_config_supercategory -> list<bcbs_config_categorie>)
            const bcbs_config_categories = config.cache.map_bcbs_config_categories_by_bcbs_config_supercategoryid[bcbs_supercategoryid] ?? {};
            bcbs_config_categories[bcbs_config_category.bcbs_config_categoryid!] = bcbs_config_category;
            config.cache.map_bcbs_config_categories_by_bcbs_config_supercategoryid[bcbs_supercategoryid] = bcbs_config_categories
        });


        // Visiblity Graph
        // ---------------------------------------------------------------------------------------------------------
        const graphProcessor = new ParentVisibilityGraphProcessor(config);
        const graph = graphProcessor.initialize(config);
        config.cache.parentVisibilityGraph = graph;
    }

    const globalOptionSetDefinitions_to_optionset = (
        globalOptionSetDefinitions: IXrmTypesExtended_IOptionSetDefinition[],
        name: IXrmGlobalOptions_TOptionSetNames
    ) => {

        const optionset = globalOptionSetDefinitions.find(q => q.Name === name);

        if (!optionset)
            throw new Error(`optionset(${name}) was not found.`);

        return optionset.Options.map(q => {
            const t: IOptionSetValue = {
                type: 'optionset',
                name: q.Label.LocalizedLabels[0].Label,
                value: q.Value
            }

            return t;
        });
    }

    const globalOptionSetDefinitions_getStaticAsync = async () => {
        const t = await import("app/GlobalOptionSetDefinitions")
        const { GlobalOptionSetDefinitions } = t;
        return GlobalOptionSetDefinitions;
    }

    const bcbs_config_categories_getStaticAsync = async () => {
        const t = await import("app/bcbs_config_categories");
        const { bcbs_config_categories } = t;
        return bcbs_config_categories;
    }

    const bcbs_config_supercategories_getStaticAsync = async () => {
        const t = await import("app/bcbs_config_supercategories")
        const { bcbs_config_supercategories } = t;
        return bcbs_config_supercategories;
    }

    let config = {} as FAM_IGroupConfigurationConfig;

    try {

        if (!bcbs_config_release) {
            console.info("[appi] dataActions.LoadConfigAsync", "bcbs_config_release is not provided, fetching the latest release.");
        }

        bcbs_config_release = bcbs_config_release ?? await dataSource.bcbs_config_releases_byPublishedRelease_getAsync();
        const configJsonFetch = dataSource.downloadConfig(bcbs_config_release.bcbs_config_releaseid!);

        const bcbs_copaycoinoptions_config_categoryFetch = dataSource.bcbs_copaycoinoption_config_category_getAsync();
        const bcbs_bccadminsFetch = dataSource.bcbs_bccadmins_getAsync();

        const [
            configJson,
            bcbs_copaycoinoptions_config_category,
            bcbs_bccadmins,
        ] = await Promise.all([
            configJsonFetch,
            bcbs_copaycoinoptions_config_categoryFetch,
            bcbs_bccadminsFetch,
        ]);

        config.segments = configJson.segments;
        config.categories = configJson.categories;
        config.tabs = configJson.tabs;
        config.sections = configJson.sections;
        config.attributes = configJson.attributes;
        config.masterattributes = configJson.masterattributes;
        config.optionsets = configJson.optionsets;
        config.optionsetitems = configJson.optionsetitems;
        config.tiersets = configJson.tiersets;
        config.tierlevels = configJson.tierlevels;
        config.rows = configJson.rows;
        config.attributeNonStandardSections = configJson.attributeNonStandardSections;
        config.copaycoinoptions = bcbs_copaycoinoptions_config_category as any;
        config.globalOptionSetDefinitions = configJson.globalOptionSetDefinitions
        config.bcbs_bccadmin = bcbs_bccadmins;
        config.bcbs_config_supercategories = configJson.bcbs_config_supercategories

        // this has to be latent load, since for earlier versions of the app, we were not capturing these
        if ((config.globalOptionSetDefinitions?.[0]?.Options?.length ?? 0) === 0) {
            config.globalOptionSetDefinitions = await globalOptionSetDefinitions_getStaticAsync()
        }

        if ((config.bcbs_config_supercategories?.length ?? 0) === 0) {
            config.bcbs_config_supercategories = await bcbs_config_supercategories_getStaticAsync()
        }

        // TODO as part of bake, we need to link supercategories to categories

        if (instructions?.liveLoad) {

            const fetchPromises = [
                dataSource.bcbs_config_segments_getAsync(),
                dataSource.bcbs_config_categories_getAsync(),
                dataSource.bcbs_config_tabs_getAsync(),
                dataSource.bcbs_config_sections_getAsync(),
                dataSource.bcbs_config_attributes_getAsync(),
                dataSource.bcbs_config_attribute_bcbs_config_sectiset_getAsync(),
                dataSource.bcbs_config_masterattributes_getAsync(),
                dataSource.bcbs_config_optionsets_getAsync(),
                dataSource.bcbs_config_optionsetitems_getAsync(),
                dataSource.bcbs_config_tiersets_getAsync(),
                dataSource.bcbs_config_tierlevels_getAsync(),
                dataSource.bcbs_copaycoinoption_config_category_getAsync(),
                dataSource.bcbs_bccadmins_getAsync(),
                dataSource.bcbs_config_rows_getAsync(),
                dataSource.bcbs_config_supercategory_getAsync(),
                dataSource.globalOptionSetDefinitions_getAsync(),
            ];

            const js = await Promise.all<any>(fetchPromises);

            [
                config.segments,
                config.categories,
                config.tabs,
                config.sections,
                config.attributes,
                config.attributeNonStandardSections,
                config.masterattributes,
                config.optionsets,
                config.optionsetitems,
                config.tiersets,
                config.tierlevels,
                config.copaycoinoptions,
                config.bcbs_bccadmin,
                config.rows,
                config.bcbs_config_supercategories,
                config.globalOptionSetDefinitions,
            ] = js as any;
        }

        // todo this is runtime info, needs to be moved out of here
        config.appOptionSets = {
            bcbs_fundingtypes: globalOptionSetDefinitions_to_optionset(config.globalOptionSetDefinitions, "bcbs_fundingtypes"),
            bcbs_groupconfigurationchangetypecode: globalOptionSetDefinitions_to_optionset(config.globalOptionSetDefinitions, "bcbs_groupconfigurationchangetypecode"),
            bcbs_erisacode: globalOptionSetDefinitions_to_optionset(config.globalOptionSetDefinitions, "bcbs_erisacode"),
            bcbs_lob: globalOptionSetDefinitions_to_optionset(config.globalOptionSetDefinitions, "bcbs_lob"),
            bcbs_grandfatheredcode: globalOptionSetDefinitions_to_optionset(config.globalOptionSetDefinitions, "bcbs_grandfatheredcode"),
            bcbs_idcardorderingoptioncode: globalOptionSetDefinitions_to_optionset(config.globalOptionSetDefinitions, "bcbs_idcardorderingoptioncode"),
        }

        config.bcbs_config_release = bcbs_config_release;

        // const releases = (await (
        //     await fetch("https://bcbsonline.crm.dynamics.com/api/data/v9.0/bcbs_config_releases?$select=bcbs_name,bcbs_config_releaseid,createdon&$orderby=createdon desc&$top=20", { headers: { Accept: "application/json" }, credentials: "include" })
        // ).json()).value.map(r => ({
        //     ...r,
        //     createdon_gettime: new Date(r.createdon).getTime()
        // }));
        // table(releases);

        config.createdOnTime = new Date(bcbs_config_release.createdon!).getTime();

        await bakeAsync(config);

        return config;

    } catch (error: any) {
        throw error;
    } finally {
        appInsights?.stopTrackEvent(`dataActions.LoadConfigAsync}`);
    }

}

export const fetch_account_snapshots_Action = (dataSource: AppDataSource, accountId: string) => {
    return async function fetch_snapshots(dispatch: TDispatch, state: () => RootState) {
        dispatch(startSnapshotLoad());
        const data = await dataSource.getGroupConfigurationSnapshotsForAccount(accountId);
        dispatch(loadSnapshotsData(data));
        dispatch(endSnapshotLoad());
    }
}
/*
export const fetchLeftCompareSnapshotWithTransformation = (dataSource: AppDataSource, accountId: string, options?: ILoadSnapshotOptions) => {
    return async function fetchTransformed(dispatch: any, getState: () => RootState) {
        dispatch(loadSnapshotAction(dataSource, accountId!, options))
        debugger;
        var comp = getState().comp;
        // after left loads, transform it - only called if right already exists
        dispatch(transformLeftSnapForCompare({leftSnapshot: getState().comp.leftSnap, rightSnapshot: getState().comp.rightSnap}))
    }
}
 
export const fetchRightCompareSnapshotWithTransformation = (dataSource: AppDataSource, accountId: string, options?: ILoadSnapshotOptions) => {
    return async function fetchTransformed(dispatch: any, getState: () => RootState) {
        dispatch(loadSnapshotAction(dataSource, accountId!, options))
        // after right loads, transform left again - only called if left already exists
        debugger;
        var comp = getState().comp;
 
        dispatch(transformLeftSnapForCompare({
            leftSnapshot: getState().comp.leftSnap,
            rightSnapshot: getState().comp.rightSnap
        }))
    }
 
}
 */

export const updatePharmacyInformationAction = (ds: AppDataSource, pharm: IPharmacyORE[]) => {
    return async function updatePharmacy(dispatch: TDispatch, state: () => RootState) {
        dispatch(updatePharmacyStatus(pharm));
        const _state = state();

        try {

            dispatch(setProcessing(true));

            let compState: RootState = {
                comp: _state.comp,
                data: {
                    json: _state.comp.rightSnap.json,
                    config: _state.comp.rightSnap.config,
                    snapshot: _state.comp.rightSnap.snapshot,
                    account: _state.comp.rightSnap.account,
                },
                router: _state.router,
                status: _state.comp.rightSnap.status
            };

            const saveResult = await SaveAsync(dispatch, ds, compState, { onSaveStatusScreen: true });

            const postSaveData: ICompareGroupConfigurationData = {
                ..._state.comp.rightSnap,
                snapshot: saveResult.snapshot!,
                status: {
                    ..._state.comp.rightSnap.status,
                    snapshotBlobVersion: saveResult.newBlobVersion!,
                }
            };

            dispatch(loadRightSnap(postSaveData));

        } catch (error) {

            alert('Encountered exception update pharmacy information...');

            // lets create a new one is payload is not providing one
            emitCustomEvent({
                eventType: 'globalException',
                details: error
            });

            throw error

        }
        finally {
            dispatch(setProcessing(false));

        }

    }
}

export const saveMergeAction = (ds: AppDataSource) => {
    return async function (dispatch: TDispatch, state: () => RootState) {

        try {
            const rootState = state();

            const newDataState: IGCData = {
                json: rootState.comp.mergeSnap.json,
                config: rootState.comp.mergeSnap.config,
                snapshot: structuredClone(rootState.comp.mergeSnap.snapshot),
                account: rootState.comp.mergeSnap.status.account!,
            }

            const changeType = IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Comprehensive Change(808900004)"];

            newDataState.snapshot._bcbs_originatingsnapshotid_value = rootState.comp.rightSnap.snapshot.bcbs_groupconfigurationsnapshotid;
            newDataState.snapshot._bcbs_originatingsnapshot2id_value = rootState.comp.leftSnap.snapshot.bcbs_groupconfigurationsnapshotid;
            newDataState.snapshot.bcbs_changetypecode = changeType.value;
            newDataState.snapshot.statuscode = IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['In Progress(1)'].value;
            newDataState.snapshot.statecode = bcbs_statecode_byStatus[newDataState.snapshot.statuscode];

            dispatch(updateAccount(rootState.comp.mergeSnap.status.account));
            dispatch(updateSnapshotData(newDataState));
            dispatch(updateSnapshotBlobVersion(''));

            dispatch(addUpdateGroupConfiguration({
                groupConfigurationChanges: {
                    changeType: {
                        name: changeType.name,
                        type: 'optionset',
                        value: changeType.value
                    }
                },
            }));

            // dispatch data fix for these        mergeRootState.data.snapshot!._bcbs_originatingsnapshotid_value = state.comp.rightSnap.snapshot.bcbs_groupconfigurationsnapshotid;

            let instructions: ISaveInstruction = {
                isMergeSave: true,
                onSaveStatusScreen: true,
                onPostSaveIgnoreReload: true,
            };

            let saveResult = await SaveAsync(dispatch, ds, state(), instructions);

            const updateMergeParent = async (localGCD: ICompareGroupConfigurationData) => {

                // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/244683
                // If parent is In Progress, Data Entry Complete, Pending Corrections, Pending Underwriting, MNIL Data Entry Complete --->
                // set the status Merged and Closed

                if (localGCD.snapshot.statuscode === IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['In Progress(1)'].value ||
                    localGCD.snapshot.statuscode === IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['Data Entry Complete(808900000)'].value ||
                    localGCD.snapshot.statuscode === IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['Corrections Pending(808900005)'].value ||
                    localGCD.snapshot.statuscode === IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['Pending Underwriting(808900009)'].value ||
                    localGCD.snapshot.statuscode === IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['MNIL Data Entry Complete(808900003)'].value) {

                    let instructions: ISaveInstruction = {
                        onSaveSetStatusCode: IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['Merged and Closed(808900010)'].value,
                        onPostSaveIgnoreReload: true,
                        onSaveStatusScreen: true,
                    };

                    let localRootState = {
                        data: {
                            // TODO maybe better to ignore saving snapshot json
                            json: localGCD.json,
                            config: localGCD.config,
                            snapshot: structuredClone(localGCD.snapshot),
                        },
                        status: {
                            account: localGCD.status.account,
                            snapshotBlobVersion: localGCD.status.snapshotBlobVersion,
                            debug: rootState.status.debug
                        } as IGroupConfigurationStatus
                    } as RootState;

                    // if above is causing issues, use this oneliner :
                    // problem is some sub processes need to be updated as well
                    // {
                    // const change: Partial<IXrmTypes_bcbs_groupconfigurationsnapshot> = {};
                    // change.statuscode = bcbs_groupconfigurationsnapshot.statuscode['Merged and Closed(808900010)'].value;
                    // change.statecode = bcbs_statecode_byStatus[change.statuscode];
                    // await ds.updateGroupConfigurationSnapshot(localGCD.snapshot.bcbs_groupconfigurationsnapshotid!, change);
                    // }

                    return await SaveAsync(dispatch, ds, localRootState, instructions);
                }
            }

            await updateMergeParent(rootState.comp.rightSnap);
            await updateMergeParent(rootState.comp.leftSnap);

            const url = `/editor/redirect?reason=view-config&accountId=${saveResult.snapshot?._bcbs_groupid_value!}&snapshotId=${saveResult.snapshot?.bcbs_groupconfigurationsnapshotid}`;
            window.location.href = url;

        } catch (error) {

            alert('Encountered exception during merge...');

            // lets create a new one is payload is not providing one
            emitCustomEvent({
                eventType: 'globalException',
                details: error
            });

            throw error;
        }
        finally {

        }

    }
}

export const SaveAsync = async (
    dispatch: TDispatch,
    dataSource: AppDataSource,
    state: RootState,
    xInstructions?: ISaveInstruction
): Promise<ISaveResult> => {

    dispatch(updateDataSavingStatus(ELoadStatus.criticalSaving));

    const newInstructions: ISaveInstruction = { ...xInstructions };

    console.log('[appl] dataAction.SaveAsync instructions', newInstructions);

    appInsights.startTrackEvent(`dataActions.SaveAsync`)

    let saveResult: ISaveResult = {};

    try {

        if (newInstructions.onSaveStatusScreen) {
            dispatch(startSnapshotLoad());
        }

        let n_bcbs_groupconfigurationsnapshot = structuredClone(state.data.snapshot)!;
        const x_bcbs_groupconfigurationsnapshot = n_bcbs_groupconfigurationsnapshot;

        let snapshotBlobVersion = state.status.snapshotBlobVersion!;
        let cloneResult: ICloneResult | null = null;

        if (
            newInstructions.isMergeSave ||
            newInstructions.onSaveClone ||
            newInstructions.saveType === ESaveType.EConcurrencyConflict ||
            newInstructions.saveType === ESaveType.EUpgradeRelease
        ) {
            // replacing snapshot will cause the following save process to use the new snapshot and will reload
            cloneResult = await CloneSnapshotAsync(dataSource, n_bcbs_groupconfigurationsnapshot, state, newInstructions);

            // movin forward use this snapshot
            n_bcbs_groupconfigurationsnapshot = cloneResult.snapshot;

            snapshotBlobVersion = '';
        }

        // If cloning due to upgrading release, lets close current snapshot
        if (cloneResult &&
            cloneResult.snapshot &&
            newInstructions.saveType === ESaveType.EUpgradeRelease
        ) {

            const saveInfo: ISnapshotInfo = {
                fileName: '',
                blobVersion: '',
                json: null
            };

            let changes: Partial<IXrmTypesExtended_IUpdatable_bcbs_groupconfigurationsnapshot> = {
                bcbs_groupconfigurationsnapshotid: x_bcbs_groupconfigurationsnapshot.bcbs_groupconfigurationsnapshotid,
                statecode: IXrmOptions_bcbs_groupconfigurationsnapshot.statecode['Inactive(1)'].value,
                statuscode: IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['Inactive(808900002)'].value,
            }

            // TODO move notes here

            const saveResult = await dataSource.save(saveInfo, changes, x_bcbs_groupconfigurationsnapshot.versionnumber);
            console.info("Inactivated previous snapshot", saveResult)
        }

        if (!n_bcbs_groupconfigurationsnapshot?.bcbs_groupconfigurationsnapshotid)
            throw new Error("Unexpected flow, group configuration snapshot was not available.");

        if (state.data.json?.iteration === null || state.data.json?.iteration === undefined)
            throw new Error("Unexpected flow, snapshot json data was not available.");

        let snapshotChanges: IXrmTypesExtended_IUpdatable_bcbs_groupconfigurationsnapshot = {
            bcbs_groupconfigurationsnapshotid: n_bcbs_groupconfigurationsnapshot.bcbs_groupconfigurationsnapshotid
        };

        // fundingType - delta - CRM loaded group configuration and local snapshot
        // --------------------------------------------------------------------
        if (state.data.json.fundingType?.value)
            if (state.data.snapshot?.bcbs_fundingtypecode !== state.data.json.fundingType.value)
                snapshotChanges.bcbs_fundingtypecode = state.data.json.fundingType?.value

        // changeType - delta - CRM loaded group configuration and local snapshot
        // --------------------------------------------------------------------
        if (state.data.json.changeType?.value)
            if (state.data.snapshot?.bcbs_changetypecode !== state.data.json.changeType.value)
                snapshotChanges.bcbs_changetypecode = state.data.json.changeType.value;

        // effectiveDate 
        // --------------------------------------------------------------------
        // - CRM loaded group configuration and local snapshot
        // * name fields depends on this
        if (state.data.json.effectiveDate?.value)
            if (!state.data.snapshot?.bcbs_effectivedate) {
                // has no value
                snapshotChanges.bcbs_effectivedate = state.data.json.effectiveDate.value;
            }
            else if (state.data.snapshot?.bcbs_effectivedate !== state.data.json.effectiveDate.value) {
                // string values are not the same? lets compare date values
                const _date1 = new Date(state.data.snapshot.bcbs_effectivedate)
                const _date2 = new Date(state.data.json.effectiveDate.value)

                if (_date1.getTime() !== _date2.getTime())
                    snapshotChanges.bcbs_effectivedate = state.data.json.effectiveDate.value;
            }


        // bcbs_operativedate
        // --------------------------------------------------------------------
        // no delta, its not stored on DATA
        // if cloned, we dont update the snapshot operative date


        // bcbs_isreconciliation
        // --------------------------------------------------------------------
        if (hasValue(state.data.json.reconciliation?.value)) {
            if (state.data.snapshot?.bcbs_isreconciliation !== state.data.json.reconciliation?.value) {
                snapshotChanges.bcbs_isreconciliation = state.data.json.reconciliation?.value;
            }
        }

        // Status
        // --------------------------------------------------------------------
        // no delta, its not stored on DATA


        // bcbs_name
        // --------------------------------------------------------------------
        // recalculating the name
        const effectiveDate = snapshotChanges.bcbs_effectivedate ?? n_bcbs_groupconfigurationsnapshot.bcbs_effectivedate ?? null;

        const changeType = newInstructions.isMergeSave
            ? IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Comprehensive Change(808900004)"].name
            : IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode[(state.data.json.changeType?.value! as keyof typeof IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode)].name;

        // example: Group Name | Effective Date mm/dd/yyyy | Change Type | Created ON (mm/dd/yyyy)
        let bcbs_name_calculated = `${state.data.account?.name ?? state.status.account?.name} | ${effectiveDate ? formatDate(parseDate(effectiveDate, 'yyyy-MM-ddThh:mm:ssZ as local'), 'local:MM/dd/yyyy') : '-'} | ${changeType} | ${formatDate(new Date(n_bcbs_groupconfigurationsnapshot.createdon!), 'en-US-ET')}`;

        if (newInstructions.onSaveSetSnapshotName)
            bcbs_name_calculated = newInstructions.onSaveSetSnapshotName

        // if calculated name is different from the record name, patch
        if (n_bcbs_groupconfigurationsnapshot.bcbs_name !== bcbs_name_calculated)
            snapshotChanges.bcbs_name = bcbs_name_calculated;

        // bcbs_shortname
        // --------------------------------------------------------------------
        if (n_bcbs_groupconfigurationsnapshot.bcbs_shortname !== state.data.json.bcbs_shortname)
            snapshotChanges.bcbs_shortname = state.data.json.bcbs_shortname;

        // bcbs_ismnil
        // --------------------------------------------------------------------
        if (!!n_bcbs_groupconfigurationsnapshot.bcbs_ismnil !== (!!state.data.json.MNIL?.value)) {
            snapshotChanges.bcbs_ismnil = (!!state.data.json.MNIL?.value);
        }

        // bcbs_isrevision
        // --------------------------------------------------------------------
        if (!!n_bcbs_groupconfigurationsnapshot.bcbs_isrevision !== (!!state.data.json.isRevision?.value)) {
            snapshotChanges.bcbs_isrevision = (!!state.data.json.isRevision?.value);
        }

        // bcbs_dualfunded
        // --------------------------------------------------------------------
        if (!!n_bcbs_groupconfigurationsnapshot.bcbs_dualfunded !== (!!state.data.json.dualFunded?.value)) {
            snapshotChanges.bcbs_dualfunded = (!!state.data.json.dualFunded?.value);
        }

        // bcbs_opportunity
        // --------------------------------------------------------------------
        if (n_bcbs_groupconfigurationsnapshot._bcbs_opportunityid_value !== (state.data.json?.opportunity?.id ?? null))
            if (state.data.json?.opportunity?.id)
                snapshotChanges['bcbs_Opportunityid@odata.bind'] = `/opportunities(${state.data.json.opportunity.id!})`;
            else
                snapshotChanges['bcbs_Opportunityid@odata.bind'] = null;

        // bcbs_benefitcategorylist
        // lets grab all distinct benefit category list
        // --------------------------------------------------------------------
        const benefitList = {} as Record<string, boolean>;

        state.data.json.benefits.forEach(benefit => {
            benefitList[benefit.bcbs_name!] = true;
        });

        const bcbs_benefitcategorylist = Object.keys(benefitList).sort().join(',');

        if (n_bcbs_groupconfigurationsnapshot.bcbs_benefitcategorylist !== bcbs_benefitcategorylist)
            snapshotChanges.bcbs_benefitcategorylist = bcbs_benefitcategorylist;

        // Emit Events
        // --------------------------------------------------------------------
        // Benefit-Status-Level

        //(lookup) Group Configuration Snapshot
        // POC code, probably will change entirely
        //TODO probably will send all this to appservice api, which then it will queue to azure service bus which then function app takes over                        
        await EmitEventProcess(dataSource);

        // status: Force change status code and following state code
        // --------------------------------------------------------------------
        if (state?.data?.json?.status?.value === FAM_EGroupConfigurationStatus.Cancelled) {
            if (n_bcbs_groupconfigurationsnapshot.statuscode !== IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['Cancelled(808900011)'].value) {
                snapshotChanges.statuscode = IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['Cancelled(808900011)'].value;
            }
        }

        if (state?.data?.json?.status?.value === FAM_EGroupConfigurationStatus['Cancellation in Progress']) {
            if (n_bcbs_groupconfigurationsnapshot.statuscode !== IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['Cancellation in Progress(808900013)'].value) {
                snapshotChanges.statuscode = IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['Cancellation in Progress(808900013)'].value;
            }
        }

        if (state?.data?.json?.status?.value === FAM_EGroupConfigurationStatus['Transferred to Other LOB']) {
            if (n_bcbs_groupconfigurationsnapshot.statuscode !== IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['Transferred to Other LOB(808900012)'].value) {
                snapshotChanges.statuscode = IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['Transferred to Other LOB(808900012)'].value;
            }
        }

        if (state?.data?.json?.status?.value === FAM_EGroupConfigurationStatus['Transferred to Other LOB in Progress']) {
            if (n_bcbs_groupconfigurationsnapshot.statuscode !== IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['Transfer to Other LOB in Progress(808900014)'].value) {
                snapshotChanges.statuscode = IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['Transfer to Other LOB in Progress(808900014)'].value;
            }
        }

        // Status: Cancellation in progress to Active needs to reset the status back to in progress
        // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/453867
        if (state?.data?.json?.status?.value === FAM_EGroupConfigurationStatus['Active']) {
            if (n_bcbs_groupconfigurationsnapshot.statuscode == IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['Cancellation in Progress(808900013)'].value) {
                snapshotChanges.statuscode = IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['In Progress(1)'].value;
            }
        }

        if (state?.data?.json?.status?.value === FAM_EGroupConfigurationStatus['Active']) {
            if (n_bcbs_groupconfigurationsnapshot.statuscode == IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['Transfer to Other LOB in Progress(808900014)'].value) {
                snapshotChanges.statuscode = IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['In Progress(1)'].value;
            }
        }

        // statuscode
        // --------------------------------------------------------------------
        // TODO move this out of here, to a admin menu as a validation save item
        if (newInstructions.onSaveForceStatusCodeToInProgress) {
            snapshotChanges.statuscode = IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['In Progress(1)'].value;
        }

        if (newInstructions.onSaveSetStatusCode) {
            if (n_bcbs_groupconfigurationsnapshot.statuscode !== newInstructions.onSaveSetStatusCode) {

                snapshotChanges.statuscode = newInstructions.onSaveSetStatusCode;

                // when a GC goes to Data Entry Complete, the Data Entry Complete date is needs to be set (always).
                if (snapshotChanges.statuscode === IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['Data Entry Complete(808900000)'].value) {
                    snapshotChanges.bcbs_dataentrycompletedate = (new Date()).toISOString();
                }

                if (snapshotChanges.statuscode === IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['Underwriting Review Complete(2)'].value ||
                    snapshotChanges.statuscode === IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['MNIL Data Entry Complete(808900003)'].value) {
                    snapshotChanges.bcbs_underwritingreviewcompletedate = (new Date()).toISOString();
                }
            }
        }

        // statecode
        // --------------------------------------------------------------------
        // lets map status code to state code
        if (snapshotChanges.statuscode) {
            snapshotChanges.statecode = bcbs_statecode_byStatus[snapshotChanges.statuscode];
        }

        // bcbs_isbypassuwandtasks
        // --------------------------------------------------------------------
        if (newInstructions.onSaveBypassUnderwritingTaskGeneration) {
            if (!n_bcbs_groupconfigurationsnapshot.bcbs_isbypassuwandtasks)
                snapshotChanges.bcbs_isbypassuwandtasks = true;
        }

        // bcbs_UWConfirmedParentid
        // --------------------------------------------------------------------
        if (newInstructions.onSaveChangeUWConfirmedParentId) {
            if (!n_bcbs_groupconfigurationsnapshot._bcbs_uwconfirmedparentid_value)
                snapshotChanges["bcbs_UWConfirmedParentid@odata.bind"] = "bcbs_groupconfigurationsnapshots(" + newInstructions.onSaveChangeUWConfirmedParentId + ")";
        }

        // bcbs_appurl
        // --------------------------------------------------------------------
        const bcbs_appurl = `${Constants.app.groupLibraryURL}/editor/redirect?reason=view-config&accountId=${n_bcbs_groupconfigurationsnapshot?._bcbs_groupid_value!}&snapshotId=${n_bcbs_groupconfigurationsnapshot?.bcbs_groupconfigurationsnapshotid}`
        if (n_bcbs_groupconfigurationsnapshot.bcbs_appurl !== bcbs_appurl)
            snapshotChanges.bcbs_appurl = bcbs_appurl;

        // bcbs_config_releaseid
        // --------------------------------------------------------------------
        if (newInstructions.onSaveUpdateRelease) {
            const publishedRelease = await dataSource.bcbs_config_releases_byPublishedRelease_getAsync();

            if (n_bcbs_groupconfigurationsnapshot._bcbs_releaseid_value !== publishedRelease.bcbs_config_releaseid)
                snapshotChanges["bcbs_Releaseid@odata.bind"] = `/bcbs_config_releases(${publishedRelease.bcbs_config_releaseid})`;

            // TODO if release gets updated, rerun whole transfomers
        }

        // bcbs_enrollmentdetailsid 
        // --------------------------------------------------------------------
        // *** care on updating dependencies beyond this point
        // example : https://localhost:44350/editor/redirect?reason=new-config&accountId=39c594d3-62d0-ea11-a812-000d3a3abb8a&snapshotId=0fa4147a-7baf-ec11-9840-000d3a3573b3
        // --------------------------------------------------------------------
        // not used at the moment
        // place holder for future if we need to save in single transaction
        if (newInstructions.onSaveCreateEnrollmentDetail) {
            // is it a brand new enrollment detail ? lets create and link it to snapshot

            // no enrollment data has been assoicated
            if (!n_bcbs_groupconfigurationsnapshot._bcbs_enrollmentdetailsid_value) {

                const enrollmentDetail = await dataSource.createAndReturnEnrollmentDetail(newInstructions.onSaveCreateEnrollmentDetail);
                snapshotChanges['bcbs_EnrollmentDetailsid@odata.bind'] = `/bcbs_enrollmentdetailses(${enrollmentDetail.bcbs_enrollmentdetailsid})`;
            }
        }

        // bcbs_filename
        // --------------------------------------------------------------------
        // *** we only want to regenerate this if it's empty
        const bcbs_filename = dataSource.generateFileNameFromSnapshot(n_bcbs_groupconfigurationsnapshot);
        if (!n_bcbs_groupconfigurationsnapshot.bcbs_filename)
            n_bcbs_groupconfigurationsnapshot.bcbs_filename = snapshotChanges.bcbs_filename = bcbs_filename;

        // Merge
        // --------------------------------------------------------------------
        // if it's a merge operation, we only need to update a few fields
        if (newInstructions.isMergeSave) {
            let overrideChange: typeof snapshotChanges = {};

            copyPropertiesOnly(
                snapshotChanges,
                overrideChange,
                [
                    'bcbs_groupconfigurationsnapshotid',
                    'bcbs_name',
                    'bcbs_appurl',
                    'bcbs_filename',
                    'bcbs_isreconciliation'
                ],
                true
            );

            snapshotChanges = overrideChange;
        }

        if (snapshotChanges.statuscode === IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['Underwriting Review Complete(2)'].value ||
            snapshotChanges.statuscode === IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['MNIL Data Entry Complete(808900003)'].value ||
            snapshotChanges.statuscode === IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['Cancelled(808900011)'].value) {

            for (const eachRevisionTask of state.data.json.revisionTasks) {
                await dataSource.revisionTaskUpdate(eachRevisionTask.id, {
                    'bcbs_RevisionSnapshotid_Task@odata.bind': `/bcbs_groupconfigurationsnapshots(${n_bcbs_groupconfigurationsnapshot.bcbs_groupconfigurationsnapshotid})`,
                });

                console.log("updating task", eachRevisionTask.id);
            }
        }

        let json = state.data.json;

        // applying updates to newJson
        if (cloneResult) {

            // making a local nonproxy object
            json = structuredClone(state.data?.json);

            n_bcbs_groupconfigurationsnapshot = { ...n_bcbs_groupconfigurationsnapshot, ...snapshotChanges };

            const cloneTransformers: IJsonTransformer[] = [];

            // if upgrading release, dont make modifications to deactivated entities
            // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/409332

            // if upgrading release
            if (newInstructions.saveType === ESaveType.EUpgradeRelease) {
                cloneTransformers.push(new UpgradeReleaseTransformer(newInstructions, x_bcbs_groupconfigurationsnapshot, n_bcbs_groupconfigurationsnapshot));
            } else {
                cloneTransformers.push(new RemoveDeactivatedDataTransformer());
                cloneTransformers.push(new PharmacyResetTransformer());
                cloneTransformers.push(new CloneTransformer(
                    state.data.config,
                    n_bcbs_groupconfigurationsnapshot,
                    false,
                    newInstructions ?? null
                ));
                cloneTransformers.push(new RevisionResetTransformer(newInstructions));
                // cloneTransformers.push(new UpgradeReleaseTransformer(newInstructions, xSnapshot, snapshot));
            }

            const transform = new JsonTransformer(cloneTransformers, EJsonTransformerRuntime.ESequential);

            if (cloneTransformers.length > 0)
                await transform.transformAsync(json, n_bcbs_groupconfigurationsnapshot);

            json.emit = true;

            // wait for clones to flush
            await EmitEventProcess(dataSource);
        }

        // Excpetion bcbs_name + bcbs_shortname
        // --------------------------------------------------------------------
        // prevent name change for gc under template account (product templates)
        // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=account&id=cb78766c-4ed5-ec11-a7b5-000d3a3ab609
        if (state.data.account?.accountid === 'cb78766c-4ed5-ec11-a7b5-000d3a3ab609') {

            if (snapshotChanges.bcbs_name) {
                console.warn("bcbs_name update has been prevented on template record")
                delete snapshotChanges.bcbs_name;
            }

            if (snapshotChanges.bcbs_shortname) {
                console.warn("bcbs_name update has been prevented on template record")
                delete snapshotChanges.bcbs_shortname;
            }
        }

        const blobChanges: ISnapshotInfo = {
            fileName: n_bcbs_groupconfigurationsnapshot.bcbs_filename!,
            blobVersion: snapshotBlobVersion,
            json: json
                ? json
                : null
        };

        if (newInstructions.ignoreJsonSave) {
            blobChanges.json = null;
        }

        saveResult = await dataSource.save(blobChanges, snapshotChanges, n_bcbs_groupconfigurationsnapshot.versionnumber);
        saveResult.instructions = newInstructions;

        // Derive relationship payloads from benefits sourced from an association template.
        // Skip when cloning — the clone path below writes its own records with action=Clone.
        if (!newInstructions.onSaveClone) {
            const configRelationships: IConfigurationRelationshipPayload[] = state.data.json.benefits
                .filter(hasBenefitProvenance)
                .map(b => ({
                    originSnapshotId: b.bcbs_associationtemplatesnapshotid!,
                    originBenefitId: b.bcbs_associationtemplatebenefitid!,
                    targetBenefitId: b.id,
                    snapshotName: state.data.snapshot?.bcbs_name,
                    categoryName: b.bcbs_name,
                    benefitName: b.name,
                    action: (b.bcbs_associationtemplateaction ?? 'Import') as 'Import' | 'Sync',
                }));

            if (configRelationships.length > 0)
                newInstructions.onSaveConfigurationRelationships = configRelationships;
        }

        // Save configuration relationship records now that the snapshot has been persisted.
        // Query existing rows first so we can skip duplicates and only PATCH when the action changed.
        if (newInstructions.onSaveConfigurationRelationships?.length) {
            const targetSnapshotId = (saveResult.snapshot ?? n_bcbs_groupconfigurationsnapshot).bcbs_groupconfigurationsnapshotid;
            const existingRelationships = targetSnapshotId
                ? await dataSource.bcbs_configurationrelationship_byTargetSnapshotId_getAsync(targetSnapshotId).catch(() => [])
                : [];
            await Promise.all(
                newInstructions.onSaveConfigurationRelationships.map(payload =>
                    dataSource.bcbs_configurationrelationship_upsertAsync(
                        buildConfigurationRelationshipRecord(payload, targetSnapshotId!),
                        existingRelationships
                    )
                )
            );
        }

        // When cloning, derive relationship records from benefits that carry association template provenance.
        // Clone saves bypass Context.tsx so the relationship payloads must be built here from the post-transform json.
        // Clone always creates a brand-new snapshot so no existing rows can exist — safe to POST directly.
        if (newInstructions.onSaveClone && cloneResult) {
            const cloneTargetSnapshotId = (saveResult.snapshot ?? n_bcbs_groupconfigurationsnapshot).bcbs_groupconfigurationsnapshotid;
            const cloneRelationships = json.benefits.filter(hasBenefitProvenance);
            if (cloneRelationships.length && cloneTargetSnapshotId) {
                await Promise.all(
                    cloneRelationships.map(b =>
                        dataSource.bcbs_configurationrelationship_saveAsync(
                            buildConfigurationRelationshipRecord({
                                snapshotName: n_bcbs_groupconfigurationsnapshot.bcbs_name,
                                action: 'Clone',
                                categoryName: b.bcbs_name,
                                benefitName: b.name,
                                originSnapshotId: b.bcbs_associationtemplatesnapshotid!,
                                originBenefitId: b.bcbs_associationtemplatebenefitid!,
                                targetBenefitId: b.id,
                            }, cloneTargetSnapshotId)
                        )
                    )
                );
            }
        }

        if (newInstructions.onSaveGenerateGoldStandardAndSQL) {

            const processes: Promise<void>[] = []
            // lets create gold standard:
            processes.push(dataSource.createProcesses({
                bcbs_recordid: saveResult.snapshot?.bcbs_groupconfigurationsnapshotid,
                bcbs_name: `${saveResult.snapshot?.bcbs_name} | Gold Standard Record Generation(M)`,
                bcbs_triggerreason: `bcbs_gsrecordgenerationurl`
            }));


            // lets create SQL:
            processes.push(dataSource.createProcesses({
                bcbs_recordid: saveResult.snapshot?.bcbs_groupconfigurationsnapshotid,
                bcbs_name: `${saveResult.snapshot?.bcbs_name} | SQL Record Generation(M)`,
                bcbs_triggerreason: `bcbs_sqlrecordgenerationurl`
            }));


            // lets create BDR:
            processes.push(dataSource.createProcesses({
                bcbs_recordid: saveResult.snapshot?.bcbs_groupconfigurationsnapshotid,
                bcbs_name: `${saveResult.snapshot?.bcbs_name} | BDR Generation(M)`,
                bcbs_triggerreason: `bcbs_autogeneratebdr`
            }));

            // lets create Benefit Detail Extract:
            processes.push(dataSource.createProcesses({
                bcbs_recordid: saveResult.snapshot?.bcbs_groupconfigurationsnapshotid,
                bcbs_name: `${saveResult.snapshot?.bcbs_name} | Benefit Detail Extract Generation(M)`,
                bcbs_triggerreason: `bcbs_autogeneratebenefitdetailextract`
            }));

            await Promise.all(processes);
        }

        dispatch(setSaveTokenOnData());

        // TODO need to talk to William Bittle about this.
        // I think it makes sense to change the arch a little to (its time...)
        // what he was suggesting here, make the responsiblity of loader to call these

        // for merge, we don't need to update the follow because merge doesn't set these initially
        // setting these will cause the BlueRelationship.tsx to fail when redirecting to the merged
        // snapshot

        // reloading snapshot record ?
        if (!newInstructions.onPostSaveIgnoreReload) {
            dispatch(updateSnapshotData({ snapshot: saveResult.snapshot }));
            dispatch(updateSnapshotBlobVersion(saveResult.newBlobVersion));
            dispatch(updateLastSaved());
        }

        // reloading json ?
        if (json && json !== state.data?.json)
            dispatch(updateSnapshotData({ json: json }));

        if (state.status.debug.exception)
            throw new Error("Testing Exception.");

        if (newInstructions.onPostSaveSetViewOnly)
            dispatch(setViewType(EViewType.ViewOnly));

        dispatch(updateDataSavingStatus(ELoadStatus.complete));

        // Cloning for debug ?
        if (state.status.debug.clone) {

            // open the new one
            (window as Window).open(AppLinkToExternal({
                id: saveResult.snapshot?.bcbs_groupconfigurationsnapshotid!,
                type: 'bcbs_groupconfigurationsnapshot'
            }));

            // lets turn of clone
            dispatch(updateDebug({ clone: false }));

            dispatch(setViewType(EViewType.ViewOnly));
        }

    } catch (err: any) {
        // we have the possibility to get two error types. One error type from BLOB storage and one from CRM.
        // check for the following error codes https://docs.microsoft.com/en-us/rest/api/storageservices/blob-service-error-codes
        // use err.code === 'LeaseAlreadyPresent', 'ConditionNotMet', 'BlobAlreadyExists' - caused by BLOB
        // use err.status === 412 - caused by the if-match optimistic concurrency failed on CRM
        if (err.code === 'LeaseAlreadyPresent' || // if someone is saving at the same time as you
            err.code === 'ConditionNotMet' || // if someone has saved on top of you
            err.code === 'BlobAlreadyExists' || // if someone has created the blob before you
            err.status === 412) { // someone has saved the CRM record on top of you

            // TODO need to talk to William Bittle about this.
            // TODO this handler needs to get out of here

            // all of these errors indicate an optimistic concurrency issue
            const snapshots = await dataSource.getGroupConfigurationSnapshotsForAccount(state?.data?.snapshot?._bcbs_groupid_value!);
            const snap = snapshots.find(s => s.bcbs_groupconfigurationsnapshotid === state?.data?.snapshot?.bcbs_groupconfigurationsnapshotid);
            const user = await dataSource.crm.getRecord<IXrmTypes_systemuser>('systemusers', trimGUIDLower(snap?._modifiedby_value!), 'fullname');
            dispatch(setConcurrencyConfirmationModal({ fullname: user.fullname ?? '', modifiedon: snap?.modifiedon! }));
            saveResult.snapshot = snap;
            return saveResult;
        }

        alert('saving failed.');

        // lets create a new one is payload is not providing one
        emitCustomEvent({
            eventType: 'globalException',
            details: err
        });

        throw err;
    } finally {

        appInsights.stopTrackEvent(`dataActions.SaveAsync`)


        if (newInstructions?.onSaveStatusScreen)
            dispatch(endSnapshotLoad());

        // enough time for beacon logs to flush (if any)
        setTimeout(() => {
            dispatch(updateDataSavingStatus(ELoadStatus.idle));
        }, 300);

        appInsights?.trackEvent({
            name: 'dataActions.SaveAsync.properties',
            properties: {
                bcbs_groupconfigurationsnapshotid: state.data.snapshot.bcbs_groupconfigurationsnapshotid,
                xInstructions: xInstructions,
                nInstructions: newInstructions,
            }
        });

        return saveResult;
    }
}

export const SaveAction = (
    dataSource: AppDataSource,
    history: any,
    instructions: ISaveInstruction,
) => {
    return async function (dispatch: TDispatch, state: () => RootState) {

        const _instructions = { ...instructions };

        console.log('[appl] dataActions.SaveAction');

        try {

            if (document.activeElement)
                (document.activeElement as any).blur();

            // giving priority to any setTimeouts on the event loop to complete if modifying data probably 0 should also work, since it will queue
            await Delay(100);

            // any data dispatches in the queue prior to save?
            if (instructions.onPreSaveUpdateJson)
                dispatch(addUpdateGroupConfiguration(instructions.onPreSaveUpdateJson));

            // lets get the latest state
            const xState = state();

            // if already saving ?
            if (xState.status.dataSavingStatus !== ELoadStatus.idle) {
                console.warn('[appl] dataActions.SaveAction eject', 'pending');
                return;
            }

            // if not in modify view ?

            if (xState.status.viewType !== EViewType.Modify && !instructions.ignoreViewType) {
                console.warn('[appl] dataActions.SaveAction eject', 'not-modfying');
                return;
            }

            if (_instructions?.saveType === ESaveType.EAuto) {
                if ((xState.data.savedOn ?? 0) > (xState.data.modifiedOn ?? -1)) {
                    // dirty changes?
                    console.warn('[appl] dataActions.SaveAction eject', 'no-dirty-change');
                    return;
                }
            }

            // latest redux state
            const saveResult = await SaveAsync(dispatch, dataSource, xState, _instructions);

            // not needed, but this gives room for the ui updates to complete
            await Delay(0);

            dispatch(updateCreatingStatus(false))

            if (saveResult.instructions?.onPostSaveSetViewOnly) {
                dispatch(setViewType(EViewType.ViewOnly));
            }

            if (_instructions.onSaveSetStatusCode && _instructions.saveType !== ESaveType.ECreate) {
                dispatch(setViewType(EViewType.ViewOnly));
                AppNavigateToExternal(history, xState, ENavigationReason.FINISHED);
            }

            if (_instructions.saveType === ESaveType.EUpgradeRelease || _instructions.onPostSaveNavigateExternalToSaveResult) {
                dispatch(setViewType(EViewType.ViewOnly));
                AppNavigateToExternal(history, xState, ENavigationReason.FINISHED);
                // const win: Window = window;
                // win.location = (`/records/blueRelationship/${xState.status.account?.accountid!}/groupConfiguration/${t.snapshot?.bcbs_groupconfigurationsnapshotid}?entry=modify`)
                window.open(`/records/blueRelationship/${xState.status.account?.accountid!}/groupConfiguration/${saveResult.snapshot?.bcbs_groupconfigurationsnapshotid}`);
            }

        } catch (error) {

            if (error instanceof ValidationError) {
                // error is expected in case of validation didnt go through
            }
            else {

                emitCustomEvent({
                    eventType: 'globalException',
                    details: error
                });

                throw error;
            }
        } finally {

        }

    }
}

export const approveUnderwritingAction = (ds: AppDataSource, history: any, approve: boolean) => {
    return async function (dispatch: TDispatch, state: () => RootState) {
        let _state = state();
        let compState: RootState = {
            comp: _state.comp,
            data: {
                json: _state.comp.rightSnap.json,
                config: _state.comp.rightSnap.config,
                snapshot: _state.comp.rightSnap.snapshot,
                account: _state.comp.rightSnap.account,
            },
            router: _state.router,
            status: _state.comp.rightSnap.status
        };

        const saveInstruction: ISaveInstruction = {
            onSaveSetStatusCode: IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['Underwriting Review Complete(2)'].value,
            onSaveStatusScreen: true,
            isUnderwritingSave: true,
        };

        if (_state.comp.rightSnap.snapshot.statuscode === IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['MNIL Data Entry Complete(808900003)'].value) {
            saveInstruction.onSaveSetStatusCode = IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['MNIL Underwriting Complete(808900004)'].value
        }
        if (!approve) {
            saveInstruction.onSaveSetStatusCode = IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['Corrections Pending(808900005)'].value;
        }

        await SaveAsync(dispatch, ds, compState, saveInstruction);

        // todo clear state
        AppNavigateToExternal(history, _state, ENavigationReason.FINISHED);
    }
}


export const SaveUnderwritingAction = (
    dataSource: AppDataSource,
    history: any,
    confirmationState: EUnderwritingConfirmationDialogState,
    comments: IComment[] | null,
) => {
    return async function (dispatch: TDispatch, state: () => RootState) {

        let navigate = true;

        if (comments) {
            dispatch(replaceComments(comments));
        }

        let _state = state();

        const saveInstruction: ISaveInstruction = {
            onSaveStatusScreen: true,
            isUnderwritingSave: true,
        }

        if (confirmationState === EUnderwritingConfirmationDialogState.Approve) {
            if (_state.data.snapshot.statuscode === IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['MNIL Data Entry Complete(808900003)'].value) {
                saveInstruction.onSaveSetStatusCode = IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['MNIL Underwriting Complete(808900004)'].value
            } else {
                saveInstruction.onSaveSetStatusCode = IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['Underwriting Review Complete(2)'].value;
            }
        } else if (confirmationState === EUnderwritingConfirmationDialogState.Corrections) {
            saveInstruction.onSaveSetStatusCode = IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['Corrections Pending(808900005)'].value;
        } else if (confirmationState === EUnderwritingConfirmationDialogState.Save) {
            navigate = false;
            saveInstruction.onSaveStatusScreen = false;
        }

        await SaveAsync(dispatch, dataSource, _state, saveInstruction);

        if (navigate) {
            history.push(
                AppLinkTo(_state, {
                    type: 'complete',
                    id: null,
                })
            );
        }
    }
}

export const entryDialogConfirmAction = (
    mode: 'view' | 'modify' | 'reopen and modify' | 'create' | 'underwriting review',
    reopenPayload?: {
        dataSource: any,
        state: any,
    },
    underwritingPayload?: {
        state: any,
        snapshot: any,
        history: any,
        accountId: string,
        snapshotId: string,
    }
) => {
    return async function (dispatch: TDispatch, state: () => RootState) {
        if (mode === 'view') {
            dispatch(setViewType(EViewType.ViewOnly));
        } else if (mode === 'modify') {
            dispatch(setViewType(EViewType.Modify));
            dispatch(updateIteration());

        } else if (mode === 'create') {
            dispatch(setViewType(EViewType.Modify));
            dispatch(updateCreatingStatus(true));

        } else if (mode === 'reopen and modify') {

            if (!reopenPayload)
                return;

            await SaveAsync(dispatch,
                reopenPayload.dataSource,
                reopenPayload.state,
                {
                    onSaveSetStatusCode: IXrmOptions_bcbs_groupconfigurationsnapshot.statuscode['In Progress(1)'].value,
                    onSaveStatusScreen: true
                }
            );

            dispatch(setViewType(EViewType.Modify));
            dispatch(updateIteration());

        } else if (mode === 'underwriting review') {

            if (!underwritingPayload)
                return;

            dispatch(setViewType(EViewType.ViewOnly))

            const foundSnapshot = compareUnderWritingReviewToWithoutDispatch(underwritingPayload.state, underwritingPayload.snapshot);

            if (foundSnapshot) {
                // if it has an originating snapshot
                const link = `/underwritingReview/${underwritingPayload.accountId}/${trimGUIDLower(foundSnapshot.bcbs_groupconfigurationsnapshotid!)}/${trimGUIDLower(underwritingPayload.state.data.snapshot.bcbs_groupconfigurationsnapshotid)}`
                underwritingPayload.history.push(link);
            } else {
                // has no originating snapshot, an initial setup case
                const link = `/underwritingReview/${underwritingPayload.accountId}/${underwritingPayload.snapshotId}/`
                underwritingPayload.history.push(link);
            }
        }
    }
}



export const loadSnapshotAction = (dataSource: AppDataSource, accountId: string, xInstructions?: ILoadSnapshotInstructions) => {
    return async function (dispatch: TDispatch, state: () => RootState) {

        console.log("[appl] dataActions.loadSnapshotAction", "instructions", xInstructions)

        const xState = state();
        const newInstructions: ILoadSnapshotInstructions = { ...xInstructions, liveLoad: xState.status.debug.liveload }

        appInsights?.trackTrace({ message: "Custom trace message" }, { width: window.innerWidth, height: window.innerHeight });

        appInsights?.startTrackEvent(`dataActions.loadSnapshotAction`);

        // to make sure store is clean prior to load
        if (newInstructions?.purgeStore) {
            dispatch(purgeOnData());
            dispatch(purgeOnStatus());
        }

        dispatch(startSnapshotLoad());

        let context: "primary" | "left" | "right" | "?" = "?";
        let bcbs_groupconfigurationsnapshotid: string | null = null;
        let bcbs_groupconfigurationsnapshotVersionId: string | null = null;

        // TODO until we can parallel load all 3three
        if (newInstructions?.leftSnapshot || newInstructions?.leftSnapshotId) {
            context = "left";
            bcbs_groupconfigurationsnapshotid = newInstructions?.leftSnapshot?.bcbs_groupconfigurationsnapshotid ?? newInstructions?.leftSnapshotId ?? null;
        }
        else if (newInstructions?.rightSnapshot || newInstructions?.rightSnapshotId) {
            context = "right";
            bcbs_groupconfigurationsnapshotid = newInstructions?.rightSnapshot?.bcbs_groupconfigurationsnapshotid ?? newInstructions?.rightSnapshotId ?? null;
        }
        else if (newInstructions?.primarySnapshot || newInstructions?.primarySnapshotId) {
            context = "primary";
            bcbs_groupconfigurationsnapshotid = newInstructions?.primarySnapshot?.bcbs_groupconfigurationsnapshotid ?? newInstructions?.primarySnapshotId ?? null;
        }

        // Snapshot version extraction
        {
            const bcbs_groupconfigurationsnapshotidWithVersionId = bcbs_groupconfigurationsnapshotid?.match(/(.*)-v(.*)/i);
            if (bcbs_groupconfigurationsnapshotidWithVersionId) {

                [, bcbs_groupconfigurationsnapshotid, bcbs_groupconfigurationsnapshotVersionId] = bcbs_groupconfigurationsnapshotidWithVersionId;
                bcbs_groupconfigurationsnapshotVersionId = URIVersionToBlobVersion(bcbs_groupconfigurationsnapshotVersionId)
            }

            if (newInstructions?.primarySnapshotVersionId)
                bcbs_groupconfigurationsnapshotVersionId = newInstructions?.primarySnapshotVersionId;
        }

        try {

            // TODO this needs to only grab the active ones, else the list is going to grow more and more
            const bcbs_groupconfigurationsnapshots = await dataSource.getGroupConfigurationSnapshotsForAccount(accountId);

            const bcbs_groupconfigurationsnapshot = bcbs_groupconfigurationsnapshotid
                ? bcbs_groupconfigurationsnapshots.find(q => trimGUIDLower(q.bcbs_groupconfigurationsnapshotid!) === trimGUIDLower(bcbs_groupconfigurationsnapshotid!))
                : null;

            // any valid snapshot loaded?
            if (!bcbs_groupconfigurationsnapshot)
                throw new Error("Snapshot not found!");

            let bcbs_config_release: IXrmTypes_bcbs_config_release | null = null;

            // fTODO temporary loading published release for all compare/merge scenarios
            if (context === 'left' || context === 'right') {
                debugger;
                bcbs_config_release = await dataSource.bcbs_config_releases_byPublishedRelease_getAsync();
            }
            else
                bcbs_config_release = await dataSource.getRelease(bcbs_groupconfigurationsnapshot._bcbs_releaseid_value!);

            // compare/merge tool?
            if ((window as any).preview)
                if (context === 'left' || context === 'right') {
                    if (newInstructions?.merge) {
                        // for merge left/right we need to load the published release
                        bcbs_config_release = await dataSource.bcbs_config_releases_byPublishedRelease_getAsync();

                    }
                    else {

                        const bcbs_config_release_state = xState?.data?.config.bcbs_config_release;

                        // for compare left/right we need to load the latest of the two config
                        // there are multiple round trips in compare load, which needs to be fixed.
                        if (bcbs_config_release_state) {

                            const bcbs_config_release_snapshot = bcbs_config_release_state.bcbs_config_releaseid === bcbs_groupconfigurationsnapshot._bcbs_releaseid_value
                                ? bcbs_config_release_state
                                : await dataSource.getRelease(bcbs_groupconfigurationsnapshot._bcbs_releaseid_value!);


                            if (bcbs_config_release_state) {
                                const bcbs_config_release_createdon_snapshot = new Date(bcbs_config_release_snapshot?.createdon!);
                                const bcbs_config_release_createdon_state = new Date(bcbs_config_release_state?.createdon!);

                                if (bcbs_config_release_createdon_snapshot.getTime() - bcbs_config_release_createdon_state.getTime() > 1) {
                                    console.log("snapshot release has later date than the store.");
                                    bcbs_config_release = bcbs_config_release_snapshot;
                                } else {
                                    bcbs_config_release = bcbs_config_release_state;
                                }

                            } else {
                                bcbs_config_release = bcbs_config_release_snapshot;
                            }

                        } else {
                            if (!bcbs_config_release) {
                                bcbs_config_release = await dataSource.getRelease(bcbs_groupconfigurationsnapshot._bcbs_releaseid_value!);
                            }
                        }
                    }
                } else {
                    if (!bcbs_config_release) {
                        bcbs_config_release = await dataSource.getRelease(bcbs_groupconfigurationsnapshot._bcbs_releaseid_value!);
                    }
                }

            if (!bcbs_config_release)
                throw new Error("Missing bcbs_config_release");

            const config = await LoadConfigAsync(
                dataSource,
                bcbs_config_release,
                newInstructions,
            );

            // TODO Sanitization Process
            // 1) scan fields for effective date
            // Show if GC effective date is in range (between attribute effective date and expiration date). Else hide and clear.
            // 2) match loaded configuration data elements with what ever group configuration snapshot has

            const bcbs_lob_byValue = objectFlip(IXrmGlobalOptions_bcbs_lob);

            // account information/it must be there
            //TODO: ask alice not to initiate loading if accountid is not there
            //TODO: search source code for my name to find todos that are really for me
            const account = await dataSource.getAccount(accountId);
            const bcbs_agencydetails = await dataSource.bcbs_agencydetails_byGroupId_getAsync(accountId);
            dispatch(updateSnapshotData({ account, bcbs_agencydetails }));

            const bcbs_acctsalesrepcode = account['_bcbs_salesrepid_value'] && await dataSource.systemusers_by_bcbs_repcode_getAsync(account['_bcbs_salesrepid_value']);

            const bcbs_acctmaintrepcode = bcbs_lob_byValue[account.bcbs_lineofbusiness!] === "National Alliance"
                ? account['_bcbs_accountexecutiveid_value'] && await dataSource.systemusers_by_bcbs_repcode_getAsync(account['_bcbs_accountexecutiveid_value']) as IXrmTypesExtended_ISystemUserExtended
                : account['_bcbs_servicerepid_value'] && await dataSource.systemusers_by_bcbs_repcode_getAsync(account['_bcbs_servicerepid_value']) as IXrmTypesExtended_ISystemUserExtended;

            const bcbs_reps = [];
            if (bcbs_acctsalesrepcode)
                bcbs_reps.push(bcbs_acctsalesrepcode);

            if (bcbs_acctmaintrepcode)
                bcbs_reps.push(bcbs_acctmaintrepcode);

            dispatch(updateSnapshotData({ bcbs_reps }));

            dispatch(updateAccount(account));

            // load all snapshots
            dispatch(updateSnapshots(bcbs_groupconfigurationsnapshots));

            let quoteRequests: IXrmTypes_bcbs_quoterequest[] | undefined = undefined;
            let opportunity: IXrmTypes_opportunity | undefined = undefined;

            if (bcbs_groupconfigurationsnapshot?._bcbs_opportunityid_value) {
                opportunity = await dataSource.opportunities_byId_getAsync(bcbs_groupconfigurationsnapshot._bcbs_opportunityid_value);
                if (opportunity) {
                    quoteRequests = await dataSource.getQuoteRequestsForOpportunity(opportunity.opportunityid!);
                }
            }

            dispatch(updateSnapshotData({
                opportunity: opportunity,
                bcbs_quoterequest: quoteRequests
            }));

            dispatch(updateSnapshotData({ snapshot: bcbs_groupconfigurationsnapshot, }));

            let resultDownloadSnapshot = await dataSource.downloadGroupConfigurationSnapshot(bcbs_groupconfigurationsnapshot, bcbs_groupconfigurationsnapshotVersionId);

            if (bcbs_groupconfigurationsnapshotVersionId)
                dispatch(updateSnapshotData({ snapshot: bcbs_groupconfigurationsnapshot, }));

            let blank: FAM_IGroupConfigurationData = { ...blankSnapshot() };
            let snapshotStatus: ESnapshotStatus = ESnapshotStatus.untracked;

            // is there a snapshot file?
            if (resultDownloadSnapshot) {
                snapshotStatus = ESnapshotStatus.loaded;
                // const fileReq = await fetch_bcbs_groupconfigurationsnapshot_bcbs_snapshot_download(authentication, snapshot.bcbs_groupconfigurationsnapshotid) as string;

                // // Test Code :
                // var x = result.json.benefits[1].nonStandards;
                // for (let index = 0; index < 100; index++) {
                //     const u = structuredClone(x);
                //     u.forEach(e => e.id = uuidv4());
                //     result.json.benefits[1].nonStandards = result.json.benefits[1].nonStandards.concat(u)
                // }

                try {
                    // const data: FAM.IGroupConfigurationData = JSON.parse(fileReq);
                    console.log('[appl] dataActions.loadSnapshotAction', "json", resultDownloadSnapshot.json);

                    if (resultDownloadSnapshot.json?.iteration !== blank.iteration)
                        console.warn("[appl] dataActions.loadSnapshotAction", "iternation-miss-match");

                }
                catch (exception) {
                    // console.error('parsing json failed for snapshot.', fileReq);
                    throw new Error('snapshot parsing failed');
                }
                finally {

                }
            } else {
                // if there is no iteration on model, means no valid data;
                // lets load blank model
                resultDownloadSnapshot = {
                    json: blank,
                    etag: ''
                };

                resultDownloadSnapshot.json.id = bcbs_groupconfigurationsnapshot.bcbs_groupconfigurationsnapshotid!;
                snapshotStatus = ESnapshotStatus.untracked;

                if (account.bcbs_lineofbusiness)
                    resultDownloadSnapshot.json.lineOfBusiness = {
                        type: 'optionset',
                        value: account.bcbs_lineofbusiness,
                        name: account['bcbs_lineofbusiness@OData.Community.Display.V1.FormattedValue'] ?? ''
                    }

                if (bcbs_groupconfigurationsnapshot.bcbs_name)
                    resultDownloadSnapshot.json.name = bcbs_groupconfigurationsnapshot.bcbs_name;

                if (bcbs_groupconfigurationsnapshot.bcbs_effectivedate)
                    resultDownloadSnapshot.json.effectiveDate = {
                        type: 'date',
                        value: bcbs_groupconfigurationsnapshot.bcbs_effectivedate
                    }

                resultDownloadSnapshot.json.changeType = {
                    type: 'optionset',
                    name: 'Initial Setup',
                    value: IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Initial Setup(808900002)"].value
                }

                console.log('blank snapshot.', bcbs_groupconfigurationsnapshot.bcbs_snapshot_name, resultDownloadSnapshot.json);
            }

            dispatch(updateSnapshotData({
                config: config,
            }));

            if (context === "left") {

                // TODO change the type of transformer to full
                /*
                -- remove deactive
                -- match config to a specific config
                -- make sure left and right use the right config for match 
                */
                const loadTransformer = JsonTransformer.getTransformerForEdit(config,
                    dataSource,
                    bcbs_groupconfigurationsnapshot,
                    account,
                    bcbs_config_release,
                    resultDownloadSnapshot.json
                )

                await loadTransformer.transformAsync(resultDownloadSnapshot.json, bcbs_groupconfigurationsnapshot);

                const leftCompareData: ICompareGroupConfigurationData = {
                    json: resultDownloadSnapshot.json,
                    config: config,
                    snapshot: bcbs_groupconfigurationsnapshot,
                    status: defaultStatusSlice(),
                    account: account,
                };

                // NOTE: we're only setting the status fields which are relevant
                // to the save function
                leftCompareData.status.snapshotBlobVersion = resultDownloadSnapshot.etag;
                leftCompareData.snapshot = bcbs_groupconfigurationsnapshot;
                leftCompareData.status.account = account;


                if (newInstructions?.compareTransformReady)
                    dispatch(transformLeftSnapForCompare({ leftSnapshot: leftCompareData, rightSnapshot: state().comp.rightSnap }));

                else
                    dispatch(loadLeftSnap(leftCompareData));

            } else if (context === "right") {

                // TODO change the type of transformer to full
                /*
                -- remove deactive
                -- match config to a specific config
                -- make sure left and right use the right config for match 
                */
                const loadTransformer = JsonTransformer.getTransformerForEdit(
                    config,
                    dataSource,
                    bcbs_groupconfigurationsnapshot,
                    account,
                    bcbs_config_release,
                    resultDownloadSnapshot.json
                )

                await loadTransformer.transformAsync(resultDownloadSnapshot.json, bcbs_groupconfigurationsnapshot);

                const rightCompareData: ICompareGroupConfigurationData = {
                    json: resultDownloadSnapshot.json,
                    config: config,
                    snapshot: bcbs_groupconfigurationsnapshot,
                    status: defaultStatusSlice(),
                    account: account,
                };

                // NOTE: we're only setting the status fields which are relevant
                // to the save function
                rightCompareData.status.snapshotBlobVersion = resultDownloadSnapshot.etag;
                rightCompareData.snapshot = bcbs_groupconfigurationsnapshot;
                rightCompareData.status.account = account;

                dispatch(loadRightSnap(rightCompareData));

                // Merge Snapshot
                // -------------------------------------
                // NOTE: setting some default data points for the merge
                const mergeSnap: ICompareGroupConfigurationData = {
                    config: rightCompareData.config,
                    json: structuredClone(rightCompareData.json),
                    snapshot: structuredClone(rightCompareData.snapshot),
                    status: structuredClone(rightCompareData.status),
                    account: structuredClone(rightCompareData.account),
                }

                // merge snapshot is always 
                // 1) Non-MNIL
                // 2) Comprehensive Change
                mergeSnap.json.MNIL = { type: 'bool', value: false };
                // removing associated opportunity in case of creating a new one
                mergeSnap.json.opportunity = null;
                let changeType = IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Comprehensive Change(808900004)"];
                mergeSnap.json.changeType = {
                    type: 'optionset',
                    name: changeType.name,
                    value: changeType.value
                }

                dispatch(loadMergeSnap(mergeSnap));

            } else if (context === "primary") {

                const loadTransformer = JsonTransformer.getTransformerForEdit(
                    config,
                    dataSource,
                    bcbs_groupconfigurationsnapshot,
                    account,
                    bcbs_config_release,
                    resultDownloadSnapshot.json
                )

                await loadTransformer.transformAsync(resultDownloadSnapshot.json, bcbs_groupconfigurationsnapshot);

                // if it has a group collect financial configuration
                if (bcbs_groupconfigurationsnapshot._bcbs_groupcollectfinancialconfigurationid_value && resultDownloadSnapshot.json.groupCollectStatus) {

                    // cloned by group collect?
                    if (resultDownloadSnapshot.json.groupCollectStatus === EGroupCollectIntegrationStatus.EUntracked) {

                        // emit event should get data ready for save
                        const transform = new JsonTransformer([
                            new AutoCalculateTransformer(config),
                            new RemoveDeactivatedDataTransformer(),
                            new PharmacyResetTransformer(),
                            new GroupCollectEmitEventTransformer(config, bcbs_groupconfigurationsnapshot, EGroupCollectIntegrationStatus.EUntracked),
                        ]);

                        await transform.transformAsync(resultDownloadSnapshot.json, bcbs_groupconfigurationsnapshot);
                    }
                    else if (resultDownloadSnapshot.json.groupCollectStatus === EGroupCollectIntegrationStatus.EModified) {
                        // updated by group collect?

                        // emit event should get data ready for save
                        const transform = new JsonTransformer([
                            new AutoCalculateTransformer(config),
                            new GroupCollectEmitEventTransformer(config, bcbs_groupconfigurationsnapshot, EGroupCollectIntegrationStatus.EModified),
                        ]);

                        await transform.transformAsync(resultDownloadSnapshot.json, bcbs_groupconfigurationsnapshot);
                    } else {
                        throw new Error(`Unexpected group collect status.`);
                    }

                    // forcing ui to ask for a save 
                    dispatch(updateModifiedOn(Date.now()));

                    // app has done its part
                    delete resultDownloadSnapshot.json.groupCollectStatus;
                }

                // cloned by group collect?
                if (resultDownloadSnapshot.json.emit) {

                    // emit event should get data ready for save
                    const transform = new JsonTransformer([
                        new CloneTransformer(config, bcbs_groupconfigurationsnapshot, true, null)
                    ]);

                    await transform.transformAsync(resultDownloadSnapshot.json, bcbs_groupconfigurationsnapshot);

                    delete resultDownloadSnapshot.json.emit;

                    console.log('[appl] emmiting events due to clone.');
                }

                // Reconcile configuration relationship records back onto benefits.
                // The relationship table is the authoritative source of template provenance —
                // any benefit whose ID matches a bcbs_target record gets its provenance fields
                // refreshed from Dynamics, overwriting whatever is in the JSON blob.
                try {
                    const relationshipRecords = await dataSource.bcbs_configurationrelationship_byTargetSnapshotId_getAsync(
                        bcbs_groupconfigurationsnapshot.bcbs_groupconfigurationsnapshotid!
                    );

                    if (relationshipRecords.length > 0) {
                        const relationshipByTargetBenefitId = ArrayToLookupByKey(relationshipRecords, 'bcbs_target');

                        resultDownloadSnapshot.json.benefits.forEach(benefit => {
                            const rel = relationshipByTargetBenefitId[benefit.id];
                            if (rel) {
                                benefit.bcbs_associationtemplatebenefitid = rel.bcbs_origin ?? benefit.bcbs_associationtemplatebenefitid;
                                benefit.bcbs_associationtemplatesnapshotid = rel._bcbs_originsnapshotid_value ?? benefit.bcbs_associationtemplatesnapshotid;
                                if (rel.bcbs_targetactioncode === ETargetActionCode.Import)
                                    benefit.bcbs_associationtemplateaction = 'Import';
                                else if (rel.bcbs_targetactioncode === ETargetActionCode.Sync)
                                    benefit.bcbs_associationtemplateaction = 'Sync';
                                // Restore the in-memory flag so the template icon shows correctly after reload
                                benefit.bcbs_fromassociationtemplate = true;
                            }
                        });
                    }
                } catch (relError) {
                    // Non-fatal: table may not exist yet. Log and continue loading.
                    console.warn('[appl] loadSnapshotAction: could not load configuration relationships (table may not be provisioned yet)', relError);
                }

                dispatch(updateSnapshotData({
                    json: resultDownloadSnapshot.json,
                    snapshot: bcbs_groupconfigurationsnapshot,
                    snapshotVersionId: newInstructions?.primarySnapshotVersionId,
                }));

                dispatch(updateSnapshotBlobVersion(resultDownloadSnapshot.etag));

                // todo maybe we can remove this
                dispatch(changeSnapshotStatus(snapshotStatus));


            }
        }
        catch (error: any) {
            alert("An error has occurred. This page cannot load.");

            // lets create a new one is payload is not providing one
            emitCustomEvent({
                eventType: 'globalException',
                details: error
            });

            throw error;

        } finally {
            dispatch(endSnapshotLoad());
            appInsights?.stopTrackEvent(`dataActions.loadSnapshotAction`);
        }
    }
}

export const loadCheckListAction = (dataSource: AppDataSource, onComplete?: (checkList: ICheckList[]) => void) => {
    return async function (dispatch: TDispatch, state: () => RootState) {

        try {

            const xState = state();

            if (!xState?.data?.snapshot?.bcbs_groupconfigurationsnapshotid)
                throw new Error("Associated snapshot was not loaded.");


            let bcbs_emchecklists = (await dataSource.bcbs_emchecklists_bySnapshot_getAsync(xState.data.snapshot.bcbs_groupconfigurationsnapshotid!))?.value ?? [];

            const checklistsToAdd: IXrmTypes_bcbs_emchecklist[] = [];
            xState.data.json.plans.forEach(eachPlan => {

                // ignore processing deacitvated plans
                if (eachPlan.deactivated)
                    return;

                // billing plans dont get a check list
                if (eachPlan.billingOnlyPlan)
                    return;

                // ignore if it has already been created for a given plan
                if (bcbs_emchecklists.find(q => q.bcbs_planid === eachPlan.id))
                    return;

                const t: IXrmTypes_bcbs_emchecklist = {
                    bcbs_name: eachPlan.name,
                    bcbs_planid: eachPlan.id,
                    _bcbs_groupconfigurationsnapshotid_value: xState.data.snapshot.bcbs_groupconfigurationsnapshotid,
                    bcbs_snapshotversion: xState.data.snapshot.bcbs_blobversion,
                } as IXrmTypes_bcbs_emchecklist;

                t.bcbs_filename = dataSource.generateEMChecklistFilename(xState.data.snapshot, t);

                checklistsToAdd.push(t);
            });

            const newCheckListsPromises = checklistsToAdd.length > 0
                ? checklistsToAdd.map(t => dataSource.create_bcbs_emchecklist(t))
                : [];

            if (newCheckListsPromises.length > 0)
                await Promise.all(newCheckListsPromises);

            // anything got created? reload
            if (checklistsToAdd.length > 0)
                bcbs_emchecklists = (await dataSource.bcbs_emchecklists_bySnapshot_getAsync(xState.data.snapshot.bcbs_groupconfigurationsnapshotid!))?.value ?? [];

            const checkList: ICheckList[] = bcbs_emchecklists.map(bcbs_emchecklist => {
                return {
                    bcbs_emchecklist: bcbs_emchecklist,
                    blob: null as any,
                    blobEtag: null,
                    versionNumber: bcbs_emchecklist.versionnumber,
                }
            });

            dispatch(updateSnapshotData({
                checkList: checkList
            }));

            onComplete?.(checkList);

        } catch (error: any) {
            // lets create a new one is payload is not providing one
            emitCustomEvent({
                eventType: 'globalException',
                details: error
            });

            console.error("loadCheckListAction, see details.", error?.toString());

            throw error;

        } finally {

        }

    }
}

export const addNonStandardToTemplateFromTemplate = (
    picks: INonstandardSearchResult[] | null,
    context: IEntityContext | null,
    payload: {
        nonStandardTemplate: FAM_INonStandardTemplate | null,
        //linkedNSCategoryType: IXrmTypes_bcbs_config_category | null,
        benefit: FAM_IBenefit | undefined,
        groupConfiguration: FAM_IGroupConfigurationData | undefined,
        //linkedNonStandards: FAM.INonStandard[] | null,
        //linkedNSSection: IXrmTypes_bcbs_config_section | null,
    }
) => {
    return async function (dispatch: TDispatch, state: () => RootState) {
        //this is the action that adds non-standards to a category from the template screen(root)
        const xState = state();
        const nonStandardTemplates: FAM_INonStandardTemplate[] = [];
        let nonStandardPayloads: INonStandardPayload[] = [];
        let nonStandardTemplatePayloads: INonStandardTemplatePayload[] = []
        if (!context) {
            console.log("No context provided for addNonStandardToTemplateFromTemplate");
            return;
        }
        let contextJson = getEntityCollection(xState.data.json, context.type)
        let updatePayload = createEntityUpdatePayload(context.type, context.entity);
        const categoryNonStandardTemplate = xState.data.config.cache.map_bcbs_config_category_named['Non-Standard Template'];

        let newRelationshipList = payload.nonStandardTemplate!.relationships!.map(rel => ({
            ...rel,
            sectionRefs: (rel.sectionRefs!.map(secRef => ({
                ...secRef,
                nonStandards: [...(secRef.nonStandards || [])] // Clone the nonstandards array
            })) || [])
        }));

        picks?.forEach(pick => {
            //we are adding a non standard from template side
            if (!payload.nonStandardTemplate) { return; }

            const fieldMap = ArrayToLookupByPredicate(payload.nonStandardTemplate.fields, q => q.configRef.id);
            const linkedConfigCategory = xState.data.config.cache.map_bcbs_config_category[payload.nonStandardTemplate?.linkedCategoryTypeId!];

            // lets create a new nonstandard template as
            let template: FAM_INonStandardTemplate = createNonStandardTemplate(
                xState.data,
                {
                    id: "",
                    name: `Non-StandardTemplate - New - ${timestamp()}`,
                    category: categoryNonStandardTemplate,
                    linkedCategory: linkedConfigCategory ?? null,
                    linkedCategoryName: payload.nonStandardTemplate.linkedCategoryName ?? null,
                    relationships: payload.nonStandardTemplate.relationships ?? []
                }
            );

            let tierChanges: ITierLevelChange[] = [];

            // transfer matching properties 
            template.name = payload.nonStandardTemplate.name;
            template.id = payload.nonStandardTemplate.id;

            //get the pick category type
            var pickSpecificCategoryId = pick.item.parent.id;

            // Find the category in the template to update
            let category = newRelationshipList.find(cat => cat.categoryID === pickSpecificCategoryId);
            const clonedNS: FAM_INonStandard = { ...pick.item.nonStandard, partOfTemplate: true }
            if (category) {
                // Find or create the section within the category
                let section = category.sectionRefs.find(sec => sec.sectionId === pick.item.nonStandard.sectionRef?.id);

                if (section) {
                    // Section exists, add the non-standard to it
                    section.nonStandards = [...section.nonStandards, { id: clonedNS.id, name: template.name }];
                } else {
                    // Section doesn't exist, create it and add the non-standard
                    const newSectionRef = {
                        sectionId: pick.item.nonStandard.sectionRef?.id || '',
                        nonStandards: [{ id: clonedNS.id, name: template.name }]
                    };
                    category.sectionRefs = [...(category.sectionRefs || []), newSectionRef];
                }
            } else {
                // Category doesn't exist, create a new category with the section and non-standard
                var newSection = xState.data.config.cache.map_bcbs_config_section[pick.item.nonStandard.sectionRef!.id];
                let categoryID = newSection!._bcbs_config_categoryid_value!;
                let newCat = xState.data.config.cache.map_bcbs_config_category[categoryID]
                const newCategory = {
                    categoryID: pickSpecificCategoryId ?? "",
                    categoryType: newCat?.bcbs_name ?? "",
                    sectionRefs: [{
                        sectionId: pick.item.nonStandard.sectionRef?.id || '',
                        nonStandards: [structuredClone(pick.item.nonStandard)]
                    }]
                };
                newRelationshipList = [...newRelationshipList, newCategory];
            }

            //need to add the new nonstandard to every relationship other than the one we just added to
            newRelationshipList.forEach(cat => {
                if (cat.categoryID !== pickSpecificCategoryId) {
                    //send the linkedNS to matching. determine if there is a match and return a NONStandard then do conditional stuff
                    let matchingNonStandardIds = matchingNonStandard(xState, pick.item.nonStandard, template, cat, contextJson);

                    //if we have ids in the list, we can skip this creation of new ones
                    if (matchingNonStandardIds && matchingNonStandardIds.length > 0) {
                        matchingNonStandardIds.forEach(id => {
                            let benefit = contextJson.find(q => q.id === cat.categoryID);
                            //get the nonstandard from the GC or Benefit
                            let categoryNonStandard = benefit ? benefit.nonStandards.find(q => q.id === id) : xState.data.json.nonStandards.find(q => q.id === id);
                            if (categoryNonStandard) {
                                //update the matched nonstandards for this category
                                const updatedNonStandard: FAM_INonStandard = structuredClone(categoryNonStandard);
                                updatedNonStandard.name = template.name;
                                updatedNonStandard.partOfTemplate = true;

                                let nonStandardBenefitDefintion = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_benefitdefinition_id);
                                let nonStandardPayAccordingToBenefit = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_doesthisbenefitpayaccoridngtotherelatedbe_id);

                                let templateDefintion = fieldMap[$att_$non_standard_template_$bcbs_nst_benefitdefinition_id];

                                const benefitDefinitionValue = templateDefintion.values[0].value; //template value
                                nonStandardBenefitDefintion!.values[0].value = structuredClone(benefitDefinitionValue); //ns value

                                if (nonStandardPayAccordingToBenefit) {
                                    let templatePayAccordingToBenefit = fieldMap[$att_$non_standard_template_$bcbs_nst_doesthisbenefitpayaccoridngtotherelatedbe_id];
                                    const payAccordingToBenefitValue = templatePayAccordingToBenefit.values[0].value; //template value
                                    nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value
                                }

                                // Find or create the section within the category
                                let section = cat.sectionRefs.find(sec => sec.sectionId === pick.item.nonStandard.sectionRef?.id);

                                if (section) {
                                    // Section exists, add the non-standard to it
                                    section.nonStandards = [...section.nonStandards, { id: updatedNonStandard.id, name: updatedNonStandard.name }];
                                } else {
                                    // Section doesn't exist, create it and add the non-standard
                                    const newSectionRef = {
                                        sectionId: pick.item.nonStandard.sectionRef?.id || '',
                                        nonStandards: [{ id: updatedNonStandard.id, name: updatedNonStandard.name }]
                                    };
                                    cat.sectionRefs = [...(cat.sectionRefs || []), newSectionRef];
                                }

                                const nonStandardPayload: INonStandardPayload = {
                                    config: xState.data.config,
                                    nonStandards: [updatedNonStandard],
                                    ...updatePayload,
                                    changeTracker: {}
                                };
                                nonStandardPayloads.push(nonStandardPayload);
                                // dispatch(addUpdateNonStandards([{
                                //     config: xState.data.config,
                                //     nonStandards: [updatedNonStandard],
                                //     benefit: benefit,
                                //     //groupConfiguration: props.groupConfiguration,
                                //     changeTracker: {}
                                // }]));
                            }
                        })

                    } else {
                        let newNonStandardSection = xState.data.config.cache.map_bcbs_config_section[pick.item.nonStandard.sectionRef!.id];
                        let benefit = contextJson.find(ben => (ben.id === cat.categoryID));
                        const newNonStandard: FAM_INonStandard = createNonStandard(
                            xState.data,
                            {
                                section: newNonStandardSection!,
                                ...updatePayload,
                                templated: true,
                                name: payload.nonStandardTemplate!.name
                            }
                        );
                        const fieldMap = ArrayToLookupByPredicate(payload.nonStandardTemplate!.fields, q => q.configRef.id);
                        //this newly created one needs to have the fields from tempalte - for some reason these ones arent caught in the wide update
                        let nonStandardBenefitDefintion = newNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_benefitdefinition_id);
                        let nonStandardPayAccordingToBenefit = newNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_doesthisbenefitpayaccoridngtotherelatedbe_id);
                        newNonStandard.partOfTemplate = true;
                        newNonStandard.name = payload.nonStandardTemplate!.name;

                        let templateDefintion = fieldMap[$att_$non_standard_template_$bcbs_nst_benefitdefinition_id];

                        const benefitDefinitionValue = templateDefintion!.values[0].value; //template value
                        nonStandardBenefitDefintion!.values[0].value = structuredClone(benefitDefinitionValue); //ns value

                        if (nonStandardPayAccordingToBenefit) {
                            let templatePayAccordingToBenefit = fieldMap[$att_$non_standard_template_$bcbs_nst_doesthisbenefitpayaccoridngtotherelatedbe_id];
                            const payAccordingToBenefitValue = templatePayAccordingToBenefit.values[0].value; //template value
                            nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value
                        }

                        const nonStandardPayload: INonStandardPayload = {
                            config: xState.data.config,
                            nonStandards: [newNonStandard],
                            ...updatePayload,
                            changeTracker: {}
                        };
                        nonStandardPayloads.push(nonStandardPayload);
                        // dispatch(addUpdateNonStandards([{
                        //     config: xState.data.config,
                        //     nonStandards: [newNonStandard],
                        //     benefit: benefit,
                        //     //groupConfiguration: props.groupConfiguration,
                        //     changeTracker: {}
                        // }]));
                        // Find or create the section within the category
                        let section = cat.sectionRefs.find(sec => sec.sectionId === pick.item.nonStandard.sectionRef?.id);

                        if (section) {
                            // Section exists, add the non-standard to it
                            section.nonStandards = [...section.nonStandards, { id: newNonStandard.id, name: newNonStandard.name }];
                        } else {
                            // Section doesn't exist, create it and add the non-standard
                            const newSectionRef = {
                                sectionId: pick.item.nonStandard.sectionRef?.id || '',
                                nonStandards: [{ id: clonedNS.id, name: clonedNS.name }]
                            };
                            cat.sectionRefs = [...(cat.sectionRefs || []), newSectionRef];
                        }
                    }
                } else {
                    //this is our current benefit/group config, just update nonstandard in it
                    // update the intial NS to be considered Templated.
                    //const updatedNonStandard: FAM.INonStandard = { ...pick.item.nonStandard, partOfTemplate: true };
                    let updatedNonStandard = structuredClone(pick.item.nonStandard);
                    let benefit = contextJson.find(ben => (ben.id === cat.categoryID));
                    updatedNonStandard.partOfTemplate = true;
                    updatedNonStandard.name = template.name;
                    let nonStandardBenefitDefintion = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_benefitdefinition_id);
                    let nonStandardPayAccordingToBenefit = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_doesthisbenefitpayaccoridngtotherelatedbe_id);
                    let templateDefintion = fieldMap[$att_$non_standard_template_$bcbs_nst_benefitdefinition_id];

                    const benefitDefinitionValue = templateDefintion!.values[0].value; //template value
                    nonStandardBenefitDefintion!.values[0].value = structuredClone(benefitDefinitionValue); //ns value

                    if (nonStandardPayAccordingToBenefit) {
                        let templatePayAccordingToBenefit = fieldMap[$att_$non_standard_template_$bcbs_nst_doesthisbenefitpayaccoridngtotherelatedbe_id];
                        const payAccordingToBenefitValue = templatePayAccordingToBenefit.values[0].value; //template value
                        nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value
                    }

                    const nonStandardPayload: INonStandardPayload = {
                        config: xState.data.config,
                        nonStandards: [updatedNonStandard],
                        ...updatePayload,
                        changeTracker: {}
                    };
                    nonStandardPayloads.push(nonStandardPayload);
                    // dispatch(addUpdateNonStandards([{
                    //     config: xState.data.config,
                    //     nonStandards: [updatedNonStandard],
                    //     benefit: payload.benefit,
                    //     //groupConfiguration: props.groupConfiguration,
                    //     changeTracker: {}
                    // }]));
                }
            });

            template.fields.forEach(templateField => {

                const field = fieldMap[templateField.configRef.id];

                // did we find a matching field
                if (field) {
                    if (field.values.length === 0) {
                        // nop
                    }
                    else if (field.values.length === 1 && templateField.values.length === 1) {

                        const value = field.values[0].value;
                        templateField.values[0].value = structuredClone(value);

                    } else if (field.values.length > 1) {
                        let predicate = (oldValue: FAM_IFlexibleFieldValue, newValue: FAM_IFlexibleFieldValue) => {
                            return oldValue.type === newValue.type;
                        }
                        if (tierChanges.length > 0) {
                            predicate = (oldValue: FAM_IFlexibleFieldValue, newValue: FAM_IFlexibleFieldValue) => {
                                return !!tierChanges.find(q => q.oldTier?.id === oldValue.tierRef?.id && q.newTier?.id === newValue.tierRef?.id)
                            };
                        }

                        templateField.values.forEach(t => {

                            const value = field.values.find(q => predicate(q, t));

                            if (value?.value) {
                                t.value = structuredClone(value.value);
                            }
                        });
                    }
                }
            });

            const updatedTemplate = {
                ...template,
                relationships: newRelationshipList
            };
            nonStandardTemplates.push(updatedTemplate);
        });


        if (nonStandardTemplates && nonStandardTemplates.length > 0) {
            dispatch(addUpdateNonStandards(nonStandardPayloads))
            dispatch(
                addUpdateNonStandardTemplates([
                    {
                        nonStandardTemplates: nonStandardTemplates
                    }
                ])
            );
            updateAllTemplatedNonStandardsFields(nonStandardTemplates[0], context);
        }
    }
}

export const existingNonStandardToTemplateAction = ( //link an existing non-standard to an existing template (trhough the wizard)
    picks: FAM_INonStandardTemplate[],
    context: IEntityContext | null,
    payload: {
        //section: IXrmTypes_bcbs_config_section,
        linkedNSCategoryType: IXrmTypes_bcbs_config_category | null,
        benefit: FAM_IBenefit | undefined,
        groupConfiguration: FAM_IGroupConfigurationData | undefined,
        accountsReceivable?: FAM_IAccountsReceivable,
        feesAndPremium?: FAM_IFeesAndPremiums,
        linkedNonStandards: FAM_INonStandard[] | null,
        linkedNSSection: IXrmTypes_bcbs_config_section | null,
    }) => {
    return async function (dispatch: TDispatch, state: () => RootState) {
        const xState = state();
        const nonStandardTemplates: FAM_INonStandardTemplate[] = [];
        let nonStandardPayloads: INonStandardPayload[] = [];
        let nonStandardTemplatePayloads: INonStandardTemplatePayload[] = []
        const categoryNonStandardTemplate = xState.data.config.cache.map_bcbs_config_category_named['Non-Standard Template'];
        if (!context) {
            console.log("No context provided for existingNonStandardToTemplateAction");
            return;
        }
        let contextJson = getEntityCollection(xState.data.json, context.type)
        let updatePayload = createEntityUpdatePayload(context.type, context.entity);
        //non standard -> template side
        //THIS IS TO CONNECT AN EXISTING NON STANDARD TO A TEMPLATE
        picks?.forEach(pick => {

            const fieldMap = ArrayToLookupByPredicate(pick.fields, q => q.configRef.id);
            const linkedConfigCategory = xState.data.config.cache.map_bcbs_config_category[pick.linkedCategoryTypeId!];

            // lets create a new nonstandard template as
            let template: FAM_INonStandardTemplate = createNonStandardTemplate(
                xState.data,
                {
                    id: "",
                    name: `Non-StandardTemplate - New - ${timestamp()}`,
                    category: categoryNonStandardTemplate,
                    linkedCategory: linkedConfigCategory ?? payload.linkedNSCategoryType,
                    linkedCategoryName: pick.linkedCategoryName ?? payload.linkedNSCategoryType?.bcbs_name!,
                    relationships: pick.relationships ?? []
                }
            );

            let tierChanges: ITierLevelChange[] = [];

            // transfer matching properties 
            template.name = pick.name;
            template.id = pick.id;


            //let newNonStandardList: FAM.INonStandard[] = [];
            let newRelationshipList = template.relationships!.map(rel => ({
                ...rel,
                sectionRefs: (rel.sectionRefs!.map(secRef => ({
                    ...secRef,
                    nonStandards: [...(secRef.nonStandards || [])] // Clone the nonstandards array
                })) || [])
            }));

            if (payload.linkedNonStandards !== null) {
                payload.linkedNonStandards?.forEach(linkedNS => {
                    //when linking new non standard, we need to check if the section id already exists on the current category
                    //so, get current category of the ns
                    let incomingNSCategoryId = context.entityId;

                    // Find the category in the template to update
                    let category = newRelationshipList.find(cat => cat.categoryID === incomingNSCategoryId);
                    const clonedNS: FAM_INonStandard = { ...linkedNS, partOfTemplate: true }
                    if (category) {
                        // Find or create the section within the category
                        let section = category.sectionRefs.find(sec => sec.sectionId === linkedNS.sectionRef?.id);

                        if (section) {
                            //section exists on template, add this nonStandard to it
                            section.nonStandards = [...section.nonStandards, { id: clonedNS.id, name: template.name }];
                        } else {
                            // Section doesn't exist, create it and add the non-standard
                            const newSectionRef = {
                                sectionId: linkedNS.sectionRef?.id || '',
                                nonStandards: [{ id: clonedNS.id, name: template.name }]
                            };
                            category.sectionRefs = [...(category.sectionRefs || []), newSectionRef];
                        }
                    } else {
                        // Category doesn't exist, create a new category with the section and non-standard
                        let categoryId = context.entityId;
                        const newCategory = {
                            categoryID: categoryId ?? "",
                            categoryType: payload.linkedNSCategoryType?.bcbs_name ?? "",
                            sectionRefs: [{
                                sectionId: linkedNS.sectionRef?.id || '',
                                nonStandards: [{ id: clonedNS.id, name: template.name }]
                            }]
                        };
                        newRelationshipList = [...newRelationshipList, newCategory];
                    }

                    //need to add the new nonstandard to every relationship other than the one we just added to
                    newRelationshipList.forEach(cat => {
                        if (cat.categoryID !== incomingNSCategoryId) {
                            //send the linkedNS to matching. determine if there is a match and return a NONStandard then do conditional stuff
                            let matchingNonStandardIds = matchingNonStandard(xState, linkedNS, template, cat, contextJson);

                            //if we have ids in the list, we can skip this creation of new ones
                            if (matchingNonStandardIds && matchingNonStandardIds.length > 0) {
                                matchingNonStandardIds.forEach(id => {
                                    let benefit = contextJson.find(q => q.id === cat.categoryID);
                                    //get the nonstandard from the GC or Benefit
                                    let categoryNonStandard = benefit ? benefit.nonStandards.find(q => q.id === id) : xState.data.json.nonStandards.find(q => q.id === id);
                                    if (categoryNonStandard) {
                                        //update the matched nonstandards for this category
                                        const updatedNonStandard: FAM_INonStandard = structuredClone(categoryNonStandard);
                                        updatedNonStandard.partOfTemplate = true;
                                        updatedNonStandard.name = categoryNonStandard.name;

                                        let nonStandardBenefitDefintion = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_benefitdefinition_id);
                                        let nonStandardPayAccordingToBenefit = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_doesthisbenefitpayaccoridngtotherelatedbe_id);

                                        let templateDefintion = fieldMap[$att_$non_standard_template_$bcbs_nst_benefitdefinition_id];

                                        const benefitDefinitionValue = templateDefintion.values[0].value; //template value
                                        nonStandardBenefitDefintion!.values[0].value = structuredClone(benefitDefinitionValue); //ns value

                                        if (nonStandardPayAccordingToBenefit) {
                                            let templatePayAccordingToBenefit = fieldMap[$att_$non_standard_template_$bcbs_nst_doesthisbenefitpayaccoridngtotherelatedbe_id];
                                            const payAccordingToBenefitValue = templatePayAccordingToBenefit.values[0].value; //template value
                                            nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value
                                        }

                                        // Find or create the section within the category
                                        let section = cat.sectionRefs.find(sec => sec.sectionId === linkedNS.sectionRef?.id);

                                        if (section) {
                                            // Section exists, add the non-standard to it

                                            section.nonStandards = [...section.nonStandards, { id: updatedNonStandard.id, name: updatedNonStandard.name }];
                                        } else {
                                            // Section doesn't exist, create it and add the non-standard
                                            const newSectionRef = {
                                                sectionId: linkedNS.sectionRef?.id || '',
                                                nonStandards: [{ id: updatedNonStandard.id, name: updatedNonStandard.name }]
                                            };
                                            cat.sectionRefs = [...(cat.sectionRefs || []), newSectionRef];
                                        }


                                        const nonStandardPayload: INonStandardPayload = {
                                            config: xState.data.config,
                                            nonStandards: [updatedNonStandard],
                                            ...updatePayload,
                                            changeTracker: {}
                                        }
                                        nonStandardPayloads.push(nonStandardPayload);
                                        // dispatch(addUpdateNonStandards([{
                                        //     config: xState.data.config,
                                        //     nonStandards: [updatedNonStandard],
                                        //     benefit: benefit,
                                        //     groupConfiguration: payload.groupConfiguration,
                                        //     changeTracker: {}
                                        // }]));
                                    }
                                })

                            } else {
                                let benefit = contextJson.find(ben => (ben.id === cat.categoryID));
                                const newNonStandard: FAM_INonStandard = createNonStandard(
                                    xState.data,
                                    {
                                        section: payload.linkedNSSection!,
                                        ...updatePayload,
                                        templated: true,
                                        name: pick.name,
                                    }
                                );

                                //this newly created one needs to have the fields from tempalte - for some reason these ones arent caught in the wide update
                                let nonStandardBenefitDefintion = newNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_benefitdefinition_id);
                                let nonStandardPayAccordingToBenefit = newNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_doesthisbenefitpayaccoridngtotherelatedbe_id);
                                let templateDefintion = fieldMap[$att_$non_standard_template_$bcbs_nst_benefitdefinition_id];

                                const benefitDefinitionValue = templateDefintion!.values[0].value; //template value
                                nonStandardBenefitDefintion!.values[0].value = structuredClone(benefitDefinitionValue); //ns value

                                if (nonStandardPayAccordingToBenefit) {
                                    let templatePayAccordingToBenefit = fieldMap[$att_$non_standard_template_$bcbs_nst_doesthisbenefitpayaccoridngtotherelatedbe_id];
                                    const payAccordingToBenefitValue = templatePayAccordingToBenefit.values[0].value; //template value
                                    nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value
                                }

                                const nonStandardPayload: INonStandardPayload = {
                                    config: xState.data.config,
                                    nonStandards: [newNonStandard],
                                    ...updatePayload,
                                    changeTracker: {}
                                };
                                nonStandardPayloads.push(nonStandardPayload);
                                // dispatch(addUpdateNonStandards([{
                                //     config: xState.data.config,
                                //     nonStandards: [newNonStandard],
                                //     benefit: benefit,
                                //     groupConfiguration: payload.groupConfiguration,
                                //     changeTracker: {}
                                // }]));
                                // Find or create the section within the category
                                let section = cat.sectionRefs.find(sec => sec.sectionId === linkedNS.sectionRef?.id);

                                if (section) {
                                    // Section exists, add the non-standard to it

                                    section.nonStandards = [...section.nonStandards, { id: newNonStandard.id, name: newNonStandard.name }];
                                } else {
                                    // Section doesn't exist, create it and add the non-standard
                                    const newSectionRef = {
                                        sectionId: linkedNS.sectionRef?.id || '',
                                        nonStandards: [{ id: newNonStandard.id, name: newNonStandard.name }]
                                    };
                                    cat.sectionRefs = [...(cat.sectionRefs || []), newSectionRef];
                                }
                            }
                        }
                    });
                    // update the intial NS to be considered Templated and update the fields.
                    const updatedNonStandard: FAM_INonStandard = structuredClone(linkedNS);
                    updatedNonStandard.partOfTemplate = true;
                    updatedNonStandard.name = template.name;
                    let nonStandardBenefitDefintion = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_benefitdefinition_id);
                    let nonStandardPayAccordingToBenefit = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_doesthisbenefitpayaccoridngtotherelatedbe_id);
                    let templateDefintion = fieldMap[$att_$non_standard_template_$bcbs_nst_benefitdefinition_id];

                    const benefitDefinitionValue = templateDefintion!.values[0].value; //template value
                    nonStandardBenefitDefintion!.values[0].value = structuredClone(benefitDefinitionValue); //ns value

                    if (nonStandardPayAccordingToBenefit) {
                        let templatePayAccordingToBenefit = fieldMap[$att_$non_standard_template_$bcbs_nst_doesthisbenefitpayaccoridngtotherelatedbe_id];
                        const payAccordingToBenefitValue = templatePayAccordingToBenefit.values[0].value; //template value
                        nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value
                    }

                    const nonStandardPayload: INonStandardPayload = {
                        config: xState.data.config,
                        nonStandards: [updatedNonStandard],
                        ...updatePayload,
                        changeTracker: {}
                    };
                    nonStandardPayloads.push(nonStandardPayload);
                    // dispatch(addUpdateNonStandards([{
                    //     config: xState.data.config,
                    //     nonStandards: [updatedNonStandard],
                    //     benefit: payload.benefit,
                    //     groupConfiguration: payload.groupConfiguration,
                    //     changeTracker: {}
                    // }]));
                });
            }

            template.fields.forEach(templateField => {

                const field = fieldMap[templateField.configRef.id];

                // did we find a matching field
                if (field) {
                    if (field.values.length === 0) {
                        // nop
                    }
                    else if (field.values.length === 1 && templateField.values.length === 1) {

                        const value = field.values[0].value;
                        templateField.values[0].value = structuredClone(value);

                    } else if (field.values.length > 1) {
                        let predicate = (oldValue: FAM_IFlexibleFieldValue, newValue: FAM_IFlexibleFieldValue) => {
                            return oldValue.type === newValue.type;
                        }
                        if (tierChanges.length > 0) {
                            predicate = (oldValue: FAM_IFlexibleFieldValue, newValue: FAM_IFlexibleFieldValue) => {
                                return !!tierChanges.find(q => q.oldTier?.id === oldValue.tierRef?.id && q.newTier?.id === newValue.tierRef?.id)
                            };
                        }

                        templateField.values.forEach(t => {

                            const value = field.values.find(q => predicate(q, t));

                            if (value?.value) {
                                t.value = structuredClone(value.value);
                            }
                        });
                    }
                }
            });

            const updatedTemplate = {
                ...template,
                relationships: newRelationshipList
            };
            nonStandardTemplates.push(updatedTemplate);
        });

        if (nonStandardTemplates && nonStandardTemplates.length > 0) {

            dispatch(addUpdateNonStandards(nonStandardPayloads));
            dispatch(
                addUpdateNonStandardTemplates([
                    {
                        nonStandardTemplates: nonStandardTemplates
                    }
                ])
            );
            updateAllTemplatedNonStandardsFields(nonStandardTemplates[0], context);
        }
    }
}

export const newNonStandardFromTemplateAction = (
    picks: FAM_INonStandardTemplate[],
    context: IEntityContext | null,
    payload: {
        section: IXrmTypes_bcbs_config_section,
        benefit: FAM_IBenefit | undefined,
        groupConfiguration: FAM_IGroupConfigurationData | undefined,
        accountsReceivable?: FAM_IAccountsReceivable
        feesAndPremiums?: FAM_IFeesAndPremiums
        stopLoss?: FAM_IStopLoss
    }) => {
    return async function (dispatch: TDispatch, state: () => RootState) {
        var xState = state();
        let nonStandardPayloads: INonStandardPayload[] = [];
        let nonStandardTemplatePayloads: INonStandardTemplatePayload[] = []
        if (!context) {
            console.log("No context provided for newNonStandardFromTemplateAction");
            return;
        }
        // updatePayload is used for the current context entity
        // For other relationships, we use findEntityByCategoryId to get the dynamic entity
        let updatePayload = createEntityUpdatePayload(context.type, context.entity);
        picks.forEach(chosenTemplate => {

            var updatedTemplate = structuredClone(chosenTemplate);
            //create the placeholder NS - 
            const nonStandard: FAM_INonStandard = createNonStandard(
                xState.data,
                {
                    section: payload.section,
                    ...updatePayload

                    //templated: true
                }
            );

            const updatedNonStandard: FAM_INonStandard = structuredClone(nonStandard);
            updatedNonStandard.partOfTemplate = true;

            const fieldMap = ArrayToLookupByPredicate(chosenTemplate.fields, q => q.configRef.id);


            //let nonStandardEffectiveDate = updatedNonStandard.fields.find(field => field.configRef.id === "0973f3f2-d38d-ec11-b400-0022480a9a56");
            let nonStandardBenefitDefintion = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_benefitdefinition_id);
            let nonStandardPayAccordingToBenefit = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_doesthisbenefitpayaccoridngtotherelatedbe_id);

            //let templateEffectiveDate = fieldMap["cb9f16c4-710c-ef11-9f89-6045bdeb64f8"];
            let templateDefintion = fieldMap[$att_$non_standard_template_$bcbs_nst_benefitdefinition_id];

            // const effectiveDateValue = templateEffectiveDate.values[0].value; //template value
            // nonStandardEffectiveDate!.values[0].value = structuredClone(effectiveDateValue); //ns value
            if (nonStandardBenefitDefintion && templateDefintion) {
                const benefitDefinitionValue = templateDefintion.values[0].value; //template value
                nonStandardBenefitDefintion.values[0].value = structuredClone(benefitDefinitionValue); //ns value
            }

            if (nonStandardPayAccordingToBenefit) {
                let templatePayAccordingToBenefit = fieldMap[$att_$non_standard_template_$bcbs_nst_doesthisbenefitpayaccoridngtotherelatedbe_id];
                if (templatePayAccordingToBenefit) {
                    const payAccordingToBenefitValue = templatePayAccordingToBenefit.values[0].value; //template value
                    nonStandardPayAccordingToBenefit.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value
                }
            }
            updatedNonStandard.name = chosenTemplate.name;

            // add non standard to all categories and section template is a part of. 
            let newRelationshipList = updatedTemplate.relationships!.map(rel => ({
                ...rel,
                sectionRefs: (rel.sectionRefs!.map(secRef => ({
                    ...secRef,
                    nonStandards: [...(secRef.nonStandards || [])] // Clone the nonStandards array
                })) || [])
            }));

            //find the section we need to add the NS to
            newRelationshipList.forEach(relationship => {
                if (relationship.categoryID === context.entityId) {
                    relationship.sectionRefs?.forEach(relationshipSection => {
                        if (relationshipSection.sectionId === nonStandard.sectionRef?.id) {
                            //we are in the correct section to add to it, so add the nonStandard ref
                            // Section exists, add the non-standard to it
                            relationshipSection.nonStandards = [...relationshipSection.nonStandards, { id: updatedNonStandard.id, name: updatedNonStandard.name }];


                            const nonStandardPayload: INonStandardPayload =
                            {
                                config: xState.data.config,
                                nonStandards: [updatedNonStandard],
                                ...updatePayload,
                                changeTracker: {}
                            }; //THIS IS KEY MANNN
                            nonStandardPayloads.push(nonStandardPayload);
                            //------------------------------------------------------
                            // //create the nonstandard
                            // dispatch(addUpdateNonStandards([{
                            //     config: xState.data.config,
                            //     nonStandards: [updatedNonStandard],
                            //     benefit: payload.benefit,
                            //     groupConfiguration: payload.groupConfiguration,
                            //     changeTracker: {}
                            // }]));
                        }
                        else {
                            const newSectionRef = {
                                sectionId: updatedNonStandard.sectionRef?.id || '',
                                nonStandards: [{ id: updatedNonStandard.id, name: updatedNonStandard.name }]
                            };
                            relationship.sectionRefs = [...(relationship.sectionRefs || []), newSectionRef];

                            const nonStandardPayload: INonStandardPayload =
                            {
                                config: xState.data.config,
                                nonStandards: [updatedNonStandard],
                                ...updatePayload,
                                changeTracker: {}
                            }; //THIS IS KEY MANNN
                            nonStandardPayloads.push(nonStandardPayload);
                            //------------------------------------------------------
                            // //create the nonstandard
                            // dispatch(addUpdateNonStandards([{
                            //     config: xState.data.config,
                            //     nonStandards: [updatedNonStandard],
                            //     benefit: payload.benefit,
                            //     groupConfiguration: payload.groupConfiguration,
                            //     changeTracker: {}
                            // }]));
                        }
                    })
                }
                else {
                    //creating new Nonstandard in different benefit/entity
                    // Dynamically find the entity based on relationship.categoryID
                    // This supports benefits, AR, F&P, and group configuration
                    var entityLookup = findEntityByCategoryId(xState.data.json, relationship.categoryID);
                    if (!entityLookup) {
                        console.log(`Could not find entity for categoryID: ${relationship.categoryID}`);
                        return;
                    }
                    const dynamicUpdatePayload = createEntityUpdatePayload(entityLookup.entityType, entityLookup.entity);

                    var configSection = xState.data.config.cache.map_bcbs_config_section[updatedNonStandard.sectionRef!.id];
                    const base: FAM_INonStandard = createNonStandard(
                        xState.data,
                        {
                            section: configSection,
                            ...dynamicUpdatePayload,
                            //templated: true
                        }
                    );

                    const newNonStandard: FAM_INonStandard = structuredClone(base);
                    newNonStandard.partOfTemplate = true;

                    const fieldMap = ArrayToLookupByPredicate(chosenTemplate.fields, q => q.configRef.id);

                    //let nonStandardEffectiveDate = newNonStandard.fields.find(field => field.configRef.id === "0973f3f2-d38d-ec11-b400-0022480a9a56");
                    let nonStandardBenefitDefintion = newNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_benefitdefinition_id);
                    let nonStandardPayAccordingToBenefit = newNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_doesthisbenefitpayaccoridngtotherelatedbe_id);

                    //let templateEffectiveDate = fieldMap["cb9f16c4-710c-ef11-9f89-6045bdeb64f8"];
                    let templateDefintion = fieldMap[$att_$non_standard_template_$bcbs_nst_benefitdefinition_id];

                    // const effectiveDateValue = templateEffectiveDate.values[0].value; //template value
                    // nonStandardEffectiveDate!.values[0].value = structuredClone(effectiveDateValue); //ns value
                    if (nonStandardBenefitDefintion && templateDefintion) {
                        const benefitDefinitionValue = templateDefintion.values[0].value; //template value
                        nonStandardBenefitDefintion.values[0].value = structuredClone(benefitDefinitionValue); //ns value
                    }

                    if (nonStandardPayAccordingToBenefit) {
                        let templatePayAccordingToBenefit = fieldMap[$att_$non_standard_template_$bcbs_nst_doesthisbenefitpayaccoridngtotherelatedbe_id];
                        if (templatePayAccordingToBenefit) {
                            const payAccordingToBenefitValue = templatePayAccordingToBenefit.values[0].value; //template value
                            nonStandardPayAccordingToBenefit.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value
                        }
                    }
                    newNonStandard.name = chosenTemplate.name;

                    // Find or create the section within the category
                    let section = relationship.sectionRefs.find(sec => sec.sectionId === newNonStandard.sectionRef?.id);

                    if (section) {
                        //section exists on template, add this nonStandard to it
                        section.nonStandards = [...section.nonStandards, { id: newNonStandard.id, name: newNonStandard.name }];
                    } else {
                        // Section doesn't exist, create it and add the non-standard
                        const newSectionRef = {
                            sectionId: newNonStandard.sectionRef?.id || '',
                            nonStandards: [{ id: newNonStandard.id, name: newNonStandard.name }]
                        };
                        relationship.sectionRefs = [...(relationship.sectionRefs || []), newSectionRef];
                    }


                    //const category = contextJson.find(q => q.id === relationship.categoryID);

                    //updatedNonStandards.push(newNonStandard);

                    const nonStandardPayload: INonStandardPayload =
                    {
                        config: xState.data.config,
                        nonStandards: [newNonStandard],
                        ...dynamicUpdatePayload,
                        changeTracker: {}
                    }; //THIS IS KEY MANNN

                    nonStandardPayloads.push(nonStandardPayload);
                    //------------------------------------------------------------------------
                    // //create the nonstandard
                    // dispatch(addUpdateNonStandards([{
                    //     config: xState.data.config,
                    //     nonStandards: [newNonStandard],
                    //     benefit: benefit,
                    //     groupConfiguration: payload.groupConfiguration,
                    //     changeTracker: {}
                    // }]));
                }
            });

            updatedTemplate = {
                ...updatedTemplate,
                relationships: newRelationshipList
            };
            const newTemplatePayload: INonStandardTemplatePayload = {
                nonStandardTemplates: [updatedTemplate]
            };
            nonStandardTemplatePayloads.push(newTemplatePayload);
            //-----------------------------------------------------------
        });

        dispatch(addUpdateNonStandards(nonStandardPayloads));

        dispatch(
            addUpdateNonStandardTemplates(nonStandardTemplatePayloads)
        );
    }
}

export const moveTemplatedNonStandardsToNewSectionAction = (
    picks: FAM_INonStandardTemplate[],
    context: IEntityContext | null,
    payload: {
        section: IXrmTypes_bcbs_config_section,
        benefit: FAM_IBenefit | undefined,
        groupConfiguration: FAM_IGroupConfigurationData | undefined,
        accountsReceivable?: FAM_IAccountsReceivable
        feesAndPremiums?: FAM_IFeesAndPremiums
        stopLoss?: FAM_IStopLoss
    }) => {
    return async function (dispatch: TDispatch, state: () => RootState) {
        var xState = state();
        let nonStandardPayloads: INonStandardPayload[] = [];
        let nonStandardTemplatePayloads: INonStandardTemplatePayload[] = []
        if (!context) {
            console.log("No context provided for moveTemplatedNonStandardsToNewSectionAction");
            return;
        }
        // Note: We don't use contextJson or updatePayload here because we dynamically 
        // find the entity for each relationship using findEntityByCategoryId
        picks.forEach(chosenTemplate => {
            //we will only show templates with one section per relationship
            var updatedTemplate = structuredClone(chosenTemplate);
            // add non standard to all categories and section template is a part of. 
            let newRelationshipList = updatedTemplate.relationships!.map(rel => ({
                ...rel,
                sectionRefs: (rel.sectionRefs!.map(secRef => ({
                    ...secRef,
                    nonStandards: [...(secRef.nonStandards || [])] // Clone the nonStandards array
                })) || [])
            }));

            //for each relationship update the section ref id and the nonstandards(in json not the template) ref id with incoming section id
            newRelationshipList.forEach(relationship => {
                relationship.sectionRefs.forEach(sectionRef => {
                    sectionRef.sectionId = payload.section.bcbs_config_sectionid ?? "";
                    sectionRef.nonStandards.forEach(nsInSection => {
                        // Dynamically find the entity based on relationship.categoryID
                        // This supports benefits, AR, F&P, and group configuration
                        var entityLookup = findEntityByCategoryId(xState.data.json, relationship.categoryID);
                        if (entityLookup) {
                            var nonStandardInTemplate = findNonStandardInEntity(entityLookup, nsInSection.id);
                            var nsClone = structuredClone(nonStandardInTemplate);
                            nsClone!.sectionRef = { id: payload.section.bcbs_config_sectionid!, name: payload.section.bcbs_name! }
                            // Create the update payload dynamically based on the entity type we found
                            const dynamicUpdatePayload = createEntityUpdatePayload(entityLookup.entityType, entityLookup.entity);
                            const nonStandardPayload: INonStandardPayload =
                            {
                                config: xState.data.config,
                                nonStandards: [nsClone!],
                                ...dynamicUpdatePayload,
                                changeTracker: {}
                            };
                            nonStandardPayloads.push(nonStandardPayload);
                        }
                    })
                })
            });

            updatedTemplate = {
                ...updatedTemplate,
                relationships: newRelationshipList
            };
            const newTemplatePayload: INonStandardTemplatePayload = {
                nonStandardTemplates: [updatedTemplate]
            };
            nonStandardTemplatePayloads.push(newTemplatePayload);
            //-----------------------------------------------------------
        });

        dispatch(addUpdateNonStandards(nonStandardPayloads));

        dispatch(
            addUpdateNonStandardTemplates(nonStandardTemplatePayloads)
        );
    }
}

export const TemplateToBenefitOrGCAction = (
    picks: FAM_INonStandardTemplate[],
    context: IEntityContext | null,
) => {
    return async function (dispatch: TDispatch, state: () => RootState) {
        var xState = state();
        let nonStandardPayloads: INonStandardPayload[] = [];
        let nonStandardTemplatePayloads: INonStandardTemplatePayload[] = [];
        let nonStandardTemplates: FAM_INonStandardTemplate[] = [];
        const categoryNonStandardTemplate = xState.data.config.cache.map_bcbs_config_category_named['Non-Standard Template'];
        if (!context) {
            console.log("No context provided for TemplateToBenefitOrGCAction");
            return;
        }
        let contextJson = getEntityCollection(xState.data.json, context.type)
        let updatePayload = createEntityUpdatePayload(context.type, context.entity);
        picks.forEach(chosenTemplate => {

            let newRelationshipList = chosenTemplate.relationships!.map(rel => ({
                ...rel,
                sectionRefs: (rel.sectionRefs!.map(secRef => ({
                    ...secRef,
                    nonStandards: [...(secRef.nonStandards || [])] // Clone the nonStandards array
                })) || [])
            }));
            const fieldMap = ArrayToLookupByPredicate(chosenTemplate.fields, q => q.configRef.id);
            const linkedConfigCategory = xState.data.config.cache.map_bcbs_config_category[chosenTemplate.linkedCategoryTypeId!];
            // lets create a new nonstandard template as
            let template: FAM_INonStandardTemplate = createNonStandardTemplate(
                xState.data,
                {
                    id: "",
                    name: `Non-StandardTemplate - New - ${timestamp()}`,
                    category: categoryNonStandardTemplate,
                    linkedCategory: linkedConfigCategory,
                    linkedCategoryName: chosenTemplate.linkedCategoryName!,
                    relationships: chosenTemplate.relationships ?? []
                }
            );

            let tierChanges: ITierLevelChange[] = [];

            // transfer matching properties 
            template.name = chosenTemplate.name;
            template.id = chosenTemplate.id;

            //we need to create all the sections 
            //(from first relationship-- all category and sections should be the same except for NS)
            //and place new nonstandards in them in the new relationship
            let sourceRelationshipSections = chosenTemplate.relationships![0].sectionRefs!.map(secRef => ({ ...secRef }));
            let newRelationshipSections: FAM_ITemplateSectionRelationship[] = [];
            let configCategory = xState.data.config.cache.map_bcbs_config_category[context.categoryConfigId!];

            let categoryToCheck: FAM_ITemplateRelationship = {
                categoryID: context.entityId ?? '',
                categoryType: configCategory?.bcbs_name ?? '',
                sectionRefs: sourceRelationshipSections.map(section => ({
                    ...section,
                    nonStandards: section.nonStandards ?? [] // Ensure nonStandards is always an array
                }))
            }

            //for each section,
            sourceRelationshipSections.forEach(section => {
                let newNonStandardReferences: FAM_IRef[] = [];
                section.nonStandards?.forEach(nonStandardRef => {
                    //find matching non standard in new category
                    let benefit = contextJson.find(q => q.id === chosenTemplate.relationships![0].categoryID);
                    //get each nonstandard from the template
                    let sourceCategoryNonStandard = benefit ? (benefit.nonStandards ?? []).find(q => q.id === nonStandardRef.id) : xState.data.json.nonStandards.find(q => q.id === nonStandardRef.id);
                    if (sourceCategoryNonStandard) {
                        //find matching ones for each ref in the original category
                        let matchingNonStandardIds = matchingNonStandard(xState, sourceCategoryNonStandard, template, categoryToCheck, contextJson);
                        if (matchingNonStandardIds && matchingNonStandardIds.length > 0) {
                            matchingNonStandardIds.forEach(id => {
                                //new category
                                let newCategory = contextJson.find(q => q.id === categoryToCheck.categoryID);
                                //get the nonstandard from the category
                                let categoryNonStandard = newCategory ? (newCategory.nonStandards ?? []).find(q => q.id === id) : xState.data.json.nonStandards.find(q => q.id === id);
                                if (categoryNonStandard) {
                                    //update the matched nonstandards for this category
                                    const updatedNonStandard: FAM_INonStandard = structuredClone(categoryNonStandard);
                                    updatedNonStandard.partOfTemplate = true;
                                    //if the names are not the same, update them
                                    updatedNonStandard.name = chosenTemplate.name;
                                    //make the activation status same as template
                                    updatedNonStandard.deactivated = sourceCategoryNonStandard!.deactivated


                                    //let nonStandardEffectiveDate = updatedNonStandard.fields.find(field => field.configRef.id === "0973f3f2-d38d-ec11-b400-0022480a9a56");
                                    let nonStandardBenefitDefintion = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_benefitdefinition_id);
                                    let nonStandardPayAccordingToBenefit = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_doesthisbenefitpayaccoridngtotherelatedbe_id);

                                    //let templateEffectiveDate = fieldMap["cb9f16c4-710c-ef11-9f89-6045bdeb64f8"];
                                    let templateDefintion = fieldMap[$att_$non_standard_template_$bcbs_nst_benefitdefinition_id];

                                    //const effectiveDateValue = templateEffectiveDate.values[0].value; //template value
                                    //nonStandardEffectiveDate!.values[0].value = structuredClone(effectiveDateValue); //ns value
                                    const benefitDefinitionValue = templateDefintion.values[0].value; //template value
                                    nonStandardBenefitDefintion!.values[0].value = structuredClone(benefitDefinitionValue); //ns value

                                    if (nonStandardPayAccordingToBenefit) {
                                        let templatePayAccordingToBenefit = fieldMap[$att_$non_standard_template_$bcbs_nst_doesthisbenefitpayaccoridngtotherelatedbe_id];
                                        const payAccordingToBenefitValue = templatePayAccordingToBenefit.values[0].value; //template value
                                        nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value
                                    }

                                    newNonStandardReferences.push({ id: updatedNonStandard.id, name: updatedNonStandard.name })

                                    const nonStandardPayload: INonStandardPayload =
                                    {
                                        config: xState.data.config,
                                        nonStandards: [updatedNonStandard],
                                        ...updatePayload,
                                        changeTracker: {}
                                    };

                                    nonStandardPayloads.push(nonStandardPayload);

                                    // dispatch(addUpdateNonStandards([{
                                    //     config: xState.data.config,
                                    //     nonStandards: [updatedNonStandard],
                                    //     benefit: newCategory,
                                    //     //groupConfiguration: props.groupConfiguration,
                                    //     changeTracker: {}
                                    // }]));
                                }
                            })

                        } else {
                            let newNonStandardSection = xState.data.config.cache.map_bcbs_config_section[section.sectionId];
                            const newNonStandard: FAM_INonStandard = createNonStandard(
                                xState.data,
                                {
                                    section: newNonStandardSection!,
                                    ...updatePayload,
                                    templated: true,
                                    name: nonStandardRef.name
                                }
                            );

                            const fieldMap = ArrayToLookupByPredicate(chosenTemplate.fields, q => q.configRef.id);
                            //this newly created one needs to have the fields from tempalte - for some reason these ones arent caught in the wide update
                            //let nonStandardEffectiveDate = newNonStandard.fields.find(field => field.configRef.id === "0973f3f2-d38d-ec11-b400-0022480a9a56");
                            let nonStandardBenefitDefintion = newNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_benefitdefinition_id);
                            let nonStandardPayAccordingToBenefit = newNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_doesthisbenefitpayaccoridngtotherelatedbe_id);
                            newNonStandard.name = chosenTemplate.name
                            newNonStandard.deactivated = sourceCategoryNonStandard!.deactivated
                            let templateEffectiveDate = fieldMap[$att_$non_standard_template_$bcbs_nst_effectivedate_id];
                            let templateDefintion = fieldMap[$att_$non_standard_template_$bcbs_nst_benefitdefinition_id];

                            //const effectiveDateValue = templateEffectiveDate!.values[0].value; //template value
                            //nonStandardEffectiveDate!.values[0].value = structuredClone(effectiveDateValue); //ns value
                            const benefitDefinitionValue = templateDefintion!.values[0].value; //template value
                            nonStandardBenefitDefintion!.values[0].value = structuredClone(benefitDefinitionValue); //ns value

                            if (nonStandardPayAccordingToBenefit) {
                                let templatePayAccordingToBenefit = fieldMap[$att_$non_standard_template_$bcbs_nst_doesthisbenefitpayaccoridngtotherelatedbe_id];
                                const payAccordingToBenefitValue = templatePayAccordingToBenefit.values[0].value; //template value
                                nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value
                            }

                            newNonStandardReferences.push({ id: newNonStandard.id, name: newNonStandard.name })

                            const nonStandardPayload: INonStandardPayload =
                            {
                                config: xState.data.config,
                                nonStandards: [newNonStandard],
                                ...updatePayload,
                                changeTracker: {}
                            }; //THIS IS KEY MANNN

                            nonStandardPayloads.push(nonStandardPayload);

                            // dispatch(addUpdateNonStandards([{
                            //     config: xState.data.config,
                            //     nonStandards: [newNonStandard],
                            //     benefit: chosenBenefit ?? undefined,
                            //     groupConfiguration: groupConfig ?? undefined,
                            //     changeTracker: {}
                            // }]));
                        }
                    }
                })

                //create new section in relationship,
                let newSection: FAM_ITemplateSectionRelationship =
                {
                    sectionId: section.sectionId,
                    nonStandards: newNonStandardReferences
                }

                newRelationshipSections = [...newRelationshipSections, newSection];
            });

            const newCategory = {
                categoryID: context.entityId ?? '',
                categoryType: configCategory?.bcbs_name ?? '',
                sectionRefs: newRelationshipSections.map(section => ({
                    ...section,
                    nonStandards: section.nonStandards ?? [] // Ensure nonstandards is always an array
                }))
            };
            newRelationshipList = [...newRelationshipList, newCategory];


            template.fields.forEach(templateField => {

                const field = fieldMap[templateField.configRef.id];

                // did we find a matching field
                if (field) {
                    if (field.values.length === 0) {
                        // nop
                    }
                    else if (field.values.length === 1 && templateField.values.length === 1) {

                        const value = field.values[0].value;
                        templateField.values[0].value = structuredClone(value);

                    } else if (field.values.length > 1) {
                        let predicate = (oldValue: FAM_IFlexibleFieldValue, newValue: FAM_IFlexibleFieldValue) => {
                            return oldValue.type === newValue.type;
                        }
                        if (tierChanges.length > 0) {
                            predicate = (oldValue: FAM_IFlexibleFieldValue, newValue: FAM_IFlexibleFieldValue) => {
                                return !!tierChanges.find(q => q.oldTier?.id === oldValue.tierRef?.id && q.newTier?.id === newValue.tierRef?.id)
                            };
                        }

                        templateField.values.forEach(t => {

                            const value = field.values.find(q => predicate(q, t));

                            if (value?.value) {
                                t.value = structuredClone(value.value);
                            }
                        });
                    }
                }
            });

            const updatedTemplate = {
                ...template,
                relationships: newRelationshipList
            };
            const newTemplatePayload: INonStandardTemplatePayload = {
                nonStandardTemplates: [updatedTemplate]
            };
            nonStandardTemplatePayloads.push(newTemplatePayload);
            nonStandardTemplates.push(updatedTemplate);
        });

        if (nonStandardTemplates && nonStandardTemplates.length > 0) {
            dispatch(addUpdateNonStandards(nonStandardPayloads));
            dispatch(
                addUpdateNonStandardTemplates(nonStandardTemplatePayloads)
            );
            nonStandardTemplates.forEach(template => { updateAllTemplatedNonStandardsFields(template, context); });
        }
    }
}

const updateAllTemplatedNonStandardsFields = (incomingTemplate: FAM_INonStandardTemplate, context: IEntityContext) => {
    return async function (dispatch: TDispatch, state: () => RootState) {
        const xState = state();
        //read template fields
        //clone the template
        const template = structuredClone(incomingTemplate);
        const contextJson = getEntityCollection(xState.data.json, context.type);
        const updatePayload = createEntityUpdatePayload(context.type, context.entity);
        const templateFields = ArrayToLookupByPredicateWithMap(template!.fields, t => t.configRef.id, t => {
            const attribute = xState.data.config.cache.map_bcbs_config_attribute[t.configRef.id];
            const masterAttribute = xState.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];
            const controlType = masterAttribute.bcbs_attributetypecode as EControlType;
            return {
                field: t,
                attribute,

                masterAttribute,
                controlType,
            }
        });
        const templateEffectiveDate = templateFields[$att_$non_standard_template_$bcbs_nst_effectivedate_id];
        const templateDefinition = templateFields[$att_$non_standard_template_$bcbs_nst_benefitdefinition_id];
        const templatePayAccordingToBenefit = templateFields[$att_$non_standard_template_$bcbs_nst_doesthisbenefitpayaccoridngtotherelatedbe_id];
        let templateEffectiveDateValue = "";
        let templateDefinitionValue = "";
        let templatePayAccordingToBenefitValue = "";

        if (templateEffectiveDate.field.values[0].value !== null) {
            templateEffectiveDateValue = formatDate(parseDate(friendlyValue(templateEffectiveDate.controlType, templateEffectiveDate.field.values[0].value), 'local:yyyy-MM-dd'), 'en-US-ET');
        }
        if (templateDefinition.field.values[0].value !== null) {
            templateDefinitionValue = friendlyValue(templateDefinition.controlType, templateDefinition.field.values[0].value);
        }
        if (templatePayAccordingToBenefit.field.values[0].value !== null) {
            templatePayAccordingToBenefitValue = friendlyValue(templatePayAccordingToBenefit.controlType, templatePayAccordingToBenefit.field.values[0].value);
        }

        //go through each connected NS and update the fields on that.
        template?.relationships?.forEach(rel => {
            let categoryId = rel.categoryID;
            rel.sectionRefs?.forEach(sectionRef => {
                sectionRef.nonStandards?.forEach(nonStandardRef => {
                    //find the benefits for the category id
                    let benefit = contextJson.find(ben => ben.id === categoryId);
                    if (benefit) {
                        //do a find on the non standard
                        let benefitNonStandard = (benefit.nonStandards ?? []).find(ns => ns.id === nonStandardRef.id);

                        //if we find the non standard, update the fields
                        if (benefitNonStandard && benefitNonStandard.deactivated !== true) {
                            //update fields
                            const updatedNonStandard: FAM_INonStandard = structuredClone(benefitNonStandard);
                            updatedNonStandard.partOfTemplate = true;

                            let nonStandardEffectiveDate = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_effectivedate_id);
                            let nonStandardBenefitDefintion = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_benefitdefinition_id);
                            let nonStandardPayAccordingToBenefit = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_doesthisbenefitpayaccoridngtotherelatedbe_id);

                            const effectiveDateValue = templateEffectiveDate.field.values[0].value; //template value
                            nonStandardEffectiveDate!.values[0].value = structuredClone(effectiveDateValue); //ns value
                            const benefitDefinitionValue = templateDefinition.field.values[0].value; //template value
                            nonStandardBenefitDefintion!.values[0].value = structuredClone(benefitDefinitionValue); //ns value
                            //const payAccordingToBenefitValue = templatePayAccordingToBenefit.field.values[0].value; //template value
                            //nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value

                            if (nonStandardPayAccordingToBenefit) {
                                const payAccordingToBenefitValue = templatePayAccordingToBenefit.field.values[0].value; //template value
                                nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value
                            }

                            dispatch(addUpdateNonStandards([{
                                config: xState.data.config,
                                nonStandards: [updatedNonStandard],
                                ...updatePayload,
                                changeTracker: {}
                            }]));
                        }
                    } else if (xState.data.json.id === categoryId) {
                        //if no benefits look at the GC
                        let gcNonStandard = xState.data.json.nonStandards.find(ns => ns.id === nonStandardRef.id);
                        if (gcNonStandard && gcNonStandard.deactivated !== true) {
                            //update fields
                            const updatedNonStandard: FAM_INonStandard = structuredClone(gcNonStandard);
                            updatedNonStandard.partOfTemplate = true;

                            let nonStandardEffectiveDate = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_effectivedate_id);
                            let nonStandardBenefitDefintion = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_benefitdefinition_id);
                            let nonStandardPayAccordingToBenefit = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_doesthisbenefitpayaccoridngtotherelatedbe_id);

                            const effectiveDateValue = templateEffectiveDate.field.values[0].value; //template value
                            nonStandardEffectiveDate!.values[0].value = structuredClone(effectiveDateValue); //ns value
                            const benefitDefinitionValue = templateDefinition.field.values[0].value; //template value
                            nonStandardBenefitDefintion!.values[0].value = structuredClone(benefitDefinitionValue); //ns value
                            //const payAccordingToBenefitValue = templatePayAccordingToBenefit.field.values[0].value; //template value
                            //nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value

                            if (nonStandardPayAccordingToBenefit) {
                                const payAccordingToBenefitValue = templatePayAccordingToBenefit.field.values[0].value; //template value
                                nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value
                            }

                            dispatch(addUpdateNonStandards([{
                                config: xState.data.config,
                                nonStandards: [updatedNonStandard],
                                ...updatePayload,
                                changeTracker: {}
                            }]));
                        }
                    }
                })
            })
        })
    }
}


export const LoadSnapshotWithVersionAction = (dataSource: AppDataSource, history: any, versionId: string) => {
    return async function (dispatch: Dispatch<any>, state: () => RootState) {
        const xState = state();

        dispatch(setViewType(EViewType.ViewOnly));
        dispatch(updateDebug({ autoSaveOff: true }));
        dispatch(loadSnapshotAction(dataSource,
            xState.data.account?.accountid!,
            {
                primarySnapshot: xState.data.snapshot,
                primarySnapshotVersionId: versionId,
                merge: false,
            })
        );
        history.goBack();
    }
}

//export const loadComparisonSnapshots = (ds: AppDataSource, id: string, snapshot: any, side: string) => {
//    return function (dispatch: any) {
//        (async () => {

//            if (side === "left")
//                dispatch(loadSnapshot(ds, id, {
//                    leftSnapshot: snapshot,
//                    leftSnapshotId: snapshot
//                }));

//            if (side === "right")
//                dispatch(loadSnapshot(ds, id, {
//                    rightSnapshot: snapshot,
//                    rightSnapshotId: snapshot
//                }));
//        })()
//    }
//}

export const autoGenerateFeesAndPremiumsFeesAction = () => {
    return function (dispatch: Dispatch<any>, state: () => RootState) {
        //TODO: MIKE
        // const newFeesAndPremiums = [...data.feesAndPremiums];

        const cState = state();
        const newFeeAndPremiums = structuredClone(cState.data.json.feesAndPremiums);

        const data = cState.data.json;
        const config = cState.data.config;

        if (cState.status.debug)
            debugger;

        //***TODO*** I don't see where a Fee and Premium/Admin Fee is related to a Plan 
        //Need to be able to set the Agent Number on the GC, the Med Short Name on the Medical and the Medical Benefit Type on the Medical
        //That will let BRK, CPF, HC1 and PCM Generate

        //Grab all BCC Admins where the Master Attribute has a value (these are automatically created ones) AND
        //the Funding Types (of the Plan) match
        //configX.bcbs_bccadmin.filter(q=>q.bcbs_attributevalue)
        //configX.masterattributes.filter(q => q.bcbs_config_masterattributeid)
        //let x = configX.masterattributes.find(q => q.bcbs_name === "Medical Benefit Type");
        //x.value
        //const att = data.benefits[0].fields.find(q => q.configRefId === '8888cb95-ce4b-ec11-8f8e-000d3a3349fe')!;
        const snapshotFundingType = data.fundingType?.name;
        let finalFundingType = '';
        if (!snapshotFundingType)
            finalFundingType = IXrmGlobalOptions_bcbs_fundingtypes.AdministrativeServicesOnly.value.toString();
        if (snapshotFundingType === 'Administrative Services Only')
            finalFundingType = IXrmGlobalOptions_bcbs_fundingtypes.AdministrativeServicesOnly.value.toString();
        if (snapshotFundingType === 'ASO with Stop Loss')
            finalFundingType = IXrmGlobalOptions_bcbs_fundingtypes.ASOwithStopLoss.value.toString();
        if (snapshotFundingType === 'Fully Insured')
            finalFundingType = IXrmGlobalOptions_bcbs_fundingtypes.FullyInsured.value.toString();
        if (snapshotFundingType === 'National Alliance')
            finalFundingType = IXrmGlobalOptions_bcbs_fundingtypes.NationalAlliance.value.toString();
        if (snapshotFundingType === 'Level Funded')
            finalFundingType = IXrmGlobalOptions_bcbs_fundingtypes.LevelFunded.value.toString();
        if (snapshotFundingType === 'Mixed')
            finalFundingType = IXrmGlobalOptions_bcbs_fundingtypes.AdministrativeServicesOnly.value.toString();

        const BCCAdminsFull = config.bcbs_bccadmin.filter(q => q._bcbs_build_masterattributeid_value);
        //const BCCAdminsFinal = BCCAdminsFull.filter(q => q.bcbs_fundingtypescode?.includes(bcbs_fundingtypes.AdministrativeServicesOnly.value.toString()));//Hardcoded ASO - needs to pull funding type of Plan 
        const BCCAdminsFinal = BCCAdminsFull.filter(q => q.bcbs_fundingtypescode?.includes(finalFundingType));//Hardcoded ASO - needs to pull funding type of Plan 

        //For each Fee and Premium, grab the Attributes associated with the first Plan associated to the Fee and Premium where
        //the Master Attribute is in the list of auto generated ones
        //*I really want to start from the Fees and Premiums to see what Plan(s) are associated can I do that? *
        //const newPlans = data.plans.map(plan => {
        //return {
        //...plan,
        //FeeAndPremiums: data.feesAndPremiums.find(f => f.id === plan.FeeAndPremiumsRef),
        //benefits: data.benefits.filter(t => plan.benefitRefs.some(q => t.id === q.id))
        //}
        //});
        // newPlans[0].benefits[1].bcbs_name //category name

        //const feeAndPremiumsWihFirstPlan = data.feesAndPremiums.map(fnp => {
        //return { ...fnp, firstMatchingPlan: data.plans.find(p => p.FeeAndPremiumsRef = fnp.id) }
        //})


        // data.plans.find(plan => plan.FeeAndPremiumsRef === )
        //data.plans.forEach(plan => {

        //})

        //Compare the requred value on the BCC Admin to the Attributes value... if they match OR if the BCC Admin is null create a Fee array to store
        //These as potential fees to create
        //I need a way to see the type (Category) of Benefits is the only way through CategoryID's?
        //const testPlan1BenefitID = data.plans[1].benefitRefs[2].id;
        //const Plan1MedicalBenefit = benefits.filter(q => q.id === testPlan1BenefitID);
        //debugger;

        //const BCCADminIDs = [];

        //Plan1MedicalBenefit[0].fields.forEach(planField => {
        //BCCAdminsFinal.forEach(BCCAdmin => {
        //if (BCCAdmin._bcbs_build_masterattributeid_value === planField.masterAttributeID) {

        //const primaryValue = planField.values[0].value as FAM.IPrimaryAttributeValue
        //if (BCCAdmin.bcbs_attributevalue === null) {
        //BCCADminIDs.push(BCCAdmin);
        //}
        //else if (BCCAdmin.bcbs_attributevalue?.includes(primaryValue.primary))//Why can't I access Primary? Why does it seem the Primary value holds an option set id (how do I get the Option Set value?)
        //{
        //BCCADminIDs.push(BCCAdmin);
        //}
        //}

        //})

        //})


        //Finally check to see if a fee with that BCC Admin already exists for the Fee and Premium... if it does we don't need to create
        //it again
        //newFeesAndPremiums.find

        //How do I simply hardcode a fee and create it? I'm assuming I have to build a Fee, set its values and then save it?
        // const feeAndPremiumCategoryId = '7c8c262c-d778-ec11-8d21-0022480b0e40'
        // const feeNPTabId = 'f5625f99-d778-ec11-8d21-000d3a35957e'

        //This is the Attribute GUID of the Attribute whose name is Fee
        // see (https://bcbsonlinedevgl.crm.dynamics.com/main.aspx?appid=975825e9-352c-ec11-b6e5-000d3a5b93e5&forceUCI=1&newWindow=true&pagetype=entityrecord&etn=bcbs_config_attribute&id=50d7c5a7-d590-ec11-b400-0022480a93ed&formid=3b6991fc-8a92-40f3-a9d4-1c178728cc9d)
        const feeAttributesetAttributeId = $matt_$fees_and_premiums_$bcbs_attributeset_fee_id;

        //const feeAttributesetAttribute = cState.data.config.cache.map_bcbs_config_attribute[feeAttributesetAttributeId];
        const feeattributesetMasterAttribute = cState.data.config.cache.map_bcbs_config_masterattribute[feeAttributesetAttributeId];

        newFeeAndPremiums.forEach(eachFeeAndPremium => {

            const firstPlan = data.plans.find(plan => plan.feesAndPremiumsRef && plan.feesAndPremiumsRef.id === eachFeeAndPremium.id);
            const firstPlanBenefits = data.benefits.filter(b => firstPlan?.benefitRefs.some(q => q.id === b.id));
            const medBenefit = firstPlanBenefits.filter(q => q.bcbs_name?.includes("Medical"));
            // const rxBenefit = firstPlanBenefits.filter(q => q.bcbs_name?.includes("Pharmacy"));
            const ancBenefit = firstPlanBenefits.filter(q => q.bcbs_name?.includes("Ancillary"));
            const visBenefit = firstPlanBenefits.filter(q => q.bcbs_name?.includes("Vision"));
            const denBenefit = firstPlanBenefits.filter(q => q.bcbs_name?.includes("Dental"));
            //Need to ensure the Fee and Premium doesn't have a Fee of this BCC Admin before creating
            //const currentFees = eachFeeAndPremium.fees
            const bccAdmins = BCCAdminsFinal.filter(eachBccAdmin =>
                firstPlanBenefits.some(eachBenefit => {
                    return eachBenefit.fields.some(eachField => {
                        //console.log("CompareLog", eachBccAdmin, eachField);
                        const fees = eachFeeAndPremium.attributeSets
                            .filter(t1 => t1.attributeSetRef.id === feeAttributesetAttributeId) //pick all fees
                        const feeBCCAdmins = fees.map(t2 => { return t2.fields.filter(w => w.name === "BCC Admin") }).flat(1)
                        const alreadyExists = feeBCCAdmins.some(q => q.values[0]?.value?.type === "entity" && q.values[0]?.value?.name === eachBccAdmin.bcbs_name)

                        if (eachBccAdmin.bcbs_name === "BRK | AGENT FEE (Commission) | Medical" && medBenefit.length > 0 && !alreadyExists)
                            return true;
                        else if (eachBccAdmin.bcbs_name === "HC1 | HCR Reinsurance Fee | Medical" && medBenefit.length > 0 && !alreadyExists)
                            return true;
                        else if (eachBccAdmin.bcbs_name === "PPF | Partner Plan Fee | Medical" && medBenefit.length > 0 && !alreadyExists)
                            return true;
                        else if (eachBccAdmin.bcbs_name === "CMM | COMPREHENSIVE | Medical" && medBenefit.length > 0 && !alreadyExists) {
                            if (medBenefit[0]?.tierSetRef?.name === "Comprehenseive")
                                return true;
                        }
                        else if (eachBccAdmin.bcbs_name === "CPF | Control Plan Fee | Medical" && medBenefit.length > 0 && !alreadyExists) {
                            if (medBenefit[0]?.tierSetRef?.name === "Comprehenseive")
                                return true;
                        }
                        else if (eachBccAdmin.bcbs_name === "CPF | PREFERRED COMP MOD (PPO/EPO) ASO | Medical" && medBenefit.length > 0 && !alreadyExists) {
                            if (medBenefit[0]?.tierSetRef?.name !== "Comprehenseive")
                                return true;
                        }
                        else if (eachBccAdmin.bcbs_name === "PCM | PREFERRED COMP MOD (PPO/EPO) | Medical" && medBenefit.length > 0 && !alreadyExists) {
                            if (medBenefit[0]?.tierSetRef?.name !== "Comprehenseive")
                                return true;
                        }

                        else if (eachBccAdmin.bcbs_name === "CPF | DENTAL | Dental" && denBenefit.length > 0 && !alreadyExists)
                            return true;
                        else if (eachBccAdmin.bcbs_name === "CPF | Vision | Vision" && visBenefit.length > 0 && !alreadyExists)
                            return true;
                        else if (eachBccAdmin.bcbs_name === "VIS | Vision | Medical" && visBenefit.length > 0 && !alreadyExists)
                            return true;
                        else if (eachBccAdmin.bcbs_name === "EES | Engagement Suite Standard Package | Medical" && ancBenefit.length > 0 && !alreadyExists)
                            return true;
                        else if (eachBccAdmin.bcbs_name === "HCC | Health Coaching - Chronic Condition | Medical" && ancBenefit.length > 0 && !alreadyExists)
                            return true;
                        else if (eachBccAdmin.bcbs_name === "HCL | Health Coaching - Lifestyle | Medical" && ancBenefit.length > 0 && !alreadyExists)
                            return true;

                        if (eachBccAdmin.bcbs_attributevalue === null && eachField.masterAttributeID === eachBccAdmin._bcbs_build_masterattributeid_value && !alreadyExists)
                            return true;

                        if (eachField.masterAttributeID === eachBccAdmin._bcbs_build_masterattributeid_value && !alreadyExists) {
                            if (eachField && eachField.values && eachField.values[0].value) {
                                const eachFirstFieldValue = eachField.values[0].value.type === "string"
                                    ? eachField.values[0].value.value
                                    : eachField.values[0].value.type === "entity"
                                        ? eachField.values[0].value.name
                                        : eachField.values[0].value.type === "bool"
                                            ? (eachField.values[0].value.value === true ? "Yes" : "No")
                                            : null

                                if (eachFirstFieldValue && eachBccAdmin.bcbs_attributevalue?.includes(eachFirstFieldValue))
                                    return true;
                            }
                        }
                        return false;

                    })
                }))

            //eachFeeAndPremium.attributeSets = [];
            bccAdmins.forEach(bccAdmin => {//Loops through each BCC Admin that needs to have a Fee created for this Fee and Premium
                let feeAttributeSet = createAttributeSet(cState.data, feeattributesetMasterAttribute);
                feeAttributeSet.name = "Fee";

                feeAttributeSet.fields.forEach(eachField => {
                    let value: (FAM_TValue | null) = null;

                    const att = config.cache.map_bcbs_config_attribute[eachField.configRef.id];
                    //debugger;
                    switch (att.bcbs_config_attributeid) {

                        // link to crm 
                        // BCC Admin
                        case $att_$fees_and_premiums_$bcbs_bccadminid_id:
                            const t: FAM_IEntityReferenceValue = {
                                type: 'entity',
                                entityType: 'bcbs_bccadmin',
                                id: bccAdmin.bcbs_bccadminid!,
                                name: bccAdmin.bcbs_name!
                            }
                            value = t;
                            break;

                        // link to crm 
                        // Fee Type
                        case $att_$fees_and_premiums_$bcbs_fee_feetype_id:
                            const x: FAM_IEntityReferenceValue = {
                                type: 'entity',
                                entityType: 'bcbs_config_optionsetitem',
                                id: $oset_$fee_fee_type_$per_employee_per_month_pepm_id,
                                name: "Per Employee Per Month (PEPM)"
                            }
                            value = x;
                            break;

                        // link to crm 
                        // Bill Group
                        case $att_$fees_and_premiums_$bcbs_billgroupcode_id:
                            const y: FAM_IBooleanValue = {
                                type: 'bool', value: true
                            }
                            value = y;
                            break;

                    }


                    //if (att.bcbs_name?.startsWith('BCC Admin')) {
                    //    const t: FAM.IEntityReferenceValue = {
                    //        type: 'entity',
                    //        entityType: 'bcbs_bccadmin',
                    //        id: bccAdmin.bcbs_bccadminid!,
                    //        name: bccAdmin.bcbs_name!
                    //    }
                    //    value = t;
                    //}

                    //if (att.bcbs_name?.startsWith('Fee Type')) {
                    //    const t: FAM.IOptionSetValue = {
                    //        type: 'optionset',
                    //        value: 1,
                    //        name: "Per Employee Per Month (PEPM)"
                    //    }
                    //    value = t;
                    //}

                    //if (att.bcbs_name?.startsWith('Bill Group?')) {
                    //    const t: FAM.IBooleanValue = {
                    //        type: 'bool', value: true
                    //    }
                    //    value = t;
                    //}

                    //const fee1DefaulTierValue: FAM.IFlexibleFieldValue = {
                    //    id: uuidv4(),
                    //    type: 'primary',
                    //    tierRef: null,
                    //    value: value
                    //}
                    eachField.values[0].value = value;
                });
                //let temp = feeAttributeSet;
                eachFeeAndPremium.attributeSets.push(feeAttributeSet);

                console.log("Fee And Premium Calculated :", eachFeeAndPremium);
            })
        })

        //configX.bcbs_bccadmin.forEach(q => {q.})

        //newFeesAndPremiums.forEach(q => {q.fees.push})

        // const adminFee = config.
        // const PLACE HOLDER FOR Michael
        // const configuration =
        dispatch(updateFeesAndPremiums(newFeeAndPremiums));
    }
}

// unmounts and remounts the root after a delay
// see context::state.status.recycling
export const WithRecycle = (action: () => void, ms: number) => {
    return async function (dispatch: Dispatch<any>, state: () => RootState) {
        dispatch(setStatusRecycle(true));
        setTimeout(() => {
            action?.()
            dispatch(setStatusRecycle(false));
        }, ms);
    }
}


export const UpdateGroupConfigurationProperties = (updateValue: IGroupConfigurationUpdate) => {
    return async function (dispatch: Dispatch<any>, state: () => RootState) {
        dispatch(addUpdateGroupConfiguration(updateValue));

    }
}

export const loadOpportunityProductAction = (
    dataSource: AppDataSource,
    bcbs_opportunityproductid: string,
    instructions?: {
        loadscreen?: 'none' | 'full' | 'partial',
    }
) => {
    return async function (dispatch: Dispatch<any>, stateSelector: () => RootState) {
        try {

            appInsights?.startTrackEvent('dataActions.loadOpportunityProductAction');

            let state = stateSelector();

            if (!instructions?.loadscreen || instructions?.loadscreen === 'full')
                dispatch(startSnapshotLoad());

            // do we reload the config
            if (!state.data.config.bcbs_config_release) {
                const config = await LoadConfigAsync(dataSource, undefined);
                dispatch(updateSnapshotData({
                    config: config,
                }));
            }

            state = stateSelector();

            const response = await dataSource.getOpportunityProductAndOptionsAndBenefits(bcbs_opportunityproductid)

            const opportunityProduct: FAM_IOpportunityProduct = {
                opportunity: response.opportunity,
                bcbs_opportunityproduct: response.bcbs_opportunityproduct,
                bcbs_quoterequest: response.bcbs_quoterequest,
                options: []
            }

            response.bcbs_opportunitybenefit.forEach(each_bcbs_opportunitybenefit => {

                const option = createOpportunityOptionAndBenefits(
                    state.data.config,
                    each_bcbs_opportunitybenefit.bcbs_quoterequest,
                    response.bcbs_opportunityproduct,
                    // From CRM:
                    {
                        bcbs_opportunitybenefits: each_bcbs_opportunitybenefit.bcbs_opportunitybenefits,
                        bcbs_opportunityoption: each_bcbs_opportunitybenefit.bcbs_opportunityoption,
                    }
                );

                option.bcbs_opportunitybenefits = each_bcbs_opportunitybenefit.bcbs_opportunitybenefits;

                opportunityProduct.options.push(option)
            })

            var account = await dataSource.getAccount(response.bcbs_opportunityproduct._bcbs_groupid_value!);

            dispatch(updateSnapshotData({
                opportunityProduct: opportunityProduct,
                account: account,

                // to keep compaitble with snapshot save
                opportunity: opportunityProduct.opportunity,
            }));
        }
        catch (error: any) {
            alert("An error has occurred. This page cannot load.");

            // lets create a new one is payload is not providing one
            emitCustomEvent({
                eventType: 'globalException',
                details: error
            });

            throw error;

        } finally {
            dispatch(endSnapshotLoad());

            appInsights?.stopTrackEvent('dataActions.loadOpportunityProductAction');
        }
    }
}

export const saveOpportunityProductDataAction = (dataSource: AppDataSource) => {
    return async function (dispatch: Dispatch<any>, state: () => RootState) {

        appInsights?.startTrackEvent('dataActions.saveOpportunityProductDataAction');

        try {
            let stateInstance = state();

            if (stateInstance.status.snapshotLoading) {
                console.log("Snapshot processing, skipping save");
                return;
            }

            console.log("stateInstance", stateInstance);

            const convert_iflexiblefieldvalue_to_bcbs_opportunitybenefit = (
                field: FAM_IFlexibleField,
                value: FAM_IFlexibleFieldValue,
                tierLevels: Record<string, FAM_ITierLevel>
            ) => {
                const configAttribiute = config.cache.map_bcbs_config_attribute[field.configRef.id];
                const configMasterAttribiute = config.cache.map_bcbs_config_masterattribute[configAttribiute._bcbs_config_masterattributeid_value!];
                const controlType = configMasterAttribiute.bcbs_attributetypecode as EControlType;

                const benefit: Partial<IXrmTypes_bcbs_opportunitybenefit> = {};
                // benefit.bcbs_opportunitybenefitid = field.id

                benefit._bcbs_config_masterattributeid_value = configMasterAttribiute.bcbs_config_masterattributeid;
                benefit._bcbs_config_attributeid_value = configAttribiute.bcbs_config_attributeid;

                benefit.bcbs_name = calcFieldName(field, value, configAttribiute, tierLevels);
                benefit.bcbs_opportunitybenefitid = value.id;

                if (!field.visibility && value && value.value) {
                    benefit.bcbs_value = JSON.stringify(value);
                    benefit.bcbs_friendlyvalue = friendlyValue(controlType, value.value);
                }
                else {
                    benefit.bcbs_value = null as any;
                    benefit.bcbs_friendlyvalue = null as any;
                }

                return benefit;

            }

            // Changeset
            const createChangeSet = <T extends unknown>(
                origin: T[],
                target: T[],
                {
                    targetKeyPredicate,
                    originKeyPredicate
                }:
                    {
                        targetKeyPredicate: (t: T) => string,
                        originKeyPredicate: (t: T) => string
                    }
            ) => {

                type MergeRecord<T> = {
                    id: string,
                    index: number,
                    onOrigin: T | null,
                    onTarget: T | null
                };

                type MergeRecordLookup<T> = Record<string, MergeRecord<T>>;

                const merged: MergeRecordLookup<T> = {};

                target.forEach((item, index) => {
                    const matchId = targetKeyPredicate(item);

                    // fetch the merge record, if doesnt exist create one
                    const merge = merged[matchId] ?? {
                        id: matchId,
                        index: index,
                        onOrigin: null,
                        onTarget: null,
                    };

                    merged[matchId] = merge;

                    merge.onTarget = item;
                });

                origin.forEach((item, index) => {
                    const matchId = originKeyPredicate(item);

                    // fetch the merge record, if doesnt exist create one
                    const merge = merged[matchId] ?? {
                        id: matchId,
                        index: index,
                        onOrigin: null,
                        onTarget: null,
                    };
                    merged[matchId] = merge;

                    merge.onOrigin = item;
                });

                return merged;
            }

            const convert_iopportunityproductoptionbenefit_to_bcbs_opportunityoption = (option: FAM_IOpportunityProductOptionBenefit) => {
                const bcbs_opportunityoption: Partial<IXrmTypes_bcbs_opportunityoption> = {
                    bcbs_opportunityoptionid: option.id!,
                    bcbs_name: option.name,
                    bcbs_buyupuwsinglei: option.bcbs_buyupuwsinglei,
                    bcbs_buyupuwfamilyi: option.bcbs_buyupuwfamilyi,
                    bcbs_buyupuweechildreni: option.bcbs_buyupuweechildreni,
                    bcbs_buyupuweespousei: option.bcbs_buyupuweespousei,
                    bcbs_buyupuwtotali: option.bcbs_buyupuwtotali,
                    bcbs_buyupuwsinglem: option.bcbs_buyupuwsinglem,
                    bcbs_buyupuwfamilym: option.bcbs_buyupuwfamilym,
                    bcbs_buyupuweechildrenm: option.bcbs_buyupuweechildrenm,
                    bcbs_buyupuweespousem: option.bcbs_buyupuweespousem,
                    bcbs_buyupuwtotalm: option.bcbs_buyupuwtotalm,
                    bcbs_annualpremium: option.bcbs_annualpremium,
                };

                bcbs_opportunityoption._owningteam_value = option.bcbs_opportunityproduct?._owningteam_value;

                // tiers
                bcbs_opportunityoption.bcbs_tierdefinition = JSON.stringify(option.bcbs_tierdefinition);

                // Status/State fields
                if (option.deactivated) {
                    bcbs_opportunityoption.statuscode = IXrmOptions_bcbs_opportunityoption.statuscode['Inactive(2)'].value;
                    bcbs_opportunityoption.statecode = IXrmOptions_bcbs_opportunityoption.statecode['Inactive(1)'].value;
                }
                else {
                    if (option.won) {
                        bcbs_opportunityoption.statuscode = IXrmOptions_bcbs_opportunityoption.statuscode['Won(808900001)'].value;
                    } else {
                        bcbs_opportunityoption.statuscode = IXrmOptions_bcbs_opportunityoption.statuscode['Active(1)'].value;
                    }
                    bcbs_opportunityoption.statecode = IXrmOptions_bcbs_opportunityoption.statecode['Active(0)'].value;
                }

                return bcbs_opportunityoption;
            };

            // function extractOnlyChangedFields<T extends Record<string | number, any>>(
            //     origin: T,
            //     target: T,
            //     fields: string[] | null,
            //     includeNull: boolean = true
            // ) {
            //     const changedFields = {} as Partial<T>;

            //     // if no field specified, create a unique key set between source and target
            //     fields = fields ?? [...new Set([...Object.keys(origin), ...Object.keys(target)])];;

            //     fields.forEach(key => {
            //         // changed?
            //         if (origin[key] !== target[key])
            //             if (target[key] !== null) {
            //                 changedFields[key as any] = target[key];
            //             }
            //             else {
            //                 if (includeNull)
            //                     changedFields[key as any] = target[key];
            //             }
            //     });
            //     return changedFields;
            // }

            dispatch(setProcessing(true));

            const config = stateInstance.data.config;

            const stateOptions = stateInstance.data.opportunityProduct?.options;

            const optionPromises: any[] = [];

            // Option:
            // Add/Update/Delete
            // ----------------------------------------------------------------
            stateOptions?.forEach(stateOption => {

                // Dont save if requested
                if (stateOption.instructions.onSaveOptionSkip) {
                    console.log("Skipping save for option", stateOption);
                    return;
                }

                const bcbs_opportunityoption_mapped = convert_iopportunityproductoptionbenefit_to_bcbs_opportunityoption(stateOption);

                // relate option to product 
                bcbs_opportunityoption_mapped._bcbs_opportunityproductid_value = stateInstance.data.opportunityProduct?.bcbs_opportunityproduct.bcbs_opportunityproductid!;

                // CREATE 
                if (!stateOption.bcbs_opportunityoption) {
                    const promise = dataSource.save_bcbs_opportunityoption(bcbs_opportunityoption_mapped);
                    optionPromises.push(promise);
                }

                // UPDATE
                if (stateOption.bcbs_opportunityoption) {

                    // fields have changed?
                    const observableFields: (keyof IXrmTypes_bcbs_opportunityoption)[] = [
                        'bcbs_name',
                        'bcbs_buyupuwsinglei',
                        'bcbs_buyupuwfamilyi',
                        'bcbs_buyupuweechildreni',
                        'bcbs_buyupuweespousei',
                        'bcbs_buyupuwtotali',
                        'bcbs_buyupuwsinglem',
                        'bcbs_buyupuwfamilym',
                        'bcbs_buyupuweechildrenm',
                        'bcbs_buyupuweespousem',
                        'bcbs_buyupuwtotalm',
                        'bcbs_annualpremium',
                        'statecode',
                        'statuscode',
                        'bcbs_tierdefinition',
                    ];

                    // any of observerd fields changed?
                    const updatedFields = observableFields.filter(key => {
                        if (stateOption.bcbs_opportunityoption![key] !== bcbs_opportunityoption_mapped[key])
                            return true;
                    });

                    // anything updated?
                    if (updatedFields.length > 0) {

                        // only grab the changed fields
                        const bcbs_opportunitybenefit_changed: Partial<IXrmTypes_bcbs_opportunityoption> = {};
                        updatedFields.forEach(key => {
                            bcbs_opportunitybenefit_changed[key] = bcbs_opportunityoption_mapped[key] as any;
                        });

                        const promise = dataSource.save_bcbs_opportunityoption(bcbs_opportunitybenefit_changed, stateOption.bcbs_opportunityoption.bcbs_opportunityoptionid!);
                        optionPromises.push(promise);
                    }
                }

            });

            // wait for all options to be saved
            await Promise.all(optionPromises);

            const optionBenefitPromises: any[] = [];

            // Benefits:
            // Add/Update/Delete of benefits for each option
            // ----------------------------------------------------------------
            stateOptions?.forEach(stateOption => {

                if (stateOption.instructions.onSaveOptionBenefitsSkip) {
                    console.log("Skipping save for option benefits", stateOption);
                    return;
                }

                const bcbs_opportunitybenefits_mapped: Partial<IXrmTypes_bcbs_opportunitybenefit>[] = []

                const bcbs_opportunitybenefits_origin = stateOption.bcbs_opportunitybenefits ?? [];

                const tierLevels = ArrayToLookup(stateOption.bcbs_tierdefinition.tierLevels);

                // converting to benefits
                stateOption.fields.forEach(eachField => {
                    eachField.values.forEach(eachFieldValue => {
                        const benefit = convert_iflexiblefieldvalue_to_bcbs_opportunitybenefit(eachField, eachFieldValue, tierLevels);

                        // relate to option
                        benefit._bcbs_opportunityoptionid_value = stateOption.id;

                        // relate to product
                        benefit._bcbs_opportunityproduct_value = stateOption.bcbs_opportunityproduct?.bcbs_opportunityproductid!;

                        benefit._owningteam_value = stateOption.bcbs_opportunityproduct?._owningteam_value;

                        bcbs_opportunitybenefits_mapped.push(benefit);
                    });
                });

                // merging
                const mergedBenefitsLookup = createChangeSet<Partial<IXrmTypes_bcbs_opportunitybenefit>>(
                    bcbs_opportunitybenefits_origin,
                    bcbs_opportunitybenefits_mapped,
                    {
                        originKeyPredicate: (item) => {
                            return item.bcbs_opportunitybenefitid!;
                            // let key = item._bcbs_config_attributeid_value!;

                            // var bcbs_value = JSON.parse(item.bcbs_value ?? '{}') as FAM.IFlexibleFieldValue;

                            // if (bcbs_value.type)
                            //     key = key + "-" + bcbs_value.type;

                            // if (bcbs_value.tierRef?.id)
                            //     key = key + "-" + bcbs_value.tierRef.id;

                            // return key;
                        },

                        targetKeyPredicate: (item) => {
                            return item.bcbs_opportunitybenefitid!;

                            // let key = item._bcbs_config_attributeid_value!;

                            // var bcbs_value = JSON.parse(item.bcbs_value ?? '{}') as FAM.IFlexibleFieldValue;

                            // if (bcbs_value.type)
                            //     key = key + "-" + bcbs_value.type;

                            // if (bcbs_value.tierRef?.id)
                            //     key = key + "-" + bcbs_value.tierRef.id;

                            // return key;
                        },
                    }
                );

                // judgements
                const mergedBenefits = Object.values(mergedBenefitsLookup).sort((a, b) => a.index - b.index);

                const result: {
                    action: 'untracked' | 'modified' | 'delete' | '',
                    change: Partial<IXrmTypes_bcbs_opportunitybenefit>
                }[] = mergedBenefits.map(t => {

                    // CREATE, we need to create a new full record
                    if (t.onTarget && !t.onOrigin) {
                        // * ignore blank/null/undefined values for create
                        if (t.onTarget.bcbs_value)
                            return {
                                action: 'untracked',
                                change: t.onTarget
                            }
                    }

                    // UPDATE
                    // TODO implementing field change detection and only updating fields if they have changed.
                    // * blank values should remove
                    // * (TODO) Fields should be updated and identified if they are different
                    if (t.onTarget && t.onOrigin) {
                        if (t.onTarget.bcbs_value !== t.onOrigin.bcbs_value) {
                            return {
                                action: 'modified',
                                change: t.onTarget
                            }
                        }
                    }

                    // DELETE
                    // * blank values should remove
                    if (!t.onTarget && t.onOrigin) {
                        return {
                            action: 'delete',
                            change: t.onOrigin
                        }
                    }

                    // default case
                    return {
                        action: '',
                        change: {} as IXrmTypes_bcbs_opportunitybenefit
                    };
                });

                result.forEach(({ action, change }) => {
                    if (action === 'untracked') {
                        const promise = dataSource.save_bcbs_opportunitybenefits(change);
                        optionBenefitPromises.push(promise);
                    }

                    if (action === 'modified') {
                        const promise = dataSource.save_bcbs_opportunitybenefits(change, change.bcbs_opportunitybenefitid);
                        optionBenefitPromises.push(promise);
                    }
                })

            })

            await Promise.all(optionBenefitPromises);

            dispatch(
                loadOpportunityProductAction(
                    dataSource,
                    stateInstance.data.opportunityProduct?.bcbs_opportunityproduct.bcbs_opportunityproductid!,
                    {
                        loadscreen: 'none'
                    }
                )
            );
        }

        catch (error: any) {
            alert("An error has occurred. This page cannot load.");

            // lets create a new one is payload is not providing one
            emitCustomEvent({
                eventType: 'globalException',
                details: error
            });

            throw error;

        } finally {
            dispatch(setProcessing(false));
            // dispatch(endSnapshotLoad());
            appInsights?.stopTrackEvent('dataActions.saveOpportunityProductDataAction');
        }


    }
}

export const UpdateOpportunityProductAction = (
    dataSource: AppDataSource,
) => {
    return async function (dispatch: Dispatch<any>, stateSelector: () => RootState) {

        try {
            appInsights?.startTrackEvent('dataActions.UpdateOpportunityProductAction');

            dispatch(startSnapshotLoad());

            const xState = stateSelector();

            const config = xState.data.config!;
            let opportunityProductRef = { ...xState.data.opportunityProduct! };
            opportunityProductRef.options = [...opportunityProductRef.options];

            const importModule = await import("global/Transform/Sprint28Migration")
            const { QuoteRequestTransformer } = importModule;
            const result = await QuoteRequestTransformer(config, opportunityProductRef);

            dispatch(
                updateSnapshotData({
                    opportunityProduct: result,
                })
            );
        }
        catch (error: any) {
            // lets create a new one is payload is not providing one
            emitCustomEvent({
                eventType: 'globalException',
                details: error
            });

            throw error;

        } finally {
            dispatch(endSnapshotLoad());
            appInsights?.stopTrackEvent('dataActions.UpdateOpportunityProductAction');
        }


    }
}


export const SaveQuoteRequestAsGroupConfigurationAction = (dataSource: AppDataSource, options: FAM_IOpportunityProductOptionBenefit[]) => {
    return async function (dispatch: Dispatch<any>, state: () => RootState) {

        try {
            dispatch(setProcessing(true));

            // Option flagged as "Won" is created as a separate plan.
            // Each Plan will have a version of each applicable benefit (Medical + Ancillary, Dental).  Where there are multiples, create a version of the applicable benefits per Plan.
            const xState = state();

            const importModule = await import("global/Transform/QuoteRequestToGroupConfigurationTransformer")
            const { GroupConfigurationToQuoteRequestTransformer } = importModule;
            const results = await GroupConfigurationToQuoteRequestTransformer(xState, options);

            var snapshot = await dataSource.createGroupConfigurationSnapshot(results.snapshotData,);

            dispatch(
                updateSnapshotData({
                    json: results.json,
                    snapshot: snapshot,
                })
            );

            const instructions = {};
            // // latest redux state
            // const saveResult = await SaveAsync(dispatch, dataSource, state(), instructions);

            // // open the new one
            // (window as Window).open(AppLinkToExternal({
            //     id: saveResult.snapshot?.bcbs_groupconfigurationsnapshotid!,
            //     type: 'bcbs_groupconfigurationsnapshot'
            // }));

            dispatch(updateSnapshotBlobVersion(null));

        }
        catch (error: any) {
            alert("An error has occurred. This page cannot load.");

            // lets create a new one is payload is not providing one
            emitCustomEvent({
                eventType: 'globalException',
                details: error
            });

            throw error;

        } finally {
            dispatch(setProcessing(false));
        }

    }
}

export interface IConfigurationRelationshipPayload {
    /** ID of the snapshot the benefit was sourced from (origin template snapshot) */
    originSnapshotId: string;
    /** GUID of the benefit in the origin (template) JSON */
    originBenefitId: string;
    /** GUID of the benefit in the target (group config) JSON */
    targetBenefitId: string;
    /** Display name of the target snapshot, e.g. "BMW 2025" */
    snapshotName?: string;
    /** CRM category name (bcbs_name), e.g. "Medical" */
    categoryName?: string;
    /** Benefit display name (name), e.g. "Medical PPO" */
    benefitName?: string;
    /** Whether the action was an initial import, a re-sync, or a clone */
    action: 'Import' | 'Sync' | 'Clone';
}

/** Returns true for benefits that carry complete association template provenance and are not deactivated. */
function hasBenefitProvenance(b: FAM_IBenefit): boolean {
    return !b.deactivated
        && !!(b as any).bcbs_associationtemplatebenefitid
        && !!(b as any).bcbs_associationtemplatesnapshotid;
}

/**
 * Stamps association template provenance fields onto a benefit being imported
 * from an Association Template snapshot. Used by both AssociationTemplates.tsx
 * and BenefitWizard.tsx to avoid duplicating this logic.
 */
export function stampAssociationTemplateProvenance(
    benefit: FAM_IBenefit,
    originalTemplateId: string | undefined,
    sourceSnapshotId: string | undefined
): void {
    benefit.bcbs_fromassociationtemplate = true;
    if (originalTemplateId) benefit.bcbs_associationtemplatebenefitid = originalTemplateId;
    if (sourceSnapshotId) benefit.bcbs_associationtemplatesnapshotid = sourceSnapshotId;
    benefit.bcbs_associationtemplateaction = 'Import';
}

// Default Association Template snapshot used when a benefit has no stored snapshot ID.
// Matches the constant in Benefit.tsx and AssociationTemplates.tsx.
const DEFAULT_TEMPLATE_SNAPSHOT_ID = '4ba75527-05c0-f011-bbd3-6045bda8e172';

/**
 * Loads an Association Template snapshot from Dataverse and stores it in Redux.
 * Used by Benefit.tsx when opening a benefit that came from a template and the
 * snapshot is not yet in the Redux store.
 */
export const loadAssociationTemplateSnapshotAction = (
    dataSource: AppDataSource,
    snapshotId: string
) => async (dispatch: TDispatch): Promise<void> => {
    try {
        const snapshot = await dataSource.getGroupConfigurationSnapshot(snapshotId);
        if (!snapshot) return;

        const downloadResult = await dataSource.downloadGroupConfigurationSnapshot(snapshot);
        if (!downloadResult?.json) return;

        const account = snapshot._bcbs_groupid_value
            ? await dataSource.getAccount(snapshot._bcbs_groupid_value)
            : null;

        dispatch(setAssociationTemplateSnapshot({
            account: account ?? {} as any,
            snapshot,
            json: downloadResult.json,
            config: {} as FAM_IGroupConfigurationConfig
        } as IGCDataLight));
    } catch (err) {
        console.error('Failed to auto-load association template snapshot:', err);
    }
};

/**
 * Syncs the current benefit from the Association Template snapshot.
 * Fetches the latest snapshot, finds the matching template benefit, applies
 * its fields/tiers, and dispatches the update to Redux.
 * Returns an error string on failure, or null on success.
 */
export const syncBenefitFromTemplateAction = (
    dataSource: AppDataSource,
    benefit: FAM_IBenefit
) => async (dispatch: TDispatch): Promise<string | null> => {
    const snapshotId = benefit.bcbs_associationtemplatesnapshotid ?? DEFAULT_TEMPLATE_SNAPSHOT_ID;

    const snapshot = await dataSource.getGroupConfigurationSnapshot(snapshotId);
    if (!snapshot) return 'Association template snapshot not found.';

    const downloadResult = await dataSource.downloadGroupConfigurationSnapshot(snapshot);
    if (!downloadResult?.json) return 'Failed to load Association Template snapshot data.';

    const account = snapshot._bcbs_groupid_value
        ? await dataSource.getAccount(snapshot._bcbs_groupid_value)
        : null;

    dispatch(setAssociationTemplateSnapshot({
        account: account ?? {} as any,
        snapshot,
        json: downloadResult.json,
        config: {} as FAM_IGroupConfigurationConfig
    } as IGCDataLight));

    const templateBenefits = downloadResult.json.benefits;

    if (!templateBenefits || templateBenefits.length === 0)
        return 'Association template has no benefits.';

    const matchingBenefit =
        (benefit.bcbs_associationtemplatebenefitid
            ? templateBenefits.find((b: FAM_IBenefit) => b.id === benefit.bcbs_associationtemplatebenefitid && !b.deactivated)
            : undefined)
        ?? templateBenefits.find((b: FAM_IBenefit) =>
            b.product?.value === benefit?.product?.value &&
            b.bcbs_config_categoryid === benefit?.bcbs_config_categoryid &&
            !b.deactivated
        );

    if (!matchingBenefit)
        return 'No matching benefit found in the association template.';

    const clonedBenefit: FAM_IBenefit = JSON.parse(JSON.stringify(benefit));
    clonedBenefit.name = matchingBenefit.name;
    clonedBenefit.bcbs_name = matchingBenefit.bcbs_name;
    clonedBenefit.fields = JSON.parse(JSON.stringify(matchingBenefit.fields));
    clonedBenefit.tiers = JSON.parse(JSON.stringify(matchingBenefit.tiers));
    clonedBenefit.bcbs_associationtemplatebenefitid = matchingBenefit.id;
    clonedBenefit.bcbs_associationtemplatesnapshotid = snapshot.bcbs_groupconfigurationsnapshotid ?? snapshotId;
    // bcbs_associationtemplateaction is intentionally not overwritten here —
    // syncing preserves the original action set when the benefit was first linked.

    dispatch(addUpdateBenefit([clonedBenefit]));
    return null;
};

/**
 * Builds the CRM record payload for a bcbs_configurationrelationship row.
 * Used by both the Import/Sync upsert path and the Clone POST path.
 */
export function buildConfigurationRelationshipRecord(
    payload: IConfigurationRelationshipPayload,
    targetSnapshotId: string
): Partial<IConfigurationRelationship> & Ibcbs_configurationrelationship_extended {
    const { snapshotName, action, categoryName, benefitName, originSnapshotId, originBenefitId, targetBenefitId } = payload;
    return {
        bcbs_name: `${snapshotName ?? 'Snapshot'} | ${action} | ${categoryName ?? 'Benefit'} | ${benefitName ?? 'Benefit'} | ${new Date().toISOString().slice(0, 10)}`,
        'bcbs_OriginSnapshotId@odata.bind': `/bcbs_groupconfigurationsnapshots(${originSnapshotId})`,
        'bcbs_TargetSnapshotId@odata.bind': `/bcbs_groupconfigurationsnapshots(${targetSnapshotId})`,
        bcbs_origin: originBenefitId,
        bcbs_origintypecode: EOriginTypeCode.Benefit,
        bcbs_target: targetBenefitId,
        bcbs_relationshiptypecode: ERelationshipTypeCode.AssociationTemplate,
        bcbs_targetactioncode:
            action === 'Sync' ? ETargetActionCode.Sync :
                action === 'Clone' ? ETargetActionCode.Clone :
                    ETargetActionCode.Import,
    };
}

/**
 * Saves a record to the bcbs_configurationrelationship table to track
 * the relationship between a template benefit and a group config benefit
 * when using the Association Template import or sync flows.
 */
export function saveConfigurationRelationship(
    dataSource: AppDataSource,
    payload: IConfigurationRelationshipPayload,
    targetSnapshotId: string
) {
    return async (_dispatch: TDispatch, _getState: () => RootState) => {
        const record = buildConfigurationRelationshipRecord(payload, targetSnapshotId);

        await dataSource.bcbs_configurationrelationship_saveAsync(record);
    };
}